// StockMaestro Logistics v2.2 — Build 3 — with Tracking Tab
(function(){
  'use strict';
  const db=()=>window.SM.DB, ui=()=>window.SM.UI, util=()=>window.SM.UTIL;

  let _courierCache=null; // cached active couriers list — avoids a fresh
                           // network round-trip every time a tracking/
                           // courier-assignment modal is opened

  async function _getActiveCouriers(){
    if(_courierCache) return _courierCache;
    const {data}=await db().select('couriers',{eq:{is_active:true},order:{col:'name'}});
    _courierCache=data||[];
    return _courierCache;
  }

  // ─── TAB ATTENTION BADGES ───────────────────────────────────────
  // A badge means "this needs you", not "here is how many records exist".
  // Shipments, Routes and Fleet are reference/config lists and deliberately
  // carry no badge - a permanent number on those would train people to
  // ignore all badges, including the ones that matter.
  function setTabBadge(tab, count, variant){
    const el = document.querySelector(`#ims-log-tabs [data-tab="${tab}"]`);
    if(!el) return;
    let badge = el.querySelector('.ims__tab-badge');
    if(!count){ badge?.remove(); return; }
    if(!badge){ badge = document.createElement('span'); el.appendChild(badge); }
    badge.className = 'ims__tab-badge' + (variant === 'warn' ? ' ims__tab-badge--warn' : '');
    badge.textContent = count > 99 ? '99+' : String(count);
  }

  async function refreshTabBadges(){
    try {
      const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      const [jobsRes, manRes, courRes] = await Promise.all([
        client.from('logistics_jobs').select('id').eq('status','unassigned'),
        db().select('manifests',{ select:'id,dispatched_at' }),
        db().select('couriers',{ select:'id,cs_email,claims_email', eq:{ is_active:true } })
      ]);
      setTabBadge('unassigned', (jobsRes.data||[]).length);
      setTabBadge('manifests',  (manRes.data ||[]).filter(m => !m.dispatched_at).length, 'warn');
      setTabBadge('couriers',   (courRes.data||[]).filter(c => !c.cs_email || !c.claims_email).length, 'warn');
    } catch(e){
      // Badges are cosmetic - never let them break the panel.
      console.warn('[tab badges]', e);
    }
  }

  function initTabs(){
    const tabs=document.getElementById('ims-log-tabs');
    if(!tabs)return;
    // The Services tab is added here, so ims-system.liquid needs no change.
    if(!tabs.querySelector('[data-tab="services"]')){
      const svc=document.createElement('div'); svc.className='ims__tab'; svc.dataset.tab='services'; svc.textContent='Services';
      const after=tabs.querySelector('[data-tab="couriers"]');
      if(after) after.insertAdjacentElement('afterend',svc); else tabs.appendChild(svc);
    }
    // initTabs runs every time Logistics is opened: add the click handlers
    // once only (they used to stack up, so one click loaded a tab several
    // times after a few visits).
    if(tabs.dataset.bound==='1') return;
    tabs.dataset.bound='1';
    tabs.querySelectorAll('.ims__tab').forEach(tab=>{
      tab.addEventListener('click',()=>{
        tabs.querySelectorAll('.ims__tab').forEach(t=>t.classList.remove('ims__tab--active'));
        tab.classList.add('ims__tab--active');
        loadTab(tab.dataset.tab);
      });
    });
  }

  function loadTab(tab){
    refreshTabBadges();
    switch(tab){
      case 'tracking':   loadTracking();   break;
      case 'unassigned': loadUnassigned(); break;
      case 'owntruck':   loadOwnTruckQueue(); break;
      case 'shipments':  loadShipments();  break;
      case 'manifests':  loadManifests();  break;
      case 'routes':     loadRoutes();     break;
      case 'fleet':      loadFleet();      break;
      case 'couriers':   loadCouriers();   break;
      case 'services':   loadServices();   break;
    }
  }

  // ─── ALREADY-DELIVERED GUARD ─────────────────────────────────────
  // Confirming delivery can be reached from three independent places
  // (Tracking, a manifest stop, self-collection) with no shared lock
  // between them. Stock deduction is already protected by its own guard,
  // but nothing stops the same order being marked delivered twice through
  // two different screens — which would still duplicate the delivery
  // email and the activity log entry. This is checked first in each of
  // the three delivery-confirmation functions.
  async function _isAlreadyDelivered(orderId){
    const { data } = await db().select('orders', {
      select: 'status', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    return data?.status === 'delivered';
  }

  // ─── SHARED ACTIVITY LOG HELPER ─────────────────────────────────
  async function logActivity(orderId, oldStatus, newStatus, action, notes){
    try {
      const userName = window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'System';
      const userId   = window.SM.AUTH.user?.id || null;
      await db().insert('order_activity_log',{
        order_id:  orderId,
        action:    action,
        old_value: oldStatus,
        new_value: newStatus,
        notes:     notes || null,
        user_name: userName,
        user_id:   userId
      });
    } catch(e){
      console.warn('[logActivity] failed:', e.message);
    }
  }

  // ─── STOCK DEDUCTION ON DELIVERY ────────────────────────────────
  // Reservation happens at order creation (reserved_qty) and is held
  // through picking/packing/despatch. Only once the order is actually
  // delivered do we touch physical stock: decrement quantity, release
  // the reservation, and log a 'sale' movement for reporting.
  async function deductStockOnDelivery(orderId, orderNumber){
    // Guard against double-deduction — this can be called more than once
    // for the same order (Tracking confirm, manifest stop update, and
    // self-collection all call it independently, with no shared lock).
    // A 'sale' movement referencing this exact order already existing
    // means stock was already deducted once; skip silently rather than
    // deducting twice.
    const { data: alreadyDeducted } = await db().select('stock_movements', {
      eq: { reference: orderNumber, movement_type: 'sale' }, limit: 1, noCompanyFilter: true
    });
    if (alreadyDeducted?.length) return;

    const { data: ord } = await db().select('orders', {
      select: 'warehouse_id,fulfillment_type,order_items(product_id,quantity)',
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });

    // Dropship orders have no warehouse of their own: their units come out
    // of the partner's allocation warehouse, exactly as on collection
    // (deductDropshipStock in ims-dropship.js). Before, a van delivery of a
    // dropship order found no warehouse here and changed nothing.
    if (ord?.fulfillment_type === 'dropship') {
      if (!window.IMS_DROPSHIP?.deductDropshipStock) {
        ui().toast(`Stock for ${orderNumber} was not updated: the Dropship module did not load. Please report this.`, 'error', 10000);
        return;
      }
      const r = await window.IMS_DROPSHIP.deductDropshipStock(orderId, orderNumber, 'Order delivered');
      if (r?.problems?.length) {
        ui().toast(`${orderNumber} delivered, but stock could not be updated for: ${r.problems.join(', ')}. Please check stock levels.`, 'warning', 10000);
      }
      return;
    }

    const whId  = ord?.warehouse_id;
    const items = ord?.order_items || [];
    if (!whId || !items.length) return;
    // One line at a time, so two lines of the same product cannot both read
    // the same starting figure and overwrite each other.
    for (const item of items) {
      if (!item.product_id) continue;
      const { data: sl } = await db().select('stock_levels', {
        eq: { product_id: item.product_id, warehouse_id: whId }, maybeSingle: true, noCompanyFilter: true
      });
      if (sl) {
        await db().update('stock_levels', sl.id, {
          quantity:     Math.max(0, (sl.quantity||0) - (item.quantity||0)),
          reserved_qty: Math.max(0, (sl.reserved_qty||0) - (item.quantity||0))
        });
      }
      await db().insert('stock_movements', {
        product_id:    item.product_id,
        warehouse_id:  whId,
        movement_type: 'sale',
        quantity:      -(item.quantity||0),
        reference:     orderNumber || null,
        reason:        'Order delivered',
        user_id:       window.SM.AUTH.user?.id
      });
    }
  }

  // ─── SMART RESTOCK (shared) ──────────────────────────────────────
  // Used any time goods are coming back into the warehouse: parcel found,
  // return received, a refund, or a dropship return. quantity is only ever
  // actually reduced by deductStockOnDelivery() above, which runs once an
  // order is delivered or collected (and logs a 'sale' movement). An order
  // that never got that far (lost in transit, a failed delivery returned
  // to base) only ever had reserved_qty increased at order creation, so
  // for those this releases the reservation instead of adding stock that
  // never left. It checks which case applies (does a 'sale' movement
  // exist for this order?) and does the right thing either way.
  //
  // Runs at most ONCE per order. The order carries a restocked_at stamp:
  // the first call stamps it and does the work; every later call, from
  // any screen and with any reason wording, sees the stamp and does
  // nothing. The previous guard looked for a stock movement with the SAME
  // reason text, so "Customer return received" followed by "Refund
  // processed" ran twice, which for a delivered order would add its stock
  // back twice. (orders.restocked_at added 25 Sep 2026; orders already
  // restocked before then were stamped at the same time.)
  //
  // Returns the number of line items processed (0 if already done, or if
  // there is nothing to do).
  async function smartRestock(orderId, orderNumber, reason){
    const restockReason = reason || 'Stock returned';

    const { data: ord } = await db().select('orders', {
      select: 'warehouse_id,restocked_at,fulfillment_type,order_items(product_id,quantity)',
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    if (!ord || ord.restocked_at) return 0;
    // Dropship orders follow the dropship rules: units go back to the shelf
    // (if they had left) and to the partner's allocation. Before, returns of
    // dropship orders found no warehouse here and put nothing back.
    if (ord.fulfillment_type === 'dropship') {
      if (!window.IMS_DROPSHIP?.restoreDropshipStock) {
        ui().toast(`Stock for ${orderNumber} was not updated: the Dropship module did not load. Please report this.`, 'error', 10000);
        return 0;
      }
      return await window.IMS_DROPSHIP.restoreDropshipStock(orderId, orderNumber, reason);
    }
    const whId  = ord.warehouse_id;
    const items = ord.order_items || [];
    if (!whId || !items.length) return 0;

    // Claim the stamp FIRST. The update only matches while restocked_at is
    // still empty, so if two restocks start at the same moment only one
    // can claim it; the other gets no row back and stops here.
    const authClient = window.supabase.createClient(
      window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
      {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
    );
    const { data: claimed, error: stampErr } = await authClient.from('orders')
      .update({ restocked_at: new Date().toISOString() })
      .eq('id', orderId)
      .is('restocked_at', null)
      .select('id');
    if (stampErr) {
      ui().toast(`Stock for ${orderNumber} was not updated: ${stampErr.message}`, 'error', 8000);
      return 0;
    }
    if (!claimed?.length) return 0;

    // Goods have physically left if the order was delivered ('sale') or
    // written off as lost ('write_off'). Either way, bringing them back
    // must add them to on-hand, not just release a reservation.
    const { data: saleMoves } = await db().select('stock_movements', {
      eq: { reference: orderNumber }, in: { movement_type: ['sale', 'write_off'] }, limit: 1, noCompanyFilter: true
    });
    const wasDelivered = !!(saleMoves?.length);

    // One line at a time, so two lines of the same product cannot both
    // read the same starting figure and overwrite each other.
    let processed = 0;
    const failedLines = [];
    for (const item of items) {
      if (!item.product_id) continue;
      const { data: sl } = await db().select('stock_levels', {
        eq: { product_id: item.product_id, warehouse_id: whId }, maybeSingle: true, noCompanyFilter: true
      });
      if (wasDelivered) {
        // Stock genuinely left, so this reverses that deduction.
        if (sl) {
          const { error } = await db().update('stock_levels', sl.id, { quantity: (sl.quantity||0) + (item.quantity||0) });
          if (error) { failedLines.push(item.product_id); continue; }
        }
        await db().insert('stock_movements', {
          product_id: item.product_id, warehouse_id: whId, movement_type: 'stock_in',
          quantity: (item.quantity||0), reference: orderNumber || null,
          reason: restockReason, user_id: window.SM.AUTH.user?.id
        });
      } else {
        // Stock never actually left, so only release the reservation.
        // Adding to quantity here would inflate stock that was never removed.
        if (sl) {
          const { error } = await db().update('stock_levels', sl.id, {
            reserved_qty: Math.max(0,(sl.reserved_qty||0)-(item.quantity||0))
          });
          if (error) { failedLines.push(item.product_id); continue; }
        }
        await db().insert('stock_movements', {
          product_id: item.product_id, warehouse_id: whId, movement_type: 'stock_in',
          quantity: 0, reference: orderNumber || null,
          reason: `${restockReason} (never delivered: reservation released, no quantity change)`,
          user_id: window.SM.AUTH.user?.id
        });
      }
      processed++;
    }
    if (failedLines.length) {
      ui().toast(`Stock for ${orderNumber}: ${failedLines.length} line(s) could not be updated. Please check them in Inventory.`, 'warning', 10000);
    }
    return processed;
  }

  // ─── TRACKING TAB ───────────────────────────────────────────────

// ─── TRACKING TAB ───────────────────────────────────────────────
  const TRACKING_STATUSES = [
    'Collected by courier',
    'At collection depot',
    'In transit',
    'At delivery depot',
    'Out for delivery',
    'Delivered ✓',
    'Failed delivery',
    'Re-attempt requested',
    'Return requested',
    'Return in transit',
    'Return received'
  ];

  async function loadTracking(){
    const content = document.getElementById('ims-log-content');
    if(!content) return;

    // Preserve the user's current filter/sort selections across reloads.
    // Every status-changing action (Delivered, Failed, Lost, Re-attempt,
    // Return, etc.) calls loadTracking() to refresh the list — which
    // previously rebuilt the filter bar from scratch every time via a
    // fresh buildFilterBar() call, silently resetting platform, courier,
    // status, and sort back to their defaults on every edit.
    const prevPlatform = document.getElementById('ims-track-platform')?.value || '';
    const prevCourier  = document.getElementById('ims-track-courier')?.value  || '';
    const prevStatus   = document.getElementById('ims-track-filter')?.value   || '';
    const prevSort     = document.getElementById('ims-track-sort')?.value     || 'newest';

    content.innerHTML = `<div class="ims__loading"><i class="ti ti-loader-2 ims__spin"></i></div>`;

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const companyId = window.SM.AUTH.company?.id;

    // ── 0. Exclude orders still sitting unassigned in Logistics ────
    // A job can be 'dispatched' at order level the moment it leaves
    // Order Management, but it hasn't actually shipped until a courier,
    // own-truck slot, or self-collection has been assigned. Tracking
    // should only show orders past that point.
    const { data: unassignedJobs } = await client
      .from('logistics_jobs')
      .select('order_id')
      .eq('status', 'unassigned');
    const unassignedOrderIds = (unassignedJobs || []).map(j => j.order_id).filter(Boolean);

    // ── 1. Main orders query ──────────────────────────────────────
    let trackingQuery = client
      .from('orders')
      .select('id,order_number,external_ref,status,fulfillment_type,order_type,customer_name,end_customer_name,postcode,end_postcode,dispatched_at,failed_delivery_count,failure_reason,last_failed_at,return_reason,platforms(name),created_at')
      .eq('company_id', companyId)
      .in('status', ['dispatched','failed_delivery','re_attempted','return_in_transit']);
    if (unassignedOrderIds.length) {
      trackingQuery = trackingQuery.not('id', 'in', `(${unassignedOrderIds.map(id => `"${id}"`).join(',')})`);
    }
    const { data: orders } = await trackingQuery.order('dispatched_at', { ascending: false });

    const items = orders || [];
    if(!items.length){
      content.innerHTML = buildFilterBar([], []) +
        '<div id="ims-tracking-table-wrap"><div class="ims__empty"><i class="ti ti-circle-check"></i><p>No orders in transit</p><p style="font-size:12px;color:var(--ims-text-muted);margin-top:6px">All dispatched orders have been delivered or resolved</p></div></div>' +
        '<div id="ims-lost-orders-wrap"></div>';
      window._trackingOrders   = [];
      window._shipmentMap      = {};
      window._latestEventMap   = {};
      window._trackingEventMap = {};
      const sortElEmpty = document.getElementById('ims-track-sort');
      if(sortElEmpty) sortElEmpty.value = prevSort;
      loadLostOrders();
      return;
    }

    const orderIds = items.map(o => o.id);

    // ── 2. Shipments query (parallel) ────────────────────────────
    const { data: shipments } = await client
      .from('shipments')
      .select('order_id,tracking_number,courier_order_ref,courier_id,couriers(id,name,cs_email,claims_email,tracking_url_base,requires_postcode,postcode_param,tracking_form_only)')
      .in('order_id', orderIds).eq('is_return_collection', false)
      .order('created_at', { ascending: false });

    // Build shipment map — one shipment per order (latest)
    const shipmentMap = {};
    (shipments || []).forEach(s => {
      if(!shipmentMap[s.order_id]) shipmentMap[s.order_id] = s;
    });

    // ── 2b. Manifest linkage (own-fleet deliveries) ────────────────
    // Tracking previously had no awareness of manifests at all — an
    // order correctly dispatched via your own fleet on a real manifest
    // was treated identically to a courier shipment with no tracking
    // number, showing "Check shipment status" (which makes no sense for
    // a manifest delivery) instead of the actual manifest it's on.
    const { data: manifestLinks } = await client
      .from('logistics_jobs')
      .select('order_id, manifest_items(manifest_id, manifests(manifest_number, status, dispatched_at, drivers(name)))')
      .in('order_id', orderIds);
    const manifestMap = {};
    (manifestLinks || []).forEach(lj => {
      const mi = (lj.manifest_items || [])[0];
      if (mi?.manifests) {
        manifestMap[lj.order_id] = {
          manifestId: mi.manifest_id,
          manifestNumber: mi.manifests.manifest_number,
          driverName: mi.manifests.drivers?.name || null,
          dispatchedAt: mi.manifests.dispatched_at
        };
      }
    });

    // ── 3. Tracking events query (parallel) ──────────────────────
    const { data: trackingEvents } = await client
      .from('order_tracking_events')
      .select('id,order_id,tracking_status,notes,user_name,created_at')
      .in('order_id', orderIds)
      .order('created_at', { ascending: false });

    // Build latest event map + full history map
    const latestEventMap  = {};
    const trackingEventMap = {};
    (trackingEvents || []).forEach(e => {
      if(!latestEventMap[e.order_id])  latestEventMap[e.order_id] = e;
      if(!trackingEventMap[e.order_id]) trackingEventMap[e.order_id] = [];
      trackingEventMap[e.order_id].push(e);
    });

    // ── 4. Build dynamic filter options ──────────────────────────
    const platformSet = new Set();
    items.forEach(o => { if(o.platforms?.name) platformSet.add(o.platforms.name); });

    const courierSet = new Set();
    items.forEach(o => {
      const s = shipmentMap[o.id];
      if(s?.couriers?.name) courierSet.add(s.couriers.name);
    });

    // ── 5. Store globally for filter reuse ───────────────────────
    window._trackingOrders   = items;
    window._shipmentMap      = shipmentMap;
    window._latestEventMap   = latestEventMap;
    window._trackingEventMap = trackingEventMap;
    window._manifestMap      = manifestMap;

    content.innerHTML = buildFilterBar([...platformSet], [...courierSet]) +
      '<div id="ims-tracking-table-wrap">' + buildTrackingTable(items, shipmentMap, latestEventMap, manifestMap) + '</div>' +
      '<div id="ims-lost-orders-wrap"></div>';

    // Restore the user's previous filter/sort selections onto the freshly
    // rendered dropdowns, then reapply them to the freshly loaded list.
    const platEl = document.getElementById('ims-track-platform');
    const courEl = document.getElementById('ims-track-courier');
    const statEl = document.getElementById('ims-track-filter');
    const sortEl = document.getElementById('ims-track-sort');
    if(platEl) platEl.value = prevPlatform;
    if(courEl) courEl.value = prevCourier;
    if(statEl) statEl.value = prevStatus;
    if(sortEl) sortEl.value = prevSort;
    filterTracking();

    // Load lost orders section below tracking table
    loadLostOrders();
  }

  function buildFilterBar(platforms, couriers){
    const platOpts = platforms.map(p => `<option value="${util().sanitize(p)}">${util().sanitize(p)}</option>`).join('');
    const courierOpts = couriers.map(c => `<option value="${util().sanitize(c)}">${util().sanitize(c)}</option>`).join('');
    return `
      <div class="ims__filters" style="margin-bottom:14px">
        <div class="ims__search-wrap" style="max-width:240px">
          <i class="ti ti-search"></i>
          <input class="ims__input" type="text" id="ims-track-search" placeholder="Tracking #, courier order no., order, customer..." autocomplete="off" oninput="window.IMS_LOGISTICS.filterTracking()">
        </div>
        <select class="ims__select" id="ims-track-platform" onchange="window.IMS_LOGISTICS.filterTracking()">
          <option value="">All platforms</option>${platOpts}
        </select>
        <select class="ims__select" id="ims-track-courier" onchange="window.IMS_LOGISTICS.filterTracking()">
          <option value="">All couriers</option>${courierOpts}
        </select>
        <select class="ims__select" id="ims-track-filter" onchange="window.IMS_LOGISTICS.filterTracking()">
          <option value="">All in-flight</option>
          <option value="alert">3+ day — check status</option>
          <option value="delayed">5+ day — overdue</option>
          <option value="failed_delivery">Failed delivery</option>
          <option value="re_attempted">Re-attempted</option>
          <option value="return_in_transit">Returns in transit</option>
          <option value="dropship">Dropship only</option>
        </select>
        <select class="ims__select" id="ims-track-sort" onchange="window.IMS_LOGISTICS.filterTracking()">
          <option value="newest">Sort: Newest first</option>
          <option value="oldest">Sort: Oldest first</option>
          <option value="order_number">Sort: Order number</option>
        </select>
        <div style="font-size:11px;color:var(--ims-text-sec);display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-left:4px">
          <span class="ims-track-swatch" data-filter="alert" style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:2px 6px;border-radius:4px;border:1.5px solid transparent" onclick="window.IMS_LOGISTICS.toggleSwatchFilter('alert')"><span style="display:inline-block;width:10px;height:10px;background:#FEF3C7;border:1px solid #D97706;border-radius:2px"></span>3d alert</span>
          <span class="ims-track-swatch" data-filter="delayed" style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:2px 6px;border-radius:4px;border:1.5px solid transparent" onclick="window.IMS_LOGISTICS.toggleSwatchFilter('delayed')"><span style="display:inline-block;width:10px;height:10px;background:#FEE2E2;border:1px solid #DC2626;border-radius:2px"></span>5d overdue</span>
          <span class="ims-track-swatch" data-filter="failed_delivery" style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:2px 6px;border-radius:4px;border:1.5px solid transparent" onclick="window.IMS_LOGISTICS.toggleSwatchFilter('failed_delivery')"><span style="display:inline-block;width:10px;height:10px;background:#FED7AA;border:1px solid #EA580C;border-radius:2px"></span>Failed</span>
          <span class="ims-track-swatch" data-filter="re_attempted" style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:2px 6px;border-radius:4px;border:1.5px solid transparent" onclick="window.IMS_LOGISTICS.toggleSwatchFilter('re_attempted')"><span style="display:inline-block;width:10px;height:10px;background:#DBEAFE;border:1px solid #2563EB;border-radius:2px"></span>Re-attempted</span>
          <span class="ims-track-swatch" data-filter="return_in_transit" style="display:flex;align-items:center;gap:4px;cursor:pointer;padding:2px 6px;border-radius:4px;border:1.5px solid transparent" onclick="window.IMS_LOGISTICS.toggleSwatchFilter('return_in_transit')"><span style="display:inline-block;width:10px;height:10px;background:#EDE9FE;border:1px solid #7C3AED;border-radius:2px"></span>Return</span>
        </div>
      </div>`;
  }

  // Swatches are a shortcut into the exact same filter the dropdown
  // already provides — clicking one sets the dropdown's value and
  // re-runs filterTracking(), so there is only ever one filtering
  // code path. Clicking an already-active swatch clears the filter.
  function toggleSwatchFilter(filterValue){
    const sel = document.getElementById('ims-track-filter');
    if(!sel) return;
    sel.value = sel.value === filterValue ? '' : filterValue;
    filterTracking();
  }

  function buildTrackingTable(items, shipmentMap, latestEventMap, manifestMap){
    manifestMap = manifestMap || {};
    const now = new Date();
    if(!items.length){
      return `<div class="ims__empty"><i class="ti ti-inbox"></i><p>No orders match this filter</p></div>`;
    }
    const rows = items.map(o => {
      const manifestInfo = manifestMap[o.id] || null;
      const dispatched = o.dispatched_at ? new Date(o.dispatched_at) : null;
      const daysSince  = dispatched ? Math.floor((now - dispatched) / 86400000) : null;
      const isAlert    = o.status === 'dispatched' && daysSince !== null && daysSince >= 3 && daysSince < 5;
      const isDelayed  = o.status === 'dispatched' && daysSince !== null && daysSince >= 5;
      const isFailed   = o.status === 'failed_delivery';
      const isReAttempt= o.status === 're_attempted';
      const isReturn   = o.status === 'return_in_transit';

      // Row background
      let rowBg = '';
      if(isDelayed)    rowBg = 'background:#FEE2E2';
      else if(isAlert) rowBg = 'background:#FEF3C7';
      else if(isFailed)rowBg = 'background:#FED7AA';
      else if(isReAttempt) rowBg = 'background:#DBEAFE';
      else if(isReturn)    rowBg = 'background:#EDE9FE';

      // Platform badge
      const platName  = o.platforms?.name || '—';
      const platBadge = `<span class="ims__pill ims__pill--info" style="font-size:10px">${util().sanitize(platName)}</span>`;

      // Fulfillment type badge
      const ftColor = o.fulfillment_type === 'dropship' ? 'warning' : o.order_type === 'wholesale' ? 'accent' : 'neutral';
      const ftLabel = o.fulfillment_type === 'dropship' ? 'Dropship' : o.order_type === 'wholesale' ? 'Wholesale' : 'Retail';
      const ftBadge = `<span class="ims__pill ims__pill--${ftColor}" style="font-size:10px">${ftLabel}</span>`;

      // Customer — dropship uses end_customer_name
      const custName = util().sanitize(o.customer_name || o.end_customer_name || '—');

      // Postcode — dropship uses end_postcode
      const postcode = util().sanitize(o.postcode || o.end_postcode || '—');

      // Shipment data
      const shipment     = shipmentMap[o.id];
      const trackingNum  = shipment?.tracking_number || null;
      const courier      = shipment?.couriers || null;
      // Manifest-shipped orders show their manifest reference here instead
      // of a dash — there's genuinely no courier tracking number for an
      // own-fleet delivery, but there is real, relevant reference info.
      const trackingCell = manifestInfo
        ? `<span class="ims__link" onclick="window.IMS_LOGISTICS.openManifest?.('${util().jsArg(manifestInfo.manifestId||'')}')" title="On manifest ${util().sanitize(manifestInfo.manifestNumber)}"><i class="ti ti-truck"></i> ${util().sanitize(manifestInfo.manifestNumber)}</span>${manifestInfo.driverName ? `<div style="font-size:10px;color:var(--ims-text-muted)">${util().sanitize(manifestInfo.driverName)}</div>` : ''}`
        : (trackingNum ? util().sanitize(trackingNum) : '<span style="color:var(--ims-text-muted)">—</span>')
          + (shipment?.courier_order_ref ? `<div style="font-size:10px;color:var(--ims-text-muted);font-family:inherit" title="Courier order no.">Order no. ${util().sanitize(shipment.courier_order_ref)}</div>` : '');

      // Track button — only if tracking number AND courier URL exists
      // (manifest deliveries have no external tracking page to link to)
      const trackUrl = !manifestInfo && trackingNum && courier?.tracking_url_base
        ? util().buildTrackingUrl(courier, trackingNum, o.postcode || o.end_postcode || '')
        : null;
      const trackBtn = trackUrl
        ? util().trackingLink(courier, trackingNum, o.postcode || o.end_postcode || '', courier?.tracking_form_only ? '<i class="ti ti-clipboard-copy"></i>' : '<i class="ti ti-external-link"></i>', 'ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon')
        : '';

      // Days display
      const daysDisplay = daysSince !== null
        ? `<span style="font-weight:700;color:${isDelayed?'var(--ims-danger)':isAlert?'var(--ims-warning)':'var(--ims-text)'}">${daysSince}d</span>`
        : '—';

      // Status column — pill + latest tracking event below
      const latestEvent = latestEventMap[o.id];
      let statusBadge = '';
      if(manifestInfo){
        // Own-fleet delivery — courier-chasing language ("Check shipment
        // status", "Overdue — update tracking") makes no sense here, this
        // was already on a real manifest with a real driver assigned.
        if(isDelayed)    statusBadge = `<div style="font-size:10px;color:var(--ims-danger);margin-top:3px;font-weight:600">Still out ${daysSince}d — confirm with driver</div>`;
        else if(isAlert) statusBadge = `<div style="font-size:10px;color:var(--ims-warning);margin-top:3px;font-weight:600">Confirm delivery with driver</div>`;
      } else {
        if(isDelayed)    statusBadge = `<div style="font-size:10px;color:var(--ims-danger);margin-top:3px;font-weight:600">Overdue — update tracking</div>`;
        else if(isAlert) statusBadge = `<div style="font-size:10px;color:var(--ims-warning);margin-top:3px;font-weight:600">Check shipment status</div>`;
      }
      if(isFailed)statusBadge = `<div style="font-size:10px;color:var(--ims-danger);margin-top:3px;font-weight:600">Action required</div>`;
      else if(isReAttempt) statusBadge = `<div style="font-size:10px;color:var(--ims-accent);margin-top:3px">Following up</div>`;
      else if(isReturn)    statusBadge = `<div style="font-size:10px;color:#7C3AED;margin-top:3px">Return in transit</div>`;

      const latestLine = latestEvent
        ? `<div style="font-size:10px;color:var(--ims-text-sec);margin-top:3px;font-style:italic">${util().sanitize(latestEvent.tracking_status)} — ${util().formatDate(latestEvent.created_at)}</div>`
        : statusBadge;

      const statusCell = `<div>${util().pill(o.status)}</div>${latestLine}`;

      // Action buttons — vary by order status
      const trackingEditBtn = `<button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openEditTracking('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}','${util().jsArg(trackingNum?util().sanitize(trackingNum):'')}','${util().jsArg(courier?.id||shipment?.courier_id||'')}','${util().jsArg(shipment?.courier_order_ref||'')}')" title="Set/edit tracking number and courier order no."><i class="ti ti-barcode"></i></button>`;
      const updateBtn = `<button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openTrackingUpdate('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Update tracking status"><i class="ti ti-pencil"></i></button>`;
      const historyBtn = `<button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openTrackingHistory('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="View tracking history"><i class="ti ti-history"></i></button>`;
      const sysLogBtn  = `<button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openActivityLog('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="View system activity log"><i class="ti ti-list-details"></i></button>`;
      // Super Admin only, and only where there is actually a courier to
      // undo — an own-truck or self-collection job has nothing to reverse.
      const reassignBtn = (window.SM.AUTH.isSuperAdmin() && courier && ['dispatched','re_attempted'].includes(o.status))
        ? `<button class="ims__btn ims__btn--warning ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.reassignToOwnTruck('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Assigned to a courier by mistake? Reassign to own truck"><i class="ti ti-truck-return"></i></button>`
        : '';

      let actionBtns = '';
      if(o.status === 'dispatched' || o.status === 're_attempted'){
        // "Follow up with courier" is meaningless for a manifest delivery —
        // there's no external courier to chase, the driver is your own.
        const showFollowUp = !manifestInfo && daysSince !== null && daysSince >= 3;
        actionBtns = `
          <button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_LOGISTICS.confirmDelivered('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Confirm delivered"><i class="ti ti-check"></i> Delivered</button>
          <button class="ims__btn ims__btn--danger ims__btn--sm" onclick="window.IMS_LOGISTICS.openFailedDelivery('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Mark failed delivery"><i class="ti ti-x"></i> Failed</button>
          ${showFollowUp ? `<button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.openCourierFollowUp('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Follow up with courier"><i class="ti ti-mail"></i> Follow up</button>` : ''}`;
      } else if(o.status === 'failed_delivery'){
        actionBtns = `
          <button class="ims__btn ims__btn--info ims__btn--sm" onclick="window.IMS_LOGISTICS.requestReAttempt('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Request re-attempt"><i class="ti ti-refresh"></i> Re-attempt</button>
          <button class="ims__btn ims__btn--warning ims__btn--sm" onclick="window.IMS_LOGISTICS.requestReturn('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Return to warehouse"><i class="ti ti-arrow-back-up"></i> Return</button>
          <button class="ims__btn ims__btn--danger ims__btn--sm" onclick="window.IMS_LOGISTICS.markAsLost('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Mark as lost"><i class="ti ti-alert-triangle"></i> Mark lost</button>`;
      } else if(o.status === 'return_in_transit'){
        actionBtns = `
          <button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_LOGISTICS.markReturnReceived('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Mark return received"><i class="ti ti-package-import"></i> Return received</button>`;
      }

      return `<tr style="${rowBg}" data-order-id="${o.id}" data-status="${o.status}" data-days="${daysSince||0}" data-ft="${o.fulfillment_type||''}" data-platform="${util().sanitize(platName)}" data-courier="${util().sanitize(courier?.name||'')}">
        <td><span class="ims__link" onclick="window.IMS_ORDERS?.openOrder('${util().jsArg(o.id)}')">${util().sanitize(o.order_number)}</span></td>
        <td style="font-size:11px;color:var(--ims-text-muted)">${util().sanitize(o.external_ref||'—')}</td>
        <td>${platBadge}</td>
        <td>${ftBadge}</td>
        <td>${custName}</td>
        <td style="font-size:12px;font-family:monospace">${trackingCell}</td>
        <td style="font-size:12px">${postcode}</td>
        <td style="font-size:11px;white-space:nowrap">${dispatched ? util().formatDate(o.dispatched_at) : '—'}</td>
        <td style="text-align:center">${daysDisplay}</td>
        <td>${statusCell}</td>
        <td>
          <div class="ims__btn-group" style="flex-wrap:nowrap">
            ${trackingEditBtn}
            ${trackBtn}
            ${updateBtn}
            ${historyBtn}
            ${sysLogBtn}
            ${reassignBtn}
            ${actionBtns}
          </div>
        </td>
      </tr>`;
    }).join('');

    return `<div class="ims__card" style="overflow-x:auto">
      <div class="ims__table-wrap">
        <table class="ims__table" id="ims-tracking-table">
          <thead><tr>
            <th>Order #</th><th>Ref</th><th>Platform</th><th>Type</th><th>Customer</th>
            <th>Tracking #</th><th>Postcode</th><th>Dispatched</th>
            <th style="text-align:center">Days</th><th>Status</th><th>Actions</th>
          </tr></thead>
          <tbody id="ims-tracking-tbody">${rows}</tbody>
        </table>
      </div>
    </div>`;
  }

  function filterTracking(){
    const platFilter    = document.getElementById('ims-track-platform')?.value || '';
    const courierFilter = document.getElementById('ims-track-courier')?.value  || '';
    const statusFilter  = document.getElementById('ims-track-filter')?.value   || '';
    const now           = new Date();

    let filtered = window._trackingOrders || [];

    // Text search — partial match across tracking number (from the shipment
    // map), order number, and customer name. This is the field that was
    // genuinely missing: a valid tracking number returned nothing because
    // nothing here ever looked at shipments.tracking_number.
    const searchTerm = (document.getElementById('ims-track-search')?.value || '').trim().toLowerCase();
    if(searchTerm){
      filtered = filtered.filter(o => {
        const s = (window._shipmentMap || {})[o.id];
        const tracking = (s?.tracking_number || '').toLowerCase();
        const courierRef = (s?.courier_order_ref || '').toLowerCase();
        const orderNum = (o.order_number || '').toLowerCase();
        const custName = (o.customer_name || o.end_customer_name || '').toLowerCase();
        return tracking.includes(searchTerm) || courierRef.includes(searchTerm) || orderNum.includes(searchTerm) || custName.includes(searchTerm);
      });
    }

    // Platform filter
    if(platFilter) filtered = filtered.filter(o => o.platforms?.name === platFilter);

    // Courier filter — uses shipment map
    if(courierFilter) filtered = filtered.filter(o => {
      const s = (window._shipmentMap || {})[o.id];
      return s?.couriers?.name === courierFilter;
    });

    // Status filter
    if(statusFilter === 'alert')
      filtered = filtered.filter(o => { const d = o.dispatched_at ? Math.floor((now - new Date(o.dispatched_at))/86400000) : 0; return o.status==='dispatched' && d>=3 && d<5; });
    else if(statusFilter === 'delayed')
      filtered = filtered.filter(o => { const d = o.dispatched_at ? Math.floor((now - new Date(o.dispatched_at))/86400000) : 0; return o.status==='dispatched' && d>=5; });
    else if(statusFilter === 'failed_delivery')   filtered = filtered.filter(o => o.status==='failed_delivery');
    else if(statusFilter === 're_attempted')       filtered = filtered.filter(o => o.status==='re_attempted');
    else if(statusFilter === 'return_in_transit')  filtered = filtered.filter(o => o.status==='return_in_transit');
    else if(statusFilter === 'dropship')           filtered = filtered.filter(o => o.fulfillment_type==='dropship');

    // Sort — defaults to Newest first, matching loadTracking()'s own query
    // order exactly, so the initial page load and this dropdown's default
    // agree. All three values must stay in sync: the query order, this
    // fallback, and the dropdown's first <option>.
    const sortOrder = document.getElementById('ims-track-sort')?.value || 'newest';
    if(sortOrder === 'newest'){
      filtered = [...filtered].sort((a,b) => new Date(b.dispatched_at||b.created_at) - new Date(a.dispatched_at||a.created_at));
    } else if(sortOrder === 'order_number'){
      filtered = [...filtered].sort((a,b) => (a.order_number||'').localeCompare(b.order_number||'', undefined, {numeric:true}));
    } else {
      filtered = [...filtered].sort((a,b) => new Date(a.dispatched_at||a.created_at) - new Date(b.dispatched_at||b.created_at));
    }

    // Rebuild only the table body — target the dedicated wrapper by ID,
    // never the ambiguous '.ims__card' class, since the Lost Orders
    // section below also uses that class. Using .innerHTML (not
    // .outerHTML) on this wrapper means the wrapper div itself is never
    // destroyed, so it stays reliably targetable no matter how many
    // times the filter toggles between empty and non-empty results —
    // previously, an empty result replaced the entire wrapper (via
    // outerHTML + querySelector), leaving nothing for the next filter
    // click to find except the Lost Orders card by accident, which then
    // got silently overwritten.
    const wrap = document.getElementById('ims-tracking-table-wrap');
    if(wrap){
      wrap.innerHTML = buildTrackingTable(filtered, window._shipmentMap||{}, window._latestEventMap||{}, window._manifestMap||{});
    }

    // Highlight whichever swatch (if any) matches the active filter
    document.querySelectorAll('.ims-track-swatch').forEach(el=>{
      el.style.borderColor = el.dataset.filter === statusFilter ? 'var(--ims-text-sec)' : 'transparent';
    });
  }

  // ─── EDIT / SET TRACKING NUMBER ─────────────────────────────────
  // Orders can reach 'dispatched' via several paths — single courier
  // assign (confirmCourierAssignment, which DOES collect a tracking
  // number), bulk courier assign (confirmBulkAssign, which does NOT),
  // own truck, or a manifest — so a tracking number may never have been
  // recorded. This lets staff add or correct one directly from Tracking.
  async function openEditTracking(orderId, orderNumber, currentTracking, currentCourierId, currentCourierRef){
    const couriers=await _getActiveCouriers();
    const courierOpts=(couriers||[]).map(c=>`<option value="${c.id}" ${c.id===currentCourierId?'selected':''}>${util().sanitize(c.name)}</option>`).join('');
    ui().openModal(`Set tracking — ${orderNumber}`,`
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>Enter the courier's tracking number (to track it on their website) and, if they gave one, their own order number (for example V1's VDI number). The order number is what courier invoices list, so the invoice check matches it exactly.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full"><label class="ims__label ims__label--req">Courier</label><select class="ims__select" id="ims-et-courier"><option value="">Select courier</option>${courierOpts}</select></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Tracking / consignment number</label><input class="ims__input" type="text" id="ims-et-tracking" value="${util().sanitize(currentTracking||'')}" placeholder="Enter tracking number"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Courier order no.</label><input class="ims__input" type="text" id="ims-et-corder" value="${util().sanitize(currentCourierRef||'')}" placeholder="Optional, e.g. VDI242546"><span class="ims__input-hint">Enter at least one of the two.</span></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveTrackingNumber('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-check"></i> Save</button>
    `,'sm');
  }

  async function saveTrackingNumber(orderId, orderNumber){
    const courierId=document.getElementById('ims-et-courier')?.value;
    const tracking=document.getElementById('ims-et-tracking')?.value?.trim()||'';
    const courierRef=document.getElementById('ims-et-corder')?.value?.trim()||'';
    if(!courierId){ui().toast('Please select a courier','error');return;}
    if(!tracking&&!courierRef){ui().toast('Please enter the tracking number, the courier order no., or both','error');return;}

    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:existing}=await client.from('shipments').select('id').eq('order_id',orderId).eq('is_return_collection',false).order('created_at',{ascending:false}).limit(1).maybeSingle();

    if(existing){
      const {error}=await client.from('shipments').update({courier_id:courierId,tracking_number:tracking||null,courier_order_ref:courierRef||null}).eq('id',existing.id);
      if(error){ui().toast('Failed: '+error.message,'error');return;}
    } else {
      const {error}=await db().insert('shipments',{
        order_id:orderId,courier_id:courierId,tracking_number:tracking||null,courier_order_ref:courierRef||null,
        ship_mode:'courier',status:'dispatched',dispatched_at:new Date().toISOString()
      });
      if(error){ui().toast('Failed: '+error.message,'error');return;}
    }

    await logActivity(orderId, null, null, 'tracking_updated', [tracking?`Tracking number: ${tracking}`:'',courierRef?`Courier order no.: ${courierRef}`:''].filter(Boolean).join(', '));
    ui().closeModal();
    ui().toast('Saved','success');
    loadTracking();
  }

  // ─── OPEN TRACKING UPDATE MODAL ─────────────────────────────────
  async function openTrackingUpdate(orderId, orderNumber){
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);

    // Fetch existing events for this order (ascending for history)
    const { data: events } = await client
      .from('order_tracking_events')
      .select('tracking_status,notes,user_name,created_at')
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });

    const historyRows = (events || []).map((e, i) => {
      const isLatest = i === (events.length - 1);
      return `<tr style="${isLatest ? 'font-weight:600;background:var(--ims-surface2)' : ''}">
        <td style="font-size:11px;white-space:nowrap">${util().formatDate(e.created_at)}${util().formatDateTime ? ' ' + new Date(e.created_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) : ''}</td>
        <td style="font-size:12px">${util().sanitize(e.user_name||'System')}</td>
        <td>${util().sanitize(e.tracking_status)}</td>
        <td style="font-size:12px;color:var(--ims-text-sec)">${util().sanitize(e.notes||'—')}</td>
      </tr>`;
    }).join('');

    const historySection = events?.length
      ? `<hr class="ims__divider">
         <strong style="font-size:13px">Tracking history</strong>
         <div class="ims__table-wrap" style="margin-top:8px;max-height:220px;overflow-y:auto">
           <table class="ims__table">
             <thead><tr><th>Date/Time</th><th>User</th><th>Status</th><th>Notes</th></tr></thead>
             <tbody>${historyRows}</tbody>
           </table>
         </div>`
      : `<hr class="ims__divider"><div style="font-size:12px;color:var(--ims-text-muted);padding:8px 0">No tracking updates recorded yet. Add the first one above.</div>`;

    const statusOpts = TRACKING_STATUSES.map(s =>
      `<option value="${s}">${s}</option>`
    ).join('');

    ui().openModal(`Update tracking — ${orderNumber}`, `
      <div class="ims__form-grid">
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Tracking status</label>
          <select class="ims__select" id="ims-tev-status">
            <option value="">Select status...</option>
            ${statusOpts}
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Notes</label>
          <input class="ims__input" type="text" id="ims-tev-notes" placeholder="e.g. Driver attempted at 2pm, card left">
        </div>
      </div>
      <div class="ims__alert ims__alert--info" style="margin-top:4px;margin-bottom:0">
        <i class="ti ti-info-circle"></i>
        <span>Selecting <strong>Delivered ✓</strong> or <strong>Failed delivery</strong> will trigger the full automation flow automatically.</span>
      </div>
      ${historySection}
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveTrackingEvent('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')">
        <i class="ti ti-check"></i> Save update
      </button>
    `, 'lg');
  }

  // ─── SAVE TRACKING EVENT ────────────────────────────────────────
  async function saveTrackingEvent(orderId, orderNumber){
    const status = document.getElementById('ims-tev-status')?.value;
    const notes  = document.getElementById('ims-tev-notes')?.value?.trim() || null;

    if(!status){ ui().toast('Please select a tracking status','error'); return; }

    // Insert to order_tracking_events
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { error } = await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: status,
      notes:           notes,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });

    if(error){ ui().toast('Failed to save: ' + error.message, 'error'); return; }

    // Trigger automation based on status
    if(status === 'Delivered ✓'){
      ui().closeModal();
      await confirmDelivered(orderId, orderNumber);
    } else if(status === 'Failed delivery'){
      ui().closeModal();
      openFailedDelivery(orderId, orderNumber);
    } else if(status === 'Return received'){
      ui().closeModal();
      markReturnReceived(orderId, orderNumber);
    } else if(status === 'Return requested'){
      // Previously this just logged the text "Return requested" with no
      // actual status change, so the row never turned purple the way the
      // proper Failed Delivery → Return flow does. Routes through the same
      // requestReturn() used there, so however staff initiate a return,
      // the order genuinely moves to return_in_transit and the row colour
      // matches reality.
      ui().closeModal();
      requestReturn(orderId, orderNumber);
    } else {
      ui().closeModal();
      ui().toast(`Tracking updated: ${status}`, 'success');
      loadTracking();
    }
  }

  // ─── OPEN TRACKING HISTORY VIEWER ───────────────────────────────
  async function openTrackingHistory(orderId, orderNumber){
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: events } = await client
      .from('order_tracking_events')
      .select('tracking_status,notes,user_name,created_at')
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });

    const items = events || [];
    const rows = items.map((e, i) => {
      const isLatest = i === items.length - 1;
      const dt = new Date(e.created_at);
      const timeStr = dt.toLocaleTimeString('en-GB', {hour:'2-digit', minute:'2-digit'});
      return `<tr style="${isLatest ? 'background:var(--ims-surface2);font-weight:600' : ''}">
        <td style="font-size:11px;white-space:nowrap">${util().formatDate(e.created_at)} ${timeStr}</td>
        <td style="font-size:12px">${util().sanitize(e.user_name||'System')}</td>
        <td>
          <span class="ims__pill ims__pill--${
            e.tracking_status.includes('Delivered') ? 'success' :
            e.tracking_status.includes('Failed') || e.tracking_status.includes('Return') ? 'danger' :
            e.tracking_status.includes('Out for delivery') ? 'accent' : 'neutral'
          }" style="font-size:11px">${util().sanitize(e.tracking_status)}</span>
          ${isLatest ? '<span style="font-size:10px;color:var(--ims-text-muted);margin-left:6px">← current</span>' : ''}
        </td>
        <td style="font-size:12px;color:var(--ims-text-sec)">${util().sanitize(e.notes||'—')}</td>
      </tr>`;
    }).join('');

    ui().openModal(`Tracking history — ${orderNumber}`, `
      ${!items.length
        ? `<div class="ims__empty"><i class="ti ti-history"></i><p>No tracking updates yet</p><p style="font-size:12px;color:var(--ims-text-muted)">Use the pencil button on the tracking row to add updates</p></div>`
        : `<div class="ims__table-wrap">
            <table class="ims__table">
              <thead><tr><th>Date/Time</th><th>User</th><th>Status</th><th>Notes</th></tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </div>`
      }
    `, `
      <button class="ims__btn ims__btn--secondary" onclick="window.IMS_LOGISTICS.openTrackingUpdate('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}');window.SM.UI.closeModal()">
        <i class="ti ti-pencil"></i> Add update
      </button>
      <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal()">Close</button>
    `, 'lg');
  }

  // ─── COURIER TRACKING-NUMBER GATE ───────────────────────────────
  // Orders dispatched via courier on/after this date must have a
  // tracking number recorded before they can be confirmed Delivered or
  // Failed — enforced going forward only, per explicit decision not to
  // retroactively block orders already in Tracking without one. Own
  // truck, self-collection, and manifest deliveries are never subject
  // to this (no third-party courier tracking exists for those methods).
  // Adjust this date if you want the enforcement start point to differ
  // from today.
  const COURIER_TRACKING_ENFORCEMENT_DATE = '2026-07-04';

  async function _blockedForMissingTracking(orderId, dispatchedAt){
    if(!dispatchedAt) return false;
    if(new Date(dispatchedAt) < new Date(COURIER_TRACKING_ENFORCEMENT_DATE)) return false;

    // Not courier-shipped (own truck / self-collection / manifest) —
    // no assigned_courier_id means no third-party tracking applies.
    const { data: job } = await db().select('logistics_jobs',{
      select:'assigned_courier_id', eq:{order_id:orderId}, maybeSingle:true, noCompanyFilter:true
    });
    if(!job?.assigned_courier_id) return false;

    const { data: ship } = await db().select('shipments',{
      select:'tracking_number', eq:{order_id:orderId,is_return_collection:false}, order:{col:'created_at',asc:false}, limit:1, maybeSingle:true, noCompanyFilter:true
    });
    return !(ship?.tracking_number && ship.tracking_number.trim());
  }

  function confirmDelivered(orderId, orderNumber){
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Confirm delivery — ${orderNumber}`, `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>This will close the order from tracking. For wholesale orders, a receivable will be created automatically.</span>
      </div>
      <div class="ims__form-group">
        <label class="ims__label ims__label--req">Delivery date</label>
        <input class="ims__input" type="date" id="ims-delconf-date" value="${todayStr}" max="${todayStr}">
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.confirmDeliveredSave('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-check"></i> Confirm delivery</button>
    `, 'sm');
  }

  // Shared by updateDelivery (the manifest's own dropdown) and
  // confirmDeliveredSave (the Tracking tab) - a delivery can legitimately
  // be marked from either screen, so both need to keep the parent
  // manifest's own status in sync rather than only one of them knowing
  // how. Closes out the manifest once every one of its stops has reached
  // a final state; previously only the manifest-dropdown path did this at
  // all, so anything resolved via Tracking left its manifest stuck on
  // 'dispatched' forever regardless of how many stops were actually done.
  async function checkManifestCompletion(manifestId){
    if(!manifestId) return;
    const {data:allItems} = await db().select('manifest_items',{
      select:'delivery_status', eq:{manifest_id:manifestId}, noCompanyFilter:true
    });
    // A stop with an outcome means the van has left: mark it departed if
    // nobody pressed "Van departed", so its orders can no longer be amended.
    if((allItems||[]).some(i=>i.delivery_status && i.delivery_status!=='pending')){
      const {data:mf}=await db().select('manifests',{select:'departed_at',eq:{id:manifestId},maybeSingle:true,noCompanyFilter:true});
      if(mf && !mf.departed_at) await db().update('manifests',manifestId,{departed_at:new Date().toISOString(),departed_by:window.SM.AUTH.user?.id||null});
    }
    const unresolved = (allItems||[]).filter(i=>!['delivered','failed','partial','collected'].includes(i.delivery_status));
    if(allItems?.length && !unresolved.length){
      await db().update('manifests',manifestId,{status:'completed'});
    }
  }

  async function confirmDeliveredSave(orderId, orderNumber){
    const dateVal = document.getElementById('ims-delconf-date')?.value;
    if(!dateVal){ ui().toast('Please select a delivery date','error'); return; }

    if(await _isAlreadyDelivered(orderId)){
      ui().toast(`${orderNumber} is already marked delivered — no changes made.`,'warning',5000);
      ui().closeModal();
      loadTracking();
      return;
    }

    const deliveredAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const { data: order } = await db().select('orders',{
      select:'status,order_type,customer_id,total_value,order_number,dispatched_at,platform_id,payment_status',
      eq:{id:orderId}, maybeSingle:true, noCompanyFilter:true
    });

    if(await _blockedForMissingTracking(orderId, order?.dispatched_at)){
      ui().toast('This courier order has no tracking number recorded yet. Please close this and add one first using the barcode icon on the Tracking tab, then mark delivered.','error',7000);
      return;
    }

    const oldStatus = order?.status || 'dispatched';
    await db().update('orders', orderId, {
      status: 'delivered',
      delivered_at: deliveredAt
    });
    // Keep the shipments record's own status field in sync — it's a
    // separate field from orders.status and was previously never
    // updated after creation, leaving it stuck on 'dispatched' forever.
    const { data: ship } = await db().select('shipments',{
      select:'id', eq:{order_id:orderId,is_return_collection:false}, order:{col:'created_at',asc:false}, limit:1, maybeSingle:true, noCompanyFilter:true
    });
    if(ship?.id) await db().update('shipments', ship.id, { status:'delivered' });
    await logActivity(orderId, oldStatus, 'delivered', 'delivered', null);
    await deductStockOnDelivery(orderId, orderNumber);

    // Keep the manifest stop's own record in sync too, if this order was
    // on one — Tracking is a genuinely valid place to mark a delivery
    // from, not just the manifest's own dropdown, so this needs to update
    // manifest_items and check completion the same way that path does.
    const { data: job } = await db().select('logistics_jobs',{
      select:'id', eq:{order_id:orderId}, maybeSingle:true, noCompanyFilter:true
    });
    if(job?.id){
      const { data: mi } = await db().select('manifest_items',{
        select:'id,manifest_id', eq:{logistics_job_id:job.id}, maybeSingle:true, noCompanyFilter:true
      });
      if(mi?.id){
        await db().update('manifest_items', mi.id, { delivery_status:'delivered', delivered_at: deliveredAt });
        await checkManifestCompletion(mi.manifest_id);
      }
    }

    // Book the marketplace commission as an expense on delivery, mirroring
    // how the wholesale receivable auto-creates here. Only for orders on a
    // platform with a commission rate set (marketplaces) — direct sales have
    // no rate and book nothing. Guarded against double-booking via a
    // reference tag unique to this order + category.
    if (order?.platform_id) {
      const client2 = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
      const { data: rate } = await client2.from('platform_commissions')
        .select('commission_percent,commission_fixed')
        .eq('company_id', window.SM.AUTH.company?.id)
        .eq('platform_id', order.platform_id)
        .maybeSingle();
      const calc = util().calcCommission(order.total_value, rate);
      if (calc.hasCommission && calc.commission > 0) {
        const ref = `COMM-${order.order_number}`;
        const { data: already } = await client2.from('transactions')
          .select('id').eq('reference', ref).eq('category', 'Marketplace fees').maybeSingle();
        if (!already) {
          await client2.from('transactions').insert({
            company_id:  window.SM.AUTH.company?.id,
            date:        deliveredAt.split('T')[0],
            type:        'expense',
            category:    'Marketplace fees',
            amount:      calc.commission,
            reference:   ref,
            description: `Commission on ${order.order_number}`,
            user_id:     window.SM.AUTH.user?.id || null
          });
        }
      }
    }

    // Auto-create accounts_receivable for wholesale orders
    if(order?.order_type === 'wholesale' && order?.customer_id){
      const { data: existing } = await db().select('accounts_receivable',{
        eq:{ order_id: orderId }, maybeSingle:true, noCompanyFilter:true
      });
      const { data: cust } = await db().select('customers',{select:'credit_terms',eq:{id:order.customer_id},maybeSingle:true,noCompanyFilter:true});
      const dueDate = util().resolveReceivableDueDate(order.payment_status, cust?.credit_terms);
      if(!existing){
        if(dueDate){
          await db().insert('accounts_receivable',{
            order_id:    orderId,
            customer_id: order.customer_id,
            amount:      order.total_value || 0,
            paid_amount: 0,
            status:      'unpaid',
            invoice_ref: order.order_number,
            due_date:    dueDate
          });
          ui().closeModal();
          ui().toast(`${orderNumber} delivered — receivable created in Accounts`, 'success', 5000);
        } else {
          ui().closeModal();
          ui().toast(`${orderNumber} marked delivered`, 'success');
        }
      } else if(existing.status === 'provisional'){
        // A provisional receivable was raised when this order first went
        // out on its manifest — marking delivered here (rather than via
        // the manifest's own dropdown) previously left it stuck at
        // provisional forever, showing "In transit" in Accounts
        // regardless of the order actually being delivered. This flips
        // it the same correct way updateDelivery() already does.
        if(dueDate){
          await db().update('accounts_receivable', existing.id, { status:'unpaid', due_date: dueDate });
        } else {
          await db().update('accounts_receivable', existing.id, { status:'paid', paid_amount: existing.amount, due_date: new Date().toISOString().split('T')[0] });
        }
        ui().closeModal();
        ui().toast(`${orderNumber} marked delivered`, 'success');
      } else {
        ui().closeModal();
        ui().toast(`${orderNumber} marked delivered`, 'success');
      }
    } else {
      ui().closeModal();
      ui().toast(`${orderNumber} marked delivered`, 'success');
    }
    window.SM.EMAIL.send(orderId, 'delivered');
    loadTracking();
  }

  // ─── FAILED DELIVERY ────────────────────────────────────────────
  function openFailedDelivery(orderId, orderNumber){
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Failed delivery — ${orderNumber}`, `
      <div class="ims__alert ims__alert--warning" style="margin-bottom:14px">
        <i class="ti ti-alert-triangle"></i>
        <span>Record the failed delivery attempt. You will then choose to request a re-attempt or return the shipment.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Failed delivery date</label>
          <input class="ims__input" type="date" id="ims-fail-date" value="${todayStr}" max="${todayStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Reason for failed delivery</label>
          <select class="ims__select" id="ims-fail-reason">
            <option value="">Select reason...</option>
            <option value="No one home">No one home</option>
            <option value="Access issues">Access / gate / entry issues</option>
            <option value="Wrong address">Wrong address details</option>
            <option value="Customer refused delivery">Customer refused delivery</option>
            <option value="Damaged in transit">Damaged in transit</option>
            <option value="Courier lost parcel">Courier lost parcel</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div class="ims__form-group ims__form-group--full" id="ims-fail-other-wrap" style="display:none">
          <label class="ims__label">Other reason (specify)</label>
          <input class="ims__input" type="text" id="ims-fail-other" placeholder="Describe reason">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Additional notes</label>
          <textarea class="ims__textarea" id="ims-fail-notes" rows="2" placeholder="Courier reference, contact made, etc."></textarea>
        </div>
      </div>
      <hr class="ims__divider">
      <strong style="font-size:13px">What would you like to do next?</strong>
      <div id="ims-fail-choices-normal" style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:10px">
        <div style="padding:14px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);cursor:pointer" onclick="window.IMS_LOGISTICS._failChoice='reattempt';document.querySelectorAll('.ims-fail-choice').forEach(el=>el.style.borderColor='var(--ims-border)');this.style.borderColor='var(--ims-accent)';" class="ims-fail-choice">
          <div style="font-weight:600;margin-bottom:4px"><i class="ti ti-refresh" style="color:var(--ims-accent)"></i> Request re-attempt</div>
          <div style="font-size:12px;color:var(--ims-text-sec)">Ask courier to attempt delivery again. Order stays on tracking as blue until delivered.</div>
        </div>
        <div style="padding:14px;border:1.5px solid var(--ims-border);border-radius:var(--ims-radius);cursor:pointer" onclick="window.IMS_LOGISTICS._failChoice='return';document.querySelectorAll('.ims-fail-choice').forEach(el=>el.style.borderColor='var(--ims-border)');this.style.borderColor='var(--ims-warning)';" class="ims-fail-choice">
          <div style="font-weight:600;margin-bottom:4px"><i class="ti ti-arrow-back-up" style="color:var(--ims-warning)"></i> Return to warehouse</div>
          <div style="font-size:12px;color:var(--ims-text-sec)">Instruct courier to return shipment. Order turns purple on tracking until received back.</div>
        </div>
      </div>
      <div id="ims-fail-choices-lost" style="display:none;margin-top:10px">
        <div style="padding:14px;border:1.5px solid var(--ims-danger);border-radius:var(--ims-radius);background:var(--ims-danger-bg)">
          <div style="font-weight:600;margin-bottom:4px;color:var(--ims-danger)"><i class="ti ti-alert-triangle"></i> Mark order as lost</div>
          <div style="font-size:12px;color:var(--ims-text-sec)">Since the courier has confirmed the parcel is lost, there's nothing to re-attempt or return. Confirming below marks the order as lost and moves it to Lost &amp; Damaged, where you can raise a courier claim.</div>
        </div>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.confirmFailed('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-check"></i> Confirm & proceed</button>
    `, 'lg');
    window.IMS_LOGISTICS._failChoice = null;

    // Script tags injected via innerHTML (as openModal() does) never
    // execute in any browser — that's why this listener previously did
    // nothing at all. Attaching it here, as a normal JS call right after
    // the modal's HTML is in the DOM, is the correct approach.
    document.getElementById('ims-fail-reason')?.addEventListener('change', function(){
      const otherWrap = document.getElementById('ims-fail-other-wrap');
      if(otherWrap) otherWrap.style.display = this.value === 'Other' ? '' : 'none';
      const isLost = this.value === 'Courier lost parcel';
      const normalChoices = document.getElementById('ims-fail-choices-normal');
      const lostChoice = document.getElementById('ims-fail-choices-lost');
      if(normalChoices) normalChoices.style.display = isLost ? 'none' : '';
      if(lostChoice) lostChoice.style.display = isLost ? '' : 'none';
      window.IMS_LOGISTICS._failChoice = isLost ? 'lost' : null;
    });
  }

  async function confirmFailed(orderId, orderNumber){
    const dateVal     = document.getElementById('ims-fail-date')?.value;
    const reasonSel   = document.getElementById('ims-fail-reason')?.value;
    const reasonOther = document.getElementById('ims-fail-other')?.value?.trim();
    const notes       = document.getElementById('ims-fail-notes')?.value?.trim();
    const choice      = window.IMS_LOGISTICS._failChoice;

    if(!dateVal){ ui().toast('Please select the failed delivery date','error'); return; }
    if(!reasonSel){ ui().toast('Please select a reason','error'); return; }
    if(reasonSel === 'Other' && !reasonOther){ ui().toast('Please specify the reason','error'); return; }
    if(!choice){ ui().toast('Please select re-attempt or return','error'); return; }

    const finalReason = reasonSel === 'Other' ? reasonOther : reasonSel;
    const fullNotes   = notes ? `${finalReason} — ${notes}` : finalReason;
    const failedAt    = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const { data: orderData } = await db().select('orders',{
      select:'status,failed_delivery_count,dispatched_at',
      eq:{id:orderId}, maybeSingle:true, noCompanyFilter:true
    });

    if(await _blockedForMissingTracking(orderId, orderData?.dispatched_at)){
      ui().toast('This courier order has no tracking number recorded yet. Please close this and add one first using the barcode icon on the Tracking tab, then try again.','error',7000);
      return;
    }

    const authClient = window.supabase.createClient(
      window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
      {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
    );

    // A courier-confirmed lost parcel has no re-attempt/return path —
    // go straight to lost, matching markAsLost()/confirmMarkAsLost()'s
    // own flow, including the follow-up "raise a claim?" prompt.
    if(choice === 'lost'){
      const oldStatus = orderData?.status || 'dispatched';

      const { error: lostErr } = await authClient.from('orders')
        .update({ status:'lost' }).eq('id', orderId);
      if(lostErr){ ui().toast('Failed to mark as lost: ' + lostErr.message, 'error'); return; }

      const { data: lostShip } = await authClient.from('shipments')
        .select('id').eq('order_id',orderId).eq('is_return_collection',false).order('created_at',{ascending:false}).limit(1).maybeSingle();
      // shipments.status has a DB check constraint with no 'lost' value —
      // only pending/packed/dispatched/in_transit/out_for_delivery/
      // delivered/failed/returned are allowed. 'failed' is the closest
      // valid state; orders.status retains the precise 'lost' distinction
      // and is what the Shipments tab's analytics actually classify by.
      if(lostShip?.id) await authClient.from('shipments').update({status:'failed'}).eq('id',lostShip.id);

      await authClient.from('order_tracking_events').insert({
        company_id:      window.SM.AUTH.company?.id,
        order_id:        orderId,
        tracking_status: 'Marked as lost',
        notes:           fullNotes,
        created_at:      failedAt,
        user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
        user_id:         window.SM.AUTH.user?.id || null
      });
      await logActivity(orderId, oldStatus, 'lost', 'marked_lost', fullNotes);

      ui().closeModal();
      loadTracking();

      setTimeout(()=>{
        ui().openModal('Raise courier claim?', `
          <div class="ims__alert ims__alert--info" style="margin-bottom:0">
            <i class="ti ti-info-circle"></i>
            <span><strong>${util().sanitize(orderNumber)}</strong> has been marked as lost. Would you like to raise a courier claim now?</span>
          </div>
        `, `
          <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Not now</button>
          <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal();setTimeout(()=>window.IMS_LOGISTICS.openRaiseClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}'),300)">
            <i class="ti ti-file-alert"></i> Raise courier claim
          </button>
        `, 'sm');
      }, 600);
      return;
    }

    // 1. Use already-fetched status and fail count
    const oldStatus  = orderData?.status || 'dispatched';
    const failCount  = (orderData?.failed_delivery_count || 0) + 1;

    // 2. Save failed_delivery status
    const { error: failErr } = await authClient.from('orders').update({
      status:               'failed_delivery',
      failure_reason:       finalReason,
      last_failed_at:       failedAt,
      failed_delivery_count: failCount
    }).eq('id', orderId);
    if(failErr){ ui().toast('Failed to update order: ' + failErr.message, 'error'); return; }

    const { data: failShip } = await authClient.from('shipments')
      .select('id').eq('order_id',orderId).eq('is_return_collection',false).order('created_at',{ascending:false}).limit(1).maybeSingle();
    // shipments.status constraint has no 'failed_delivery' value — 'failed' is correct here.
    if(failShip?.id) await authClient.from('shipments').update({status:'failed'}).eq('id',failShip.id);

    // 3. Log to tracking events
    await authClient.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Failed delivery',
      notes:           fullNotes,
      created_at:      failedAt,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, oldStatus, 'failed_delivery', 'delivery_failed', fullNotes);

    ui().closeModal();

    // 4. Proceed with choice — pass finalReason so return knows why
    if(choice === 'reattempt'){
      await requestReAttempt(orderId, orderNumber, true);
    } else {
      await requestReturn(orderId, orderNumber, true, finalReason);
    }
  }

  // ─── RE-ATTEMPT ─────────────────────────────────────────────────
  async function requestReAttempt(orderId, orderNumber, skipModal = false){
    if(skipModal){
      const authClient = window.supabase.createClient(
        window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
        {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
      );
      const { error: raErr } = await authClient.from('orders').update({
        status:                   're_attempted',
        re_attempt_requested_at:  new Date().toISOString()
      }).eq('id', orderId);
      if(raErr){ ui().toast('Failed to update order: ' + raErr.message, 'error'); return; }

      await authClient.from('order_tracking_events').insert({
        company_id:      window.SM.AUTH.company?.id,
        order_id:        orderId,
        tracking_status: 'Re-attempt requested',
        notes:           'Courier re-attempt requested',
        user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
        user_id:         window.SM.AUTH.user?.id || null
      });
      await logActivity(orderId, 'failed_delivery', 're_attempted', 're_attempt_requested', 'Re-attempt requested with courier');
      ui().toast(`${orderNumber} — re-attempt requested. Row is now blue on tracking.`, 'success', 5000);
      loadTracking();
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Request re-attempt — ${orderNumber}`, `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>This will update the order status to <strong>Re-attempted</strong> and highlight it blue on tracking for follow-up until delivered or failed again.</span>
      </div>
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label ims__label--req">Re-attempt requested date</label>
        <input class="ims__input" type="date" id="ims-reattempt-date" value="${todayStr}" max="${todayStr}">
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Notes for courier (optional)</label>
        <textarea class="ims__textarea" id="ims-reattempt-notes" rows="2" placeholder="e.g. Customer will be home on Wednesday afternoon"></textarea>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.confirmReAttempt('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-refresh"></i> Confirm re-attempt</button>
    `, 'sm');
  }

  async function confirmReAttempt(orderId, orderNumber){
    const dateVal = document.getElementById('ims-reattempt-date')?.value;
    const notes = document.getElementById('ims-reattempt-notes')?.value?.trim();
    if(!dateVal){ ui().toast('Please select the re-attempt requested date','error'); return; }
    const requestedAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const authClient = window.supabase.createClient(
      window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
      {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
    );
    const { error } = await authClient.from('orders').update({
      status:                  're_attempted',
      re_attempt_requested_at: requestedAt
    }).eq('id', orderId);
    if(error){ ui().toast('Failed: ' + error.message, 'error'); return; }

    await authClient.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Re-attempt requested',
      notes:           notes || 'Re-attempt requested with courier',
      created_at:      requestedAt,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, 'failed_delivery', 're_attempted', 're_attempt_requested', notes || 'Re-attempt requested with courier');
    ui().closeModal();
    ui().toast(`${orderNumber} — re-attempt requested. Row is now blue on tracking.`, 'success', 5000);
    loadTracking();
  }

  // ─── RETURN TO WAREHOUSE ────────────────────────────────────────
  async function requestReturn(orderId, orderNumber, skipModal = false, returnReason = null){
    if(skipModal){
      const authClient = window.supabase.createClient(
        window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
        {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
      );

      // Guard against re-triggering this on an order that's already been
      // resolved — e.g. selecting "Return requested" from the Tracking log
      // dropdown on an order that was already marked received back at the
      // warehouse days ago, which would otherwise silently regress a
      // correctly-closed order back to return_in_transit.
      const { data: current } = await authClient.from('orders').select('status').eq('id', orderId).maybeSingle();
      if(current && ['return_received','delivered','refunded','cancelled'].includes(current.status)){
        ui().toast(`${orderNumber} is already ${current.status.replace('_',' ')} — a return can't be re-requested from here.`, 'warning', 6000);
        return;
      }

      const notes = returnReason || 'Return to warehouse requested';

      const { error: retErr } = await authClient.from('orders').update({
        status:               'return_in_transit',
        return_requested_at:  new Date().toISOString(),
        return_reason:        returnReason || null
      }).eq('id', orderId);
      if(retErr){ ui().toast('Failed to update order: ' + retErr.message, 'error'); return; }

      await authClient.from('order_tracking_events').insert({
        company_id:      window.SM.AUTH.company?.id,
        order_id:        orderId,
        tracking_status: 'Return in transit',
        notes:           notes,
        user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
        user_id:         window.SM.AUTH.user?.id || null
      });
      await logActivity(orderId, 'failed_delivery', 'return_in_transit', 'return_requested', notes);
      ui().toast(`${orderNumber} — return requested. Row is now purple on tracking.`, 'info', 5000);
      loadTracking();

      // If reason is damage-related, prompt to raise a claim
      if(returnReason && returnReason.toLowerCase().includes('damage')){
        setTimeout(()=>{
          ui().openModal('Raise damage claim?', `
            <div class="ims__alert ims__alert--warning" style="margin-bottom:0">
              <i class="ti ti-alert-triangle"></i>
              <span><strong>${util().sanitize(orderNumber)}</strong> is returning due to damage in transit. Would you like to raise a courier claim for the damaged goods?</span>
            </div>
          `, `
            <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Not now</button>
            <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal();setTimeout(()=>window.IMS_LOGISTICS.openRaiseClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}'),300)">
              <i class="ti ti-file-alert"></i> Raise damage claim
            </button>
          `, 'sm');
        }, 800);
      }
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Return to warehouse — ${orderNumber}`, `
      <div class="ims__alert ims__alert--warning" style="margin-bottom:14px">
        <i class="ti ti-alert-triangle"></i>
        <span>This will instruct the courier to return the shipment. The order will appear <strong>purple</strong> on tracking until the return is received at the warehouse.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Return requested date</label>
          <input class="ims__input" type="date" id="ims-return-date" value="${todayStr}" max="${todayStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Return reason</label>
          <select class="ims__select" id="ims-return-reason">
            <option value="">Select reason...</option>
            <option value="Customer refused">Customer refused delivery</option>
            <option value="Wrong address — cannot locate">Wrong address — cannot locate</option>
            <option value="Damaged — returning for inspection">Damaged — returning for inspection</option>
            <option value="No one home — multiple attempts">No one home — multiple attempts</option>
            <option value="Order cancelled by customer">Order cancelled by customer</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Additional notes</label>
          <textarea class="ims__textarea" id="ims-return-notes" rows="2" placeholder="Courier reference number, contact made, etc."></textarea>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
            <input type="checkbox" id="ims-return-notify" checked> Notify courier by email to arrange the return
          </label>
          <span class="ims__input-hint">Opens an editable email to the courier's customer-service address before finishing.</span>
        </div>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--warning" onclick="window.IMS_LOGISTICS.confirmReturn('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-arrow-back-up"></i> Confirm return</button>
    `, 'sm');
  }

  // Opens an editable email asking the courier to arrange return of the
  // consignment to our warehouse. Uses the same direct-send helper as
  // follow-ups/claims - optional, editable, and only sent on confirm.
  async function openReturnNotify(orderId, orderNumber, reasonNote){
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: ship } = await client.from('shipments')
      .select('tracking_number,couriers(name,cs_email)')
      .eq('order_id', orderId).eq('is_return_collection', false).order('created_at',{ascending:false}).limit(1).maybeSingle();

    const courier     = ship?.couriers;
    const courierName = courier?.name || 'Courier';
    // Delivered by our own van: there is no courier to notify (the goods
    // come back on the van).
    if(!ship){
      ui().toast(`${orderNumber} was delivered by our own van: no courier to notify. The goods come back on the van; use "Return received" when they are back.`,'info',9000);
      return;
    }
    const csEmail     = courier?.cs_email || '';
    const tracking    = ship?.tracking_number || 'N/A';
    const compName    = window.SM.AUTH.company?.name || 'MDF London Ltd';

    const subject = `Return request - ${tracking} - ${orderNumber}`;
    const body =
`Dear ${courierName} Team,

We would like to arrange the return of the consignment below to our warehouse.

Tracking Number:   ${tracking}
Order Reference:   ${orderNumber}
Reason:            ${reasonNote || 'Return to sender'}

Please advise on the process for returning this consignment to us, including any reference number, collection arrangements, or charges that apply.

Kind regards,
${compName}`;

    ui().openModal(`Notify courier - return ${orderNumber}`, `
      <div class="ims__form-grid" style="margin-bottom:12px">
        <div class="ims__form-group">
          <label class="ims__label">Send to</label>
          <input class="ims__input" type="email" id="ims-rn-email" value="${util().sanitize(csEmail)}" placeholder="Courier customer-service email">
          ${csEmail?'':`<span class="ims__input-hint" style="color:var(--ims-warning)">${util().sanitize(courier?.name||'This courier')} has no customer-services email yet: add it in Logistics &rarr; Couriers, and it will fill in by itself next time.</span>`}
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Subject</label>
          <input class="ims__input" type="text" id="ims-rn-subject" value="${util().sanitize(subject)}">
        </div>
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Email body (edit as needed before sending)</label>
        <textarea class="ims__textarea" id="ims-rn-body" rows="13" style="font-family:monospace;font-size:12px">${util().sanitize(body)}</textarea>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Skip email</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.sendReturnNotify('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-send"></i> Send to courier</button>
    `, 'lg');
  }

  async function sendReturnNotify(orderId, orderNumber){
    const to      = document.getElementById('ims-rn-email')?.value?.trim() || '';
    const subject = document.getElementById('ims-rn-subject')?.value || '';
    const body    = document.getElementById('ims-rn-body')?.value || '';
    if(!to){ ui().toast('Please enter the courier email','error'); return; }

    const result = await sendCourierEmail({
      to, subject, body,
      cc: window.SM.AUTH.company?.email || 'salesmdflondon@gmail.com',
      orderId,
      emailType: 'courier_return_request',
      logNote: `Return notification sent to courier for ${orderNumber}`
    });
    if(!result.sent) return;

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Return request sent to courier',
      notes:           `Return notification emailed to ${to}`,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });

    ui().closeModal();
    ui().toast(`Return request sent to ${to}`, 'success', 5000);
    loadTracking();
  }

  async function confirmReturn(orderId, orderNumber){
    const dateVal = document.getElementById('ims-return-date')?.value;
    const reason = document.getElementById('ims-return-reason')?.value;
    const notes  = document.getElementById('ims-return-notes')?.value?.trim();
    if(!dateVal){ ui().toast('Please select the return requested date','error'); return; }
    if(!reason){ ui().toast('Please select a return reason','error'); return; }
    const fullNotes = notes ? `${reason} — ${notes}` : reason;
    const requestedAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const authClient = window.supabase.createClient(
      window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
      {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
    );
    const { data: ord } = await authClient.from('orders').select('status').eq('id',orderId).single();
    const { error } = await authClient.from('orders').update({
      status:              'return_in_transit',
      return_requested_at: requestedAt,
      return_reason:       reason
    }).eq('id', orderId);
    if(error){ ui().toast('Failed: ' + error.message, 'error'); return; }

    await authClient.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Return in transit',
      notes:           fullNotes,
      created_at:      requestedAt,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, ord?.status || 'failed_delivery', 'return_in_transit', 'return_requested', fullNotes);
    const notifyCourier = document.getElementById('ims-return-notify')?.checked;
    ui().closeModal();
    ui().toast(`${orderNumber} - return to warehouse initiated. Row is now purple on tracking.`, 'info', 5000);
    loadTracking();

    // Optional courier notification - opens an editable email, does not
    // auto-send. Deferred slightly so it does not clash with the toast/claim
    // prompt, and skipped silently if damage (the claim prompt takes priority).
    if (notifyCourier && !reason.toLowerCase().includes('damage')) {
      setTimeout(() => openReturnNotify(orderId, orderNumber, fullNotes), 400);
    }

    // Prompt claim if damage-related
    if(reason.toLowerCase().includes('damage')){
      setTimeout(()=>{
        ui().openModal('Raise damage claim?', `
          <div class="ims__alert ims__alert--warning" style="margin-bottom:0">
            <i class="ti ti-alert-triangle"></i>
            <span><strong>${util().sanitize(orderNumber)}</strong> is returning due to damage. Would you like to raise a courier claim?</span>
          </div>
        `, `
          <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Not now</button>
          <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal();setTimeout(()=>window.IMS_LOGISTICS.openRaiseClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}'),300)">
            <i class="ti ti-file-alert"></i> Raise damage claim
          </button>
        `, 'sm');
      }, 800);
    }
  }

  // ─── MARK RETURN RECEIVED ───────────────────────────────────────
  function markReturnReceived(orderId, orderNumber){
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Return received — ${orderNumber}`, `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>Confirm the shipment has been physically received back at the warehouse. The order will move to the <strong>Lost &amp; Damaged</strong> section below for claim management.</span>
      </div>
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label ims__label--req">Return received date</label>
        <input class="ims__input" type="date" id="ims-retrcv-date" value="${todayStr}" max="${todayStr}">
      </div>
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label">Stock action</label>
        <select class="ims__select" id="ims-retrcv-stock">
          <option value="restore">Restore stock to inventory</option>
          <option value="inspect">Hold for inspection — do not restore yet</option>
          <option value="damaged">Mark as damaged — do not restore</option>
        </select>
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Notes</label>
        <textarea class="ims__textarea" id="ims-retrcv-notes" rows="2" placeholder="Condition of goods, any damage noted..."></textarea>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success" onclick="window.IMS_LOGISTICS.confirmReturnReceived('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-package-import"></i> Confirm received</button>
    `, 'sm');
  }

  async function confirmReturnReceived(orderId, orderNumber){
    const dateVal      = document.getElementById('ims-retrcv-date')?.value;
    const stockAction = document.getElementById('ims-retrcv-stock')?.value;
    const notes       = document.getElementById('ims-retrcv-notes')?.value?.trim();
    if(!dateVal){ ui().toast('Please select the return received date','error'); return; }
    const fullNotes   = `Stock action: ${stockAction}${notes ? ' — ' + notes : ''}`;
    const receivedAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const authClient = window.supabase.createClient(
      window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
      {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
    );

    const { error: rcvErr } = await authClient.from('orders').update({
      status:             'return_received',
      return_received_at: receivedAt
    }).eq('id', orderId);
    if(rcvErr){ ui().toast('Failed: ' + rcvErr.message, 'error'); return; }

    await authClient.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Return received at warehouse',
      notes:           fullNotes,
      created_at:      receivedAt,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, 'return_in_transit', 'return_received', 'return_received', fullNotes);
    // A courier collection for this return is now complete.
    await authClient.from('shipments').update({status:'returned'}).eq('order_id',orderId).eq('is_return_collection',true).neq('status','returned');

    let toastMsg = `${orderNumber} — return received at warehouse.`;

    if(stockAction === 'restore'){
      // Delegates to the shared smartRestock helper — this order came back
      // via return_in_transit (a failed delivery attempt), meaning it very
      // likely never actually reached 'delivered', so quantity was never
      // deducted in the first place (only reserved at order creation).
      // smartRestock checks this and releases the reservation instead of
      // wrongly inflating quantity; if the order genuinely was delivered
      // before this return, it restocks correctly instead.
      const restockedCount = await smartRestock(orderId, orderNumber, 'Customer return received');
      toastMsg += restockedCount ? ' Stock updated.' : ' No stock action taken (already processed, or nothing to restock).';
    } else if(stockAction === 'damaged'){
      toastMsg += ' Marked as damaged — stock not restored.';
    } else {
      toastMsg += ' Held for inspection.';
    }

    ui().closeModal();
    ui().toast(toastMsg, 'success', 6000);
    loadTracking();

    // Receiving stock back and refunding the customer are two separate
    // steps — nudge staff toward closing the loop rather than letting an
    // order sit here indefinitely with the customer never actually
    // refunded. Staggered later if the damage-claim prompt below is also
    // about to show, so the two don't collide.
    const { data: payStatus } = await authClient.from('orders').select('payment_status,total_value').eq('id', orderId).maybeSingle();
    if(payStatus?.payment_status !== 'refunded'){
      setTimeout(()=>{
        ui().openModal('Refund still pending', `
          <div class="ims__alert ims__alert--warning" style="margin-bottom:0">
            <i class="ti ti-alert-triangle"></i>
            <span><strong>${util().sanitize(orderNumber)}</strong> has been received back, but the customer hasn't been refunded yet. This order is flagged <strong>Refund pending</strong> in the Lost &amp; Damaged list until that's done.</span>
          </div>
        `, `
          <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Not now</button>
          <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal();setTimeout(()=>window.IMS_ORDERS?.processRefund('${util().jsArg(orderId)}',${payStatus?.total_value||0}),300)">
            <i class="ti ti-currency-pound"></i> Process refund now
          </button>
        `, 'sm');
      }, stockAction === 'damaged' ? 1400 : 800);
    }

    // If damaged stock action, prompt to raise claim
    if(stockAction === 'damaged'){
      setTimeout(()=>{
        ui().openModal('Raise damage claim?', `
          <div class="ims__alert ims__alert--warning" style="margin-bottom:0">
            <i class="ti ti-alert-triangle"></i>
            <span><strong>${util().sanitize(orderNumber)}</strong> has been received back damaged. Would you like to raise a courier claim for the damaged goods?</span>
          </div>
        `, `
          <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Not now</button>
          <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal();setTimeout(()=>window.IMS_LOGISTICS.openRaiseClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}'),300)">
            <i class="ti ti-file-alert"></i> Raise damage claim
          </button>
        `, 'sm');
      }, 800);
    }
  }

  // ─── MARK AS LOST ───────────────────────────────────────────────
  function markAsLost(orderId, orderNumber){
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Mark as lost — ${orderNumber}`, `
      <div class="ims__alert ims__alert--danger" style="margin-bottom:14px">
        <i class="ti ti-alert-triangle"></i>
        <span>Marking this order as lost will remove it from the tracking list and move it to the <strong>Lost &amp; Damaged</strong> section. Only use this when the courier has confirmed the parcel cannot be located.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Date confirmed lost</label>
          <input class="ims__input" type="date" id="ims-lost-date" value="${todayStr}" max="${todayStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Reason</label>
          <select class="ims__select" id="ims-lost-reason">
            <option value="">Select reason...</option>
            <option value="Courier confirmed lost">Courier confirmed lost</option>
            <option value="No delivery after multiple attempts — presumed lost">No delivery after multiple attempts — presumed lost</option>
            <option value="Tracking shows no movement — presumed lost">Tracking shows no movement — presumed lost</option>
            <option value="Customer reports not received — courier cannot locate">Customer reports not received — courier cannot locate</option>
            <option value="Damaged beyond use — total loss">Damaged beyond use — total loss</option>
          </select>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Additional notes</label>
          <textarea class="ims__textarea" id="ims-lost-notes" rows="2" placeholder="Any additional context..."></textarea>
        </div>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--danger" onclick="window.IMS_LOGISTICS.confirmMarkAsLost('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')">
        <i class="ti ti-alert-triangle"></i> Confirm — mark as lost
      </button>
    `, 'sm');
  }

  async function confirmMarkAsLost(orderId, orderNumber){
    const dateVal = document.getElementById('ims-lost-date')?.value;
    const reason = document.getElementById('ims-lost-reason')?.value;
    const notes  = document.getElementById('ims-lost-notes')?.value?.trim();
    if(!dateVal){ ui().toast('Please select the date confirmed lost','error'); return; }
    if(!reason){ ui().toast('Please select a reason','error'); return; }
    const fullNotes = notes ? `${reason} — ${notes}` : reason;
    const lostAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const authClient = window.supabase.createClient(
      window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY,
      {global:{headers:{Authorization:`Bearer ${window.SM.AUTH.session?.access_token}`}}}
    );

    const { data: ord } = await authClient.from('orders')
      .select('status').eq('id', orderId).single();

    const { error: lostErr } = await authClient.from('orders')
      .update({ status:'lost' }).eq('id', orderId);
    if(lostErr){ ui().toast('Failed to mark as lost: ' + lostErr.message, 'error'); return; }

    await authClient.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Marked as lost',
      notes:           fullNotes,
      created_at:      lostAt,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, ord?.status || 'failed_delivery', 'lost', 'marked_lost', fullNotes);

    ui().closeModal();
    loadTracking();

    setTimeout(()=>{
      ui().openModal('Raise courier claim?', `
        <div class="ims__alert ims__alert--info" style="margin-bottom:0">
          <i class="ti ti-info-circle"></i>
          <span><strong>${util().sanitize(orderNumber)}</strong> has been marked as lost. Would you like to raise a courier claim now?</span>
        </div>
      `, `
        <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Not now</button>
        <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal();setTimeout(()=>window.IMS_LOGISTICS.openRaiseClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}'),300)">
          <i class="ti ti-file-alert"></i> Raise courier claim
        </button>
      `, 'sm');
    }, 600);
  }


  // ─── COURIER FOLLOW-UP ──────────────────────────────────────────
  // Sends a courier-facing email (follow-up or claim) directly via the
  // send-order-email Edge Function. Unlike customer emails these are B2B and
  // can legitimately repeat (you may chase the same order many times), so
  // there is NO dedup — every send is logged as its own row. Claims CC the
  // company Gmail for a paper trail; routine follow-ups do not. Reply-to is
  // the company Gmail so courier replies land where staff already work.
  async function sendCourierEmail({ to, subject, body, cc, orderId, emailType, logNote }) {
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(to.trim())) {
      ui().toast('No valid courier email address — add one in Logistics → Couriers, or copy the email and send manually.', 'error', 7000);
      return { sent: false };
    }
    // Plain-text body → simple HTML (preserve line breaks), so it renders
    // correctly while still reading as a normal typed email.
    const htmlBody = `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#0F172A;white-space:pre-wrap">${util().sanitize(body)}</div>`;
    try {
      const res = await fetch(`${window.IMS_SUPABASE_URL}/functions/v1/send-order-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${window.IMS_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ to: to.trim(), subject, html: htmlBody, cc: cc || undefined })
      });
      const out = await res.json().catch(() => ({}));
      const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);

      if (!res.ok || !out.success) {
        const msg = out.error || `HTTP ${res.status}`;
        await client.from('email_log').insert({
          order_id: orderId, email_type: emailType, recipient: to.trim(),
          subject, status: 'failed', error_message: msg
        }).then(()=>{}, ()=>{});
        ui().toast(`Email failed to send: ${msg}`, 'error', 8000);
        return { sent: false, error: msg };
      }

      await client.from('email_log').insert({
        order_id: orderId, email_type: emailType, recipient: to.trim(),
        subject, status: 'sent', error_message: logNote || null
      }).then(()=>{}, ()=>{});
      return { sent: true };
    } catch (err) {
      ui().toast(`Email failed: ${String(err?.message || err)}`, 'error', 8000);
      return { sent: false, error: String(err) };
    }
  }

  async function openCourierFollowUp(orderId, orderNumber){
    const shipment = (window._shipmentMap || {})[orderId];
    const order    = (window._trackingOrders || []).find(o => o.id === orderId);
    const courier  = shipment?.couriers || null;
    const tracking = shipment?.tracking_number || 'N/A';
    const postcode = order?.postcode || order?.end_postcode || 'N/A';
    const custName = order?.customer_name || order?.end_customer_name || 'N/A';
    const dispDate = order?.dispatched_at ? util().formatDate(order.dispatched_at) : 'N/A';
    const daysSince= order?.dispatched_at ? Math.floor((new Date() - new Date(order.dispatched_at))/86400000) : '?';
    const compName = window.SM.AUTH.company?.name || 'MDF London Ltd';
    const csEmail  = courier?.cs_email || '';
    const courierName = courier?.name || 'Courier';

    // Status-aware wording, driven off the reliable orders.status field.
    // A re-attempted order is a different conversation from a still-in-transit
    // one, and phrasing it as such shows the courier we're tracking it
    // properly. The last known tracking note (if any) is quoted for
    // reference rather than used to build sentences, since it's manually
    // entered and often stale.
    const isReattempt = order?.status === 're_attempted';
    const lastNote = order?.last_tracking_note || '';

    const subject = isReattempt
      ? `Re-attempt update requested — ${orderNumber} — ${tracking}`
      : `Delivery update requested — ${orderNumber} — ${tracking}`;

    const opening = isReattempt
      ? `We previously reported a failed delivery on this shipment and a re-delivery was requested. It still has not shown a successful delivery, and we would like an update.`
      : `We are following up on the shipment below, dispatched on ${dispDate} (${daysSince} days ago), which has not yet shown delivery confirmation.`;

    const body =
`Dear ${courierName} Customer Services,

${opening}

Order Reference:   ${orderNumber}
Tracking Number:   ${tracking}
Dispatch Date:     ${dispDate}
Days in Transit:   ${daysSince} days
Delivery Postcode: ${postcode}
Customer Name:     ${custName}${lastNote ? `
Last known status: ${lastNote}` : ''}

Please could you provide an update on the current status and expected delivery date? If the parcel cannot be located, please advise on next steps for raising a formal claim.

Kind regards,
${compName}`;

    ui().openModal(`Follow up courier — ${orderNumber}`, `
      ${!csEmail ? `<div class="ims__alert ims__alert--warning" style="margin-bottom:12px"><i class="ti ti-alert-triangle"></i><span>No customer services email configured for ${util().sanitize(courierName)}. Go to Logistics → Couriers → Edit to add one. Template still available below.</span></div>` : ''}
      <div class="ims__form-grid" style="margin-bottom:12px">
        <div class="ims__form-group">
          <label class="ims__label">To (courier CS email)</label>
          <input class="ims__input" type="text" id="ims-fu-email" value="${util().sanitize(csEmail)}" placeholder="courier@example.com">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Subject</label>
          <input class="ims__input" type="text" id="ims-fu-subject" value="${util().sanitize(subject)}">
        </div>
      </div>
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label">Email body (copy and paste into your email client)</label>
        <textarea class="ims__textarea" id="ims-fu-body" rows="14" style="font-family:monospace;font-size:12px">${util().sanitize(body)}</textarea>
      </div>
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label">Internal note (logged to tracking history)</label>
        <input class="ims__input" type="text" id="ims-fu-note" placeholder="e.g. Chased DPD — awaiting response">
      </div>
      <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="
        const b=document.getElementById('ims-fu-body');
        const s=document.getElementById('ims-fu-subject');
        navigator.clipboard.writeText('To: '+document.getElementById('ims-fu-email').value+'\\nSubject: '+s.value+'\\n\\n'+b.value)
          .then(()=>window.SM.UI.toast('Email copied to clipboard','success'))
          .catch(()=>window.SM.UI.toast('Copy failed — please select and copy manually','warning'));
      "><i class="ti ti-copy"></i> Copy instead</button>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveFollowUp('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')">
        <i class="ti ti-send"></i> Send follow-up
      </button>
    `, 'lg');
  }

  async function saveFollowUp(orderId, orderNumber){
    const note    = document.getElementById('ims-fu-note')?.value?.trim() || '';
    const to      = document.getElementById('ims-fu-email')?.value?.trim() || '';
    const subject = document.getElementById('ims-fu-subject')?.value || '';
    const body    = document.getElementById('ims-fu-body')?.value || '';

    const result = await sendCourierEmail({
      to, subject, body, orderId,
      emailType: 'courier_followup',
      logNote: note || 'Courier chased for shipment update'
    });
    // If the send failed, keep the modal open so nothing is lost and the
    // user can fix the address or copy the email instead.
    if (!result.sent) return;

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Follow-up sent to courier',
      notes:           note || 'Courier chased for shipment update',
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, null, null, 'courier_followup', `Follow-up emailed to courier${note ? ' — ' + note : ''}`);
    ui().closeModal();
    ui().toast(`Follow-up sent to ${to}`, 'success');
    loadTracking();
  }

  // ─── RAISE COURIER CLAIM ────────────────────────────────────────
  async function openRaiseClaim(orderId, orderNumber, claimType='lost'){
    const client   = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    let shipment = (window._shipmentMap || {})[orderId] || (window._lostShipMap || {})[orderId];
    const order    = (window._lostOrders  || []).find(o => o.id === orderId)
                  || (window._trackingOrders || []).find(o => o.id === orderId);

    // Fetch full order if not in cache
    let orderData = order;
    if(!orderData){
      const { data } = await db().select('orders',{
        select:'id,order_number,external_ref,status,customer_name,end_customer_name,postcode,end_postcode,total_value,dispatched_at,order_type,platforms(name)',
        eq:{id:orderId}, maybeSingle:true, noCompanyFilter:true
      });
      orderData = data;
    }

    // Fetch shipment fresh if it's in neither cache — this happens right
    // after an order is marked lost, since the main tracking query that
    // builds window._shipmentMap explicitly excludes 'lost' orders, and
    // window._lostShipMap may not have been populated yet either (e.g.
    // when raising a claim immediately from the "mark as lost" prompt,
    // before Lost Orders has fully re-rendered).
    if(!shipment){
      const { data: freshShipment } = await client
        .from('shipments')
        .select('order_id,tracking_number,courier_id,couriers(name,claims_email,cs_email,tracking_url_base,requires_postcode,postcode_param,tracking_form_only)').eq('is_return_collection',false)
        .eq('order_id', orderId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      shipment = freshShipment || null;
    }

    const courier      = shipment?.couriers || null;
    const courierId    = shipment?.courier_id || null;
    const courierName  = courier?.name || 'Unknown courier';
    const claimsEmail  = courier?.claims_email || '';
    const tracking     = shipment?.tracking_number || 'N/A';
    const postcode     = orderData?.postcode || orderData?.end_postcode || 'N/A';
    const custName     = orderData?.customer_name || orderData?.end_customer_name || 'N/A';
    const dispDate     = orderData?.dispatched_at ? util().formatDate(orderData.dispatched_at) : 'N/A';
    // A damage claim is very often for less than the full order value —
    // only some items/boxes arrived damaged — so it still defaults to the
    // full total as a sensible starting point, but staff can edit this
    // down to the actual damaged portion before sending.
    const claimAmt     = orderData?.total_value || 0;
    const compName     = window.SM.AUTH.company?.name || 'MDF London Ltd';
    // Marketplace's own order reference (e.g. the Temu/Amazon order ID).
    // Couriers and marketplaces both key off this, so it belongs in the
    // claim email — our internal ORD-xxxxx number means nothing to them.
    const extRef       = orderData?.external_ref || '';

    // Generate claim ref
    // Order by claim_ref itself (not created_at, which does not exist on
    // this table - that silently returned nothing and reset every claim to
    // CLM-00001). Since refs are zero-padded to 5 digits, string-descending
    // order matches numeric order and reliably finds the highest existing
    // number to increment from.
    const { data: lastClaim } = await client.from('claims')
      .select('claim_ref').not('claim_ref','is',null)
      .order('claim_ref', { ascending: false }).limit(1).maybeSingle();
    const claimRef = util().generateRef('CLM', lastClaim?.claim_ref);

    const { subject, body } = buildClaimEmail(claimType, {
      courierName, orderNumber, extRef, claimRef, tracking, dispDate, postcode, custName, claimAmt, compName
    });

    ui().openModal(`Raise courier claim — ${orderNumber}`, `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px">
        <div><span class="ims__label">Claim reference</span><div style="font-weight:700;font-size:15px;color:var(--ims-accent)">${claimRef}</div></div>
        <div><span class="ims__label">Courier</span><div>${util().sanitize(courierName)}</div></div>
        <div><span class="ims__label">Order</span><div>${util().sanitize(orderNumber)}</div></div>
        <div><span class="ims__label">Tracking #</span><div style="font-family:monospace">${util().sanitize(tracking)}</div></div>
        <div><span class="ims__label">Marketplace ref</span><div style="font-family:monospace">${extRef ? util().sanitize(extRef) : '<span style="color:var(--ims-text-muted);font-family:inherit">Not set</span>'}</div></div>
      </div>
      <div class="ims__form-grid" style="margin-bottom:12px">
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Claim type</label>
          <select class="ims__select" id="ims-clm-type" onchange="window.IMS_LOGISTICS.syncClaimType('${util().jsArg(orderNumber)}','${util().jsArg(courierName)}','${util().jsArg(extRef)}','${util().jsArg(claimRef)}','${util().jsArg(tracking)}','${util().jsArg(dispDate)}','${util().jsArg(postcode)}','${util().jsArg(custName)}','${util().jsArg(compName)}')">
            <option value="lost" ${claimType==='lost'?'selected':''}>Lost / undelivered</option>
            <option value="damaged" ${claimType==='damaged'?'selected':''}>Damaged</option>
          </select>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Amount claimed (£)</label>
          <input class="ims__input" type="number" step="0.01" id="ims-clm-amount" value="${claimAmt.toFixed(2)}" oninput="window.IMS_LOGISTICS.syncClaimAmount()">
          ${claimType==='damaged'?'<span class="ims__input-hint">Defaults to the full order value — reduce this if only part of the order was affected</span>':''}
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Claims email</label>
          <input class="ims__input" type="text" id="ims-clm-email" value="${util().sanitize(claimsEmail)}" placeholder="claims@courier.com">
          ${!claimsEmail ? '<span class="ims__input-hint" style="color:var(--ims-warning)">No claims email set — configure in Couriers tab</span>' : ''}
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Subject</label>
          <input class="ims__input" type="text" id="ims-clm-subject" value="${util().sanitize(subject)}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Internal description</label>
          <input class="ims__input" type="text" id="ims-clm-desc" value="${claimType==='damaged'?'Damaged':'Lost'} shipment — ${util().sanitize(orderNumber)} — ${util().sanitize(courierName)}">
        </div>
      </div>
      <div class="ims__form-group" style="margin-bottom:10px">
        <label class="ims__label">Email body (copy and paste into your email client)</label>
        <textarea class="ims__textarea" id="ims-clm-body" rows="16" style="font-family:monospace;font-size:12px">${util().sanitize(body)}</textarea>
      </div>
      <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="
        const to=document.getElementById('ims-clm-email').value;
        const sub=document.getElementById('ims-clm-subject').value;
        const bod=document.getElementById('ims-clm-body').value;
        navigator.clipboard.writeText('To: '+to+'\\nSubject: '+sub+'\\n\\n'+bod)
          .then(()=>window.SM.UI.toast('Claim email copied to clipboard','success'))
          .catch(()=>window.SM.UI.toast('Copy failed — select and copy manually','warning'));
      "><i class="ti ti-copy"></i> Copy instead</button>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel — raise later</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}','${util().jsArg(claimRef)}','${util().jsArg(courierId||'')}')">
        <i class="ti ti-send"></i> Send &amp; save claim — ${claimRef}
      </button>
    `, 'xl');
  }

  // Shared by openRaiseClaim (initial render) and syncClaimType (dropdown
  // change) so the two can never drift out of sync with each other.
  function buildClaimEmail(claimType, d){
    const subject = claimType==='damaged'
      ? `Damage Claim - ${d.tracking} - ${d.orderNumber} - ${d.claimRef}`
      : `Formal Courier Claim - ${d.tracking} - ${d.orderNumber} - ${d.claimRef}`;
    const openingLine = claimType==='damaged'
      ? 'We are writing to raise a claim regarding a shipment that was returned to us in a damaged condition, as detailed below.'
      : 'We are formally raising a claim for a lost/undelivered shipment as detailed below.';
    const closingLine = claimType==='damaged'
      ? 'The parcel was returned to us with visible damage to the contents. We request formal investigation and compensation in accordance with your claims policy.'
      : 'Despite our follow-up communications, this shipment has not been delivered and cannot be located. We request formal investigation and compensation in accordance with your claims policy.';
    const body =
`Dear ${d.courierName} Claims Department,

${openingLine}

Claim Reference:   ${d.claimRef}
Order Reference:   ${d.orderNumber}${d.extRef ? `
Marketplace Ref:   ${d.extRef}` : ''}
Tracking Number:   ${d.tracking}
Dispatch Date:     ${d.dispDate}
Delivery Postcode: ${d.postcode}
Customer Name:     ${d.custName}
Amount Claimed:    £${d.claimAmt.toFixed(2)}

${closingLine}

Please acknowledge receipt of this claim and advise on the next steps and timeline.

Kind regards,
${d.compName}`;
    return { subject, body };
  }

  // Regenerates subject + body when the claim type dropdown is switched,
  // preserving whatever amount is currently in the form.
  function syncClaimType(orderNumber, courierName, extRef, claimRef, tracking, dispDate, postcode, custName, compName){
    const claimType = document.getElementById('ims-clm-type')?.value || 'lost';
    const claimAmt = parseFloat(document.getElementById('ims-clm-amount')?.value) || 0;
    const { subject, body } = buildClaimEmail(claimType, {
      courierName, orderNumber, extRef, claimRef, tracking, dispDate, postcode, custName, claimAmt, compName
    });
    const subjectEl = document.getElementById('ims-clm-subject');
    const bodyEl = document.getElementById('ims-clm-body');
    const descEl = document.getElementById('ims-clm-desc');
    if(subjectEl) subjectEl.value = subject;
    if(bodyEl) bodyEl.value = body;
    if(descEl) descEl.value = `${claimType==='damaged'?'Damaged':'Lost'} shipment — ${orderNumber} — ${courierName}`;
  }

  // Keeps the "Amount Claimed: £X.XX" line in the email body in sync with
  // the amount field as it's edited, so what gets sent always matches.
  function syncClaimAmount(){
    const amt = parseFloat(document.getElementById('ims-clm-amount')?.value) || 0;
    const bodyEl = document.getElementById('ims-clm-body');
    if (!bodyEl) return;
    bodyEl.value = bodyEl.value.replace(/Amount Claimed:\s*£[\d.,]+/, `Amount Claimed:    £${amt.toFixed(2)}`);
  }

  async function saveClaim(orderId, orderNumber, claimRef, courierId){
    const amount    = parseFloat(document.getElementById('ims-clm-amount')?.value) || 0;
    const claimType = document.getElementById('ims-clm-type')?.value || 'lost';
    const desc      = document.getElementById('ims-clm-desc')?.value?.trim() || '';
    const to        = document.getElementById('ims-clm-email')?.value?.trim() || '';
    const subject   = document.getElementById('ims-clm-subject')?.value || '';
    const body      = document.getElementById('ims-clm-body')?.value || '';

    // Send first — if the courier email can't go, don't record the claim as
    // raised, so "raised" always means the courier was actually notified.
    // Claims CC the company Gmail for a paper trail.
    const result = await sendCourierEmail({
      to, subject, body,
      cc: window.SM.AUTH.company?.email || 'salesmdflondon@gmail.com',
      orderId, emailType: 'courier_claim',
      logNote: `${claimRef} — £${amount.toFixed(2)}`
    });
    if (!result.sent) return;

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);

    const { error } = await client.from('claims').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      courier_id:      courierId || null,
      claim_reference: claimRef,
      claim_ref:       claimRef,
      claim_type:      claimType,
      items_value:     amount,
      amount_claimed:  amount,
      description:     desc,
      status:          'raised',
      raised_at:       new Date().toISOString()
    });

    if(error){ ui().toast('Failed to save claim: ' + error.message, 'error'); return; }

    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Claim raised',
      notes:           `${claimRef} — £${amount.toFixed(2)} claimed`,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    // A damaged-return claim's order sits at return_received (goods really
    // are back in the warehouse, just unsellable), not lost — the old
    // hardcoded 'lost'→'lost' label here was misleading for that case even
    // though it never actually touched the real order status (logActivity
    // only writes to the audit log).
    const fromStatus = claimType === 'damaged' ? 'return_received' : 'lost';
    await logActivity(orderId, fromStatus, fromStatus, claimType === 'damaged' ? 'damage_claim_raised' : 'claim_raised', `${claimRef} — £${amount.toFixed(2)}`);

    ui().closeModal();
    ui().toast(`Claim ${claimRef} sent to courier & raised — visible in Accounts → Claims`, 'success', 5000);
    loadLostOrders();
  }

  // ─── LOST ORDERS SECTION ────────────────────────────────────────
  // ─── COURIER-ISSUED CLAIM REFERENCE ──────────────────────────────
  // Couriers issue their own claim reference only after they acknowledge
  // the claim, so this can't be captured at raise time — it's entered
  // afterwards from the Lost Orders row and stored alongside our own
  // internal CLM-xxxxx reference, not instead of it.
  function openCourierClaimRef(claimId, internalRef, currentRef){
    ui().openModal(`Courier claim reference — ${internalRef}`, `
      <div class="ims__alert ims__alert--info" style="margin-bottom:12px">
        <i class="ti ti-info-circle"></i>
        <span>Enter the reference number the courier gave you when they acknowledged this claim. Your internal reference <strong>${util().sanitize(internalRef)}</strong> is kept as-is.</span>
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Courier claim reference</label>
        <input class="ims__input" type="text" id="ims-ccr-value" value="${util().sanitize(currentRef || '')}" placeholder="e.g. XDP-CLM-88213" style="font-family:monospace">
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveCourierClaimRef('${util().jsArg(claimId)}')"><i class="ti ti-check"></i> Save reference</button>
    `, 'sm');
  }

  async function saveCourierClaimRef(claimId){
    const val = document.getElementById('ims-ccr-value')?.value?.trim() || '';
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { error } = await client.from('claims')
      .update({ courier_claim_ref: val || null })
      .eq('id', claimId);
    if(error){ ui().toast('Failed to save: ' + error.message, 'error'); return; }
    ui().closeModal();
    ui().toast(val ? 'Courier claim reference saved' : 'Courier claim reference cleared', 'success');
    loadLostOrders();
  }

  // Claim statuses that mean the case is finished. 'recovered' is set by
  // "Parcel found" (saleable); 'written_off' by "Close without claim".
  const CLOSED_CLAIM_STATUSES = ['settled', 'rejected', 'recovered', 'written_off'];

  // Finished cases leave Lost Orders but stay available here: the full
  // claims history in Accounts > Claims (and Reports > Courier claims log).
  function viewClosedClaims(){
    window.SM.UI.showPanel('accounts');
    setTimeout(() => window.IMS_ACCOUNTS?.switchTab?.('claims'), 300);
  }

  async function loadLostOrders(){
    const wrap = document.getElementById('ims-lost-orders-wrap');
    if(!wrap) return;

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const companyId = window.SM.AUTH.company?.id;

    // Pull lost orders + return_received (damaged returns back at warehouse awaiting claim)
    // return_in_transit stays in main tracking table
    const { data: lostOrders } = await client
      .from('orders')
      .select('id,order_number,external_ref,status,fulfillment_type,order_type,customer_name,end_customer_name,postcode,end_postcode,total_value,payment_status,dispatched_at,platforms(name),created_at')
      .eq('company_id', companyId)
      .in('status', ['lost','return_received'])
      .order('dispatched_at', { ascending: false });

    // Also pull orders with a valid claim that may have moved to other statuses
    const { data: claimedRows } = await client
      .from('claims')
      .select('order_id')
      .eq('company_id', companyId)
      .not('claim_ref','is',null);

    const claimedIds  = (claimedRows||[]).map(c=>c.order_id).filter(Boolean);
    const existingIds = (lostOrders||[]).map(o=>o.id);
    const extraIds    = claimedIds.filter(id=>!existingIds.includes(id));

    let extraOrders = [];
    if(extraIds.length){
      const {data:extras} = await client.from('orders')
        .select('id,order_number,external_ref,status,fulfillment_type,order_type,customer_name,end_customer_name,postcode,end_postcode,total_value,payment_status,dispatched_at,platforms(name),created_at')
        .in('id', extraIds);
      extraOrders = extras || [];
    }

    const allItems = [...(lostOrders||[]), ...extraOrders];
    const items = allItems;
    window._lostOrders = items;

    if(!items.length){
      wrap.innerHTML = `<div class="ims__empty" style="padding:30px"><i class="ti ti-circle-check"></i><p style="font-size:13px">No lost or damaged orders</p></div>`;
      return;
    }

    const orderIds = items.map(o => o.id);

    // Shipments for lost orders
    const { data: shipments } = await client
      .from('shipments')
      .select('order_id,tracking_number,courier_id,couriers(name,claims_email,cs_email,tracking_url_base,requires_postcode,postcode_param,tracking_form_only)').eq('is_return_collection',false)
      .in('order_id', orderIds)
      .order('created_at', { ascending: false });

    const lostShipMap = {};
    (shipments || []).forEach(s => { if(!lostShipMap[s.order_id]) lostShipMap[s.order_id] = s; });
    window._lostShipMap = lostShipMap;

    // Claims for lost orders
    const { data: claims } = await client
      .from('claims')
      .select('id,order_id,claim_ref,courier_claim_ref,claim_type,status,amount_claimed,amount_settled,raised_at,settled_at,rejected_at')
      .in('order_id', orderIds)
      .order('raised_at', { ascending: false });

    const claimMap = {};
    (claims || []).forEach(c => { if(!claimMap[c.order_id]) claimMap[c.order_id] = c; });

    // Latest tracking event per lost order
    const { data: events } = await client
      .from('order_tracking_events')
      .select('order_id,tracking_status,created_at')
      .in('order_id', orderIds)
      .order('created_at', { ascending: false });

    const lostLatestEvent = {};
    (events || []).forEach(e => { if(!lostLatestEvent[e.order_id]) lostLatestEvent[e.order_id] = e; });

    // Only cases that still need action stay on this list: goods back with
    // a refund still pending, a claim still open, or a lost order with no
    // claim yet. Finished cases (claim settled, rejected, parcel found, or
    // closed without a claim) leave the list and stay available in
    // Accounts > Claims and Reports > Courier claims log.
    const isOpenCase = o => {
      const c = claimMap[o.id];
      if (o.status === 'return_received' && o.payment_status !== 'refunded') return true;
      if (c) return !CLOSED_CLAIM_STATUSES.includes(c.status);
      return o.status === 'lost';
    };
    const openItems = items.filter(isOpenCase);
    const historyLink = `<span class="ims__link" style="font-size:12px" onclick="window.IMS_LOGISTICS.viewClosedClaims()"><i class="ti ti-archive"></i> View closed claims</span>`;
    if (!openItems.length) {
      wrap.innerHTML = `<div class="ims__empty" style="padding:30px"><i class="ti ti-circle-check"></i><p style="font-size:13px">No open lost or damaged cases</p><p style="margin-top:6px">${historyLink}</p></div>`;
      return;
    }

    const rows = openItems.map(o => {
      const shipment   = lostShipMap[o.id];
      const claim      = claimMap[o.id];
      const courier    = shipment?.couriers || null;
      const courierName= courier?.name || '—';
      const tracking   = shipment?.tracking_number || '—';
      const postcode   = o.postcode || o.end_postcode || '—';
      const custName   = util().sanitize(o.customer_name || o.end_customer_name || '—');
      const lostDate   = util().formatDate(o.dispatched_at || o.created_at);
      const daysSince  = o.dispatched_at ? Math.floor((new Date() - new Date(o.dispatched_at))/86400000) : '?';
      const latestEvt  = lostLatestEvent[o.id];

      // Claim status badge
      let claimBadge = '<span class="ims__pill ims__pill--neutral" style="font-size:10px">No claim</span>';
      let claimRef   = '—';
      if(claim){
        // Show our internal CLM-xxxxx on top and the courier's own claim
        // reference beneath it once they've issued one — both are needed:
        // theirs for chasing the courier, ours for internal records.
        const internalRef = util().sanitize(claim.claim_ref || '—');
        claimRef = claim.courier_claim_ref
          ? `${internalRef}<div style="font-size:10px;font-weight:500;color:var(--ims-text-sec);font-family:monospace;margin-top:2px" title="Courier claim reference">${util().sanitize(claim.courier_claim_ref)}</div>`
          : `${internalRef}<div style="font-size:10px;font-weight:400;color:var(--ims-text-muted);margin-top:2px">No courier ref</div>`;
        const claimColors = {
          raised:'warning', acknowledged:'info', escalated_14:'danger',
          overdue_30:'danger', settled:'success', rejected:'danger',
          recovered:'success', written_off:'neutral'
        };
        const claimLabels = {
          raised:'Raised', acknowledged:'Acknowledged', escalated_14:'Escalated',
          overdue_30:'Overdue', settled:'Settled', rejected:'Rejected',
          recovered:'Parcel found', written_off:'Written off'
        };
        claimBadge = `<span class="ims__pill ims__pill--${claimColors[claim.status]||'neutral'}" style="font-size:10px">${claimLabels[claim.status]||claim.status}</span>`;
        if(claim.claim_type === 'damaged'){
          claimBadge += ` <span class="ims__pill ims__pill--accent" style="font-size:9px" title="Claim for damaged goods, not a total loss">Damaged</span>`;
        }
        if(claim.status === 'settled' && claim.amount_settled){
          claimBadge += `<div style="font-size:10px;color:var(--ims-success);margin-top:2px">£${claim.amount_settled.toFixed(2)} settled</div>`;
        }
        if(claim.status === 'rejected'){
          claimBadge += `<div style="font-size:10px;color:var(--ims-danger);margin-top:2px">Written off as expense</div>`;
        }
      }

      // Latest status line
      const latestLine = latestEvt
        ? `<div style="font-size:10px;color:var(--ims-text-sec);margin-top:2px;font-style:italic">${util().sanitize(latestEvt.tracking_status)} — ${util().formatDate(latestEvt.created_at)}</div>`
        : '';

      // Receiving stock back and refunding the customer are two separate
      // steps (logistics vs accounts) with nothing previously connecting
      // them — an order could sit at return_received indefinitely with
      // the customer never actually refunded, and nothing surfaced that.
      const refundPending = o.status === 'return_received' && o.payment_status !== 'refunded';
      const refundBadge = refundPending
        ? `<div style="margin-top:3px"><span class="ims__pill ims__pill--danger" style="font-size:9px" title="Goods are back but the customer has not been refunded yet">Refund pending</span></div>`
        : '';

      // Action buttons
      const canRaiseClaim   = !claim;
      // Some orders land here still mid-transit rather than genuinely
      // lost or already received back — give a direct way to close that
      // out here too, instead of forcing a hunt through Tracking (which
      // may not even show the order if its logistics_jobs entry is in an
      // unusual state).
      const canMarkReceived = o.status !== 'return_received' && o.status !== 'lost';
      // Claim actions only while the claim is still open. "Parcel found"
      // (saleable) closes a claim too, so Settle, Reject and Parcel found
      // must not show after it. Before, they still did, which is how
      // CLM-00002 was settled on top of a "Parcel found".
      const claimOpen       = !!claim && !CLOSED_CLAIM_STATUSES.includes(claim.status);
      const canSettle       = claimOpen;
      const canReject       = claimOpen;
      // A lost order that will never be claimed (for example lost on our
      // own van) can be closed and written off. Admin only: posts an expense.
      const canCloseNoClaim = !claim && o.status === 'lost' && window.SM.AUTH.isAdmin();
      const canAddCourierRef = !!claim;
      // Reminders only make sense while a claim is genuinely outstanding.
      const canChaseClaim    = claimOpen;
      const claimDaysOut     = claim?.raised_at
        ? Math.floor((Date.now() - new Date(claim.raised_at).getTime()) / 86400000) : 0;

      const actions = `<div class="ims__btn-group" style="flex-wrap:nowrap">
        <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openTrackingHistory('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="View full activity log"><i class="ti ti-history"></i></button>
        <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.addLostOrderNote('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Add manual note"><i class="ti ti-pencil"></i></button>
        ${canMarkReceived ? `<button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_LOGISTICS.markReturnReceived('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Mark as received back at the warehouse"><i class="ti ti-package-import"></i> Return received</button>` : ''}
        ${refundPending ? `<button class="ims__btn ims__btn--danger ims__btn--sm" onclick="window.IMS_ORDERS?.processRefund('${util().jsArg(o.id)}',${o.total_value||0})" title="Customer has not been refunded yet"><i class="ti ti-currency-pound"></i> Process refund</button>` : ''}
        ${canRaiseClaim ? `<button class="ims__btn ims__btn--warning ims__btn--sm" onclick="window.IMS_LOGISTICS.openRaiseClaim('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')" title="Raise courier claim"><i class="ti ti-file-alert"></i> Raise claim</button>` : ''}
        ${canCloseNoClaim ? `<button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.openCloseNoClaim('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}',${o.total_value||0})" title="No claim will be made: close this case and write it off"><i class="ti ti-archive"></i> Close without claim</button>` : ''}
        ${canAddCourierRef ? `<button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openCourierClaimRef('${util().jsArg(claim.id)}','${util().jsArg(claim.claim_ref||'')}','${util().jsArg(claim.courier_claim_ref||'')}')" title="${claim.courier_claim_ref ? 'Edit' : 'Add'} courier claim reference"><i class="ti ti-hash"></i></button>` : ''}
        ${canChaseClaim ? `<button class="ims__btn ${claimDaysOut >= 14 ? 'ims__btn--warning' : 'ims__btn--secondary'} ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openClaimFollowUp('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}','${util().jsArg(claim.id)}')" title="Send claim reminder to courier${claimDaysOut ? ' (' + claimDaysOut + ' days outstanding)' : ''}"><i class="ti ti-bell-ringing"></i></button>` : ''}
        ${canSettle     ? `<button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_LOGISTICS.openSettleClaim('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}','${util().jsArg(claim.id)}','${util().jsArg(claim.claim_ref)}',${claim.amount_claimed})" title="Settle claim"><i class="ti ti-cash"></i> Settle</button>` : ''}
        ${canSettle     ? `<button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.openParcelFound('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}','${util().jsArg(claim.id)}','${util().jsArg(claim.claim_ref)}')" title="Parcel found and returned - close claim with no charge"><i class="ti ti-package"></i> Parcel found</button>` : ''}
        ${canReject     ? `<button class="ims__btn ims__btn--danger ims__btn--sm" onclick="window.IMS_LOGISTICS.openRejectClaim('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}','${util().jsArg(claim.id)}','${util().jsArg(claim.claim_ref)}',${claim.amount_claimed})" title="Reject claim"><i class="ti ti-x"></i> Reject</button>` : ''}
      </div>`;

      return `<tr>
        <td><span class="ims__link" onclick="window.IMS_ORDERS?.openOrder('${util().jsArg(o.id)}')">${util().sanitize(o.order_number)}</span>${refundBadge}</td>
        <td style="font-family:monospace;font-size:11px;color:var(--ims-text-sec)">${o.external_ref ? util().sanitize(o.external_ref) : '—'}</td>
        <td><span class="ims__pill ims__pill--info" style="font-size:10px">${util().sanitize(o.platforms?.name||'—')}</span></td>
        <td>${custName}</td>
        <td style="font-size:12px">${util().sanitize(courierName)}</td>
        <td style="font-family:monospace;font-size:12px">${util().sanitize(tracking)}</td>
        <td style="font-size:11px">${lostDate}</td>
        <td style="font-size:12px;font-weight:600;color:var(--ims-accent)">${claimRef}</td>
        <td>${claimBadge}</td>
        <td style="font-size:12px;color:var(--ims-warning);font-weight:600">${daysSince}d${latestLine}</td>
        <td>${actions}</td>
      </tr>`;
    }).join('');

    wrap.innerHTML = `
      <div class="ims__card" style="margin-top:20px">
        <div class="ims__card-header">
          <div class="ims__card-title"><i class="ti ti-alert-triangle" style="color:var(--ims-danger)"></i>Lost Orders (${openItems.length})</div>
          <span style="font-size:12px;color:var(--ims-text-muted)">Open cases only. Finished cases: ${historyLink}</span>
        </div>
        <div class="ims__table-wrap">
          <table class="ims__table">
            <thead><tr>
              <th>Order #</th><th>Marketplace ref</th><th>Platform</th><th>Customer</th><th>Courier</th>
              <th>Tracking #</th><th>Lost on</th><th>Claim #</th>
              <th>Claim status</th><th>Days</th><th>Actions</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>`;
  }

  // ─── ADD MANUAL NOTE TO LOST ORDER ──────────────────────────────
  // ─── CLAIM FOLLOW-UP / REMINDER ─────────────────────────────────
  // Chases a courier on an outstanding claim. Unlike the initial claim,
  // reminders can legitimately be sent repeatedly, so there is no dedup -
  // each chase is its own logged event. Days-outstanding is computed from
  // the claim's raised_at so the courier sees exactly how long it has sat.
  async function openClaimFollowUp(orderId, orderNumber, claimId){
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: claim } = await client.from('claims')
      .select('id,claim_ref,courier_claim_ref,amount_claimed,raised_at,status')
      .eq('id', claimId).maybeSingle();
    if(!claim){ ui().toast('Claim not found','error'); return; }

    const ship        = (window._lostShipMap || {})[orderId];
    const courier     = ship?.couriers;
    const courierName = courier?.name || 'Courier';
    const claimsEmail = courier?.claims_email || '';
    const tracking    = ship?.tracking_number || 'N/A';
    const compName    = window.SM.AUTH.company?.name || 'MDF London Ltd';
    const raisedDate  = claim.raised_at ? util().formatDate(claim.raised_at) : 'N/A';
    const daysOut     = claim.raised_at
      ? Math.floor((Date.now() - new Date(claim.raised_at).getTime()) / 86400000)
      : 0;
    const amount      = parseFloat(claim.amount_claimed || 0);

    const subject = `Claim reminder - ${claim.claim_ref}${claim.courier_claim_ref ? ' / ' + claim.courier_claim_ref : ''} - ${orderNumber}`;

    const body =
`Dear ${courierName} Claims Department,

We are following up on the claim below, which was raised ${daysOut} day${daysOut===1?'':'s'} ago and remains unresolved. We have not yet received an update or settlement decision.

Our Claim Reference:    ${claim.claim_ref}${claim.courier_claim_ref ? `
Your Claim Reference:   ${claim.courier_claim_ref}` : ''}
Order Reference:        ${orderNumber}
Tracking Number:        ${tracking}
Date Claim Raised:      ${raisedDate}
Days Outstanding:       ${daysOut}
Amount Claimed:         ${util().formatCurrency(amount)}

Please provide an update on the current status of this claim, including any reference number, the stage of investigation, and an expected resolution date.

If any further information or documentation is required from us to progress this claim, please let us know and we will supply it promptly.

Kind regards,
${compName}`;

    ui().openModal(`Claim reminder - ${util().sanitize(claim.claim_ref)}`, `
      <div class="ims__alert ${daysOut >= 14 ? 'ims__alert--warning' : 'ims__alert--info'}" style="margin-bottom:12px">
        <i class="ti ti-clock-exclamation"></i>
        <span>This claim has been outstanding for <strong>${daysOut} day${daysOut===1?'':'s'}</strong>${claim.courier_claim_ref ? '' : ' and has <strong>no courier reference</strong> recorded yet'}.</span>
      </div>
      <div class="ims__form-grid" style="margin-bottom:12px">
        <div class="ims__form-group">
          <label class="ims__label">Send to</label>
          <input class="ims__input" type="email" id="ims-cfu-email" value="${util().sanitize(claimsEmail)}" placeholder="Courier claims email">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Subject</label>
          <input class="ims__input" type="text" id="ims-cfu-subject" value="${util().sanitize(subject)}">
        </div>
      </div>
      <div class="ims__form-group" style="margin-bottom:12px">
        <label class="ims__label">Email body (edit as needed before sending)</label>
        <textarea class="ims__textarea" id="ims-cfu-body" rows="14" style="font-family:monospace;font-size:12px">${util().sanitize(body)}</textarea>
      </div>
      <div class="ims__form-group">
        <label class="ims__label">Internal note for the tracking log</label>
        <input class="ims__input" type="text" id="ims-cfu-note" placeholder="e.g. 3rd chaser sent - escalate to account manager if no reply by Friday">
        <span class="ims__input-hint">Recorded against this order's tracking history alongside the reminder.</span>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="
        const to=document.getElementById('ims-cfu-email').value;
        const s=document.getElementById('ims-cfu-subject').value;
        const b=document.getElementById('ims-cfu-body').value;
        navigator.clipboard.writeText('To: '+to+'\\nSubject: '+s+'\\n\\n'+b)
          .then(()=>window.SM.UI.toast('Copied','success'))
          .catch(()=>window.SM.UI.toast('Copy failed','warning'));
      "><i class="ti ti-copy"></i> Copy instead</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveClaimFollowUp('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}','${util().jsArg(claimId)}','${util().jsArg(claim.claim_ref)}',${daysOut})">
        <i class="ti ti-send"></i> Send reminder
      </button>
    `, 'lg');
  }

  async function saveClaimFollowUp(orderId, orderNumber, claimId, claimRef, daysOut){
    const to      = document.getElementById('ims-cfu-email')?.value?.trim() || '';
    const subject = document.getElementById('ims-cfu-subject')?.value || '';
    const body    = document.getElementById('ims-cfu-body')?.value || '';
    const note    = document.getElementById('ims-cfu-note')?.value?.trim() || '';

    const result = await sendCourierEmail({
      to, subject, body,
      cc: window.SM.AUTH.company?.email || 'salesmdflondon@gmail.com',
      orderId,
      emailType: 'courier_claim_reminder',
      logNote: `${claimRef} - reminder after ${daysOut} days${note ? ' - ' + note : ''}`
    });
    if(!result.sent) return;

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Claim reminder sent to courier',
      notes:           `${claimRef} - chased after ${daysOut} day${daysOut===1?'':'s'}${note ? '. ' + note : ''}`,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, null, null, 'claim_reminder', `Claim reminder sent for ${claimRef} (${daysOut} days outstanding)${note ? ' - ' + note : ''}`);

    ui().closeModal();
    ui().toast(`Reminder sent to ${to} and logged`, 'success', 5000);
    loadLostOrders();
  }

  function addLostOrderNote(orderId, orderNumber){
    ui().openModal(`Add note — ${orderNumber}`, `
      <div class="ims__form-group">
        <label class="ims__label ims__label--req">Note</label>
        <textarea class="ims__textarea" id="ims-lostnote-text" rows="4" placeholder="e.g. Called courier 28/06 — advised 5 working days for investigation..."></textarea>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveLostOrderNote('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')">
        <i class="ti ti-check"></i> Save note
      </button>
    `, 'sm');
  }

  async function saveLostOrderNote(orderId, orderNumber){
    const note = document.getElementById('ims-lostnote-text')?.value?.trim();
    if(!note){ ui().toast('Please enter a note','error'); return; }
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Manual note',
      notes:           note,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    ui().closeModal();
    ui().toast('Note saved to tracking history', 'success');
    loadLostOrders();
  }

  // ─── SETTLE CLAIM ───────────────────────────────────────────────
  function openSettleClaim(orderId, orderNumber, claimId, claimRef, amountClaimed){
    ui().openModal(`Settle claim — ${claimRef}`, `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>Settlement amount will be recorded as <strong>income</strong> under "Courier claim settlement" in Accounts → Transactions.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Amount settled (£)</label>
          <input class="ims__input" type="number" step="0.01" id="ims-settle-amount" value="${parseFloat(amountClaimed).toFixed(2)}">
          <span class="ims__input-hint">Amount claimed: £${parseFloat(amountClaimed).toFixed(2)}</span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Settlement date</label>
          <input class="ims__input" type="date" id="ims-settle-date" value="${new Date().toISOString().split('T')[0]}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Courier payment reference</label>
          <input class="ims__input" type="text" id="ims-settle-ref" placeholder="e.g. CLM-DPD-2026-4471">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Notes</label>
          <input class="ims__input" type="text" id="ims-settle-notes" placeholder="e.g. Full settlement received via BACS">
        </div>
      </div>
      <div class="ims__alert ims__alert--warning" style="margin-top:4px">
        <i class="ti ti-alert-triangle"></i>
        <span>If this order had an open <strong>accounts receivable</strong>, please write it off manually in <strong>Accounts → Receivables</strong> after settling.</span>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success" onclick="window.IMS_LOGISTICS.confirmSettleClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}','${util().jsArg(claimId)}','${util().jsArg(claimRef)}')">
        <i class="ti ti-cash"></i> Confirm settlement
      </button>
    `, 'sm');
  }

  // Closes a claim, but only while it is still open: the update matches
  // nothing once the claim is settled, rejected, recovered or written off.
  // So two clicks, or the same claim closed from Accounts and from Lost
  // Orders at once, cannot both go through (no double income or double
  // write-off). Returns { ok: true }, { already: true } or { error }.
  async function _closeClaim(claimId, fields){
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data, error } = await client.from('claims')
      .update(fields)
      .eq('id', claimId)
      .not('status', 'in', `(${CLOSED_CLAIM_STATUSES.join(',')})`)
      .select('id');
    if (error) return { error };
    if (!data?.length) return { already: true };
    return { ok: true };
  }

  async function confirmSettleClaim(orderId, orderNumber, claimId, claimRef){
    const amount  = parseFloat(document.getElementById('ims-settle-amount')?.value) || 0;
    const date    = document.getElementById('ims-settle-date')?.value;
    const ref     = document.getElementById('ims-settle-ref')?.value?.trim() || '';
    const notes   = document.getElementById('ims-settle-notes')?.value?.trim() || '';
    if(!amount || amount <= 0){ ui().toast('Please enter a valid settlement amount','error'); return; }
    if(!date){ ui().toast('Please select the settlement date','error'); return; }

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: claim } = await client.from('claims').select('courier_id,couriers(name)').eq('id',claimId).maybeSingle();
    const courierName = claim?.couriers?.name || 'Courier';

    // Both settled-amount columns are written, so Lost Orders, Accounts >
    // Claims and Reports all show the same figure.
    const closed = await _closeClaim(claimId, {
      status:         'settled',
      amount_settled: amount,
      settled_amount: amount,
      settled_at:     new Date(date).toISOString(),
      settlement_ref: ref || null
    });
    if (closed.error) { ui().toast(`Claim ${claimRef} not settled: ${closed.error.message}. Nothing was changed.`, 'error', 10000); return; }
    if (closed.already) {
      ui().closeModal();
      ui().toast(`Claim ${claimRef} is already closed. Nothing was changed.`, 'warning', 6000);
      loadLostOrders();
      return;
    }

    const problems = [];
    const { error: txErr } = await db().insert('transactions',{
      date:        date,
      type:        'income',
      category:    'Courier claim settlement',
      description: `Claim settlement: ${claimRef}, ${orderNumber}, ${courierName}${notes ? ', ' + notes : ''}`,
      amount:      amount,
      reference:   claimRef
    });
    if (txErr) problems.push(`the settlement could not be recorded in Accounts (${txErr.message})`);

    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Claim settled',
      notes:           `${claimRef}: £${amount.toFixed(2)} settled${ref ? ', ref ' + ref : ''}`,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, 'lost', 'lost', 'claim_settled', `${claimRef}: £${amount.toFixed(2)}`);

    // The courier has paid for the goods, so they are gone for good:
    // take them off stock (skipped if they were delivered or put back).
    const wo = await writeOffOrderStock(orderId, orderNumber, `Claim ${claimRef} settled: goods lost or damaged, taken off stock`);
    wo.problems.forEach(p => problems.push(`stock could not be written off for ${p}`));

    ui().closeModal();
    if (problems.length) {
      ui().toast(`Claim ${claimRef} settled, but ${problems.join('; ')}. Please put this right by hand.`, 'warning', 15000);
    } else {
      ui().toast(`Claim ${claimRef} settled: £${amount.toFixed(2)} recorded as income in Accounts${wo.done ? `, ${wo.done} line(s) taken off stock` : ''}`, 'success', 6000);
    }
    loadLostOrders();
  }

  // ─── REJECT CLAIM ───────────────────────────────────────────────
  function openRejectClaim(orderId, orderNumber, claimId, claimRef, amountClaimed){
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Reject claim — ${claimRef}`, `
      <div class="ims__alert ims__alert--danger" style="margin-bottom:14px">
        <i class="ti ti-alert-triangle"></i>
        <span>Rejecting this claim means the loss falls on the company. <strong>£${parseFloat(amountClaimed).toFixed(2)}</strong> will be recorded as an operating expense (write-off) in Accounts → Transactions.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Rejection date</label>
          <input class="ims__input" type="date" id="ims-reject-date" value="${todayStr}" max="${todayStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Rejection reason</label>
          <select class="ims__select" id="ims-reject-reason">
            <option value="">Select reason...</option>
            <option value="Courier rejected — insufficient evidence">Courier rejected — insufficient evidence</option>
            <option value="Courier rejected — outside claim window">Courier rejected — outside claim window</option>
            <option value="Courier rejected — terms and conditions exclusion">Courier rejected — terms and conditions exclusion</option>
            <option value="Claim withdrawn — resolved directly with customer">Claim withdrawn — resolved directly with customer</option>
            <option value="Courier rejected — no response after multiple attempts">Courier rejected — no response after multiple attempts</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Additional notes</label>
          <textarea class="ims__textarea" id="ims-reject-notes" rows="2" placeholder="Any further context..."></textarea>
        </div>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--danger" onclick="window.IMS_LOGISTICS.confirmRejectClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}','${util().jsArg(claimId)}','${util().jsArg(claimRef)}',${amountClaimed})">
        <i class="ti ti-x"></i> Confirm rejection — write off £${parseFloat(amountClaimed).toFixed(2)}
      </button>
    `, 'sm');
  }

  async function confirmRejectClaim(orderId, orderNumber, claimId, claimRef, amountClaimed){
    const dateVal = document.getElementById('ims-reject-date')?.value;
    const reason = document.getElementById('ims-reject-reason')?.value;
    const notes  = document.getElementById('ims-reject-notes')?.value?.trim() || '';
    if(!dateVal){ ui().toast('Please select the rejection date','error'); return; }
    if(!reason){ ui().toast('Please select a rejection reason','error'); return; }
    const fullReason = notes ? `${reason}: ${notes}` : reason;
    const rejectedAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: claim } = await client.from('claims').select('courier_id,couriers(name)').eq('id',claimId).maybeSingle();
    const courierName = claim?.couriers?.name || 'Courier';
    const amount = parseFloat(amountClaimed) || 0;

    const closed = await _closeClaim(claimId, {
      status:            'rejected',
      rejected_at:       rejectedAt,
      rejection_reason:  fullReason
    });
    if (closed.error) { ui().toast(`Claim ${claimRef} not rejected: ${closed.error.message}. Nothing was changed.`, 'error', 10000); return; }
    if (closed.already) {
      ui().closeModal();
      ui().toast(`Claim ${claimRef} is already closed. Nothing was changed.`, 'warning', 6000);
      loadLostOrders();
      return;
    }

    const problems = [];
    // A rejected claim is the company's loss. The category name is kept
    // exactly as before so existing reports keep grouping these together.
    const { error: txErr } = await db().insert('transactions',{
      date:        dateVal,
      type:        'expense',
      category:    'Write-off \u2014 Rejected courier claim',
      description: `Claim rejected: ${claimRef}, ${orderNumber}, ${courierName}, ${reason}`,
      amount:      amount,
      reference:   claimRef
    });
    if (txErr) problems.push(`the write-off could not be recorded in Accounts (${txErr.message})`);

    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Claim rejected',
      notes:           `${claimRef}: £${amount.toFixed(2)} written off as expense. ${fullReason}`,
      created_at:      rejectedAt,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, 'lost', 'lost', 'claim_rejected', `${claimRef}: written off £${amount.toFixed(2)}`);

    // The goods are gone and nobody is paying for them: take them off
    // stock (skipped if they were delivered or put back).
    const wo = await writeOffOrderStock(orderId, orderNumber, `Claim ${claimRef} rejected: goods lost or damaged, taken off stock`);
    wo.problems.forEach(p => problems.push(`stock could not be written off for ${p}`));

    ui().closeModal();
    if (problems.length) {
      ui().toast(`Claim ${claimRef} rejected, but ${problems.join('; ')}. Please put this right by hand.`, 'warning', 15000);
    } else {
      ui().toast(`Claim ${claimRef} rejected: £${amount.toFixed(2)} written off as an expense in Accounts${wo.done ? `, ${wo.done} line(s) taken off stock` : ''}`, 'danger', 7000);
    }
    loadLostOrders();
  }

  // ─── PARCEL FOUND ───────────────────────────────────────────────
  // The parcel has physically come back. What happens depends on its
  // condition:
  //  - saleable: the claim closes as 'recovered' with no money moving, and
  //    the goods go back to stock;
  //  - damaged: the goods are NOT restocked and the claim stays OPEN, so
  //    it can still be settled (the courier pays) or rejected.
  // Staff must also tick a box confirming the parcel is really back. Two
  // mistaken "Parcel found" entries caused trouble before (CLM-00002).
  function openParcelFound(orderId, orderNumber, claimId, claimRef){
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Parcel found - ${claimRef}`, `
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-package"></i>
        <span>Use this only when the parcel has <strong>physically come back to us</strong>. If the goods are saleable, the claim closes with no money moving and they go back to stock. If they are damaged, the claim stays open so it can still be settled or rejected.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Date returned to us</label>
          <input class="ims__input" type="date" id="ims-pf-date" value="${todayStr}" max="${todayStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Condition of goods</label>
          <select class="ims__select" id="ims-pf-condition">
            <option value="">Select condition...</option>
            <option value="saleable">Saleable: add back to stock and close the claim</option>
            <option value="damaged">Damaged: do NOT restock, keep the claim open</option>
          </select>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Notes</label>
          <textarea class="ims__textarea" id="ims-pf-notes" rows="2" placeholder="e.g. returned by courier depot, packaging intact..."></textarea>
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-size:13px">
            <input type="checkbox" id="ims-pf-confirm" style="margin-top:3px">
            <span>I confirm this parcel is <strong>physically back in our warehouse</strong>.</span>
          </label>
        </div>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success" onclick="window.IMS_LOGISTICS.confirmParcelFound('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}','${util().jsArg(claimId)}','${util().jsArg(claimRef)}')">
        <i class="ti ti-check"></i> Confirm parcel found
      </button>
    `, 'sm');
  }

  async function restockFoundParcel(orderId, orderNumber){
    // Delegates to the shared smartRestock helper, which works out whether
    // the goods had left stock (delivered or written off) and does the
    // right thing either way, and never runs twice for the same order.
    return await smartRestock(orderId, orderNumber, 'Parcel found - returned to stock');
  }

  async function confirmParcelFound(orderId, orderNumber, claimId, claimRef){
    const dateVal   = document.getElementById('ims-pf-date')?.value;
    const condition = document.getElementById('ims-pf-condition')?.value;
    const notes     = document.getElementById('ims-pf-notes')?.value?.trim() || '';
    if(!dateVal){ ui().toast('Please select the date returned','error'); return; }
    if(!condition){ ui().toast('Please select the condition of the goods','error'); return; }
    if(!document.getElementById('ims-pf-confirm')?.checked){
      ui().toast('Please tick the box to confirm the parcel is physically back in the warehouse','error');
      return;
    }

    const foundAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const userName = window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User';

    if (condition === 'damaged') {
      // Goods are back but cannot be sold. The claim stays open so it can
      // be settled or rejected; settling or rejecting then takes the
      // damaged goods off stock.
      await client.from('order_tracking_events').insert({
        company_id:      window.SM.AUTH.company?.id,
        order_id:        orderId,
        tracking_status: 'Parcel returned damaged',
        notes:           `${claimRef}: parcel back but damaged, not restocked. Claim stays open.${notes ? ' ' + notes : ''}`,
        created_at:      foundAt,
        user_name:       userName,
        user_id:         window.SM.AUTH.user?.id || null
      });
      await logActivity(orderId, 'lost', 'lost', 'parcel_returned_damaged', `${claimRef}: parcel back damaged, claim still open`);
      ui().closeModal();
      ui().toast(`${claimRef}: parcel recorded as returned damaged. Not restocked. The claim stays open so it can be settled or rejected.`, 'info', 8000);
      loadLostOrders();
      return;
    }

    // Saleable: close the claim (only if still open), then restock.
    const closed = await _closeClaim(claimId, {
      status:           'recovered',
      settled_at:       foundAt,
      rejection_reason: `Parcel found and returned - saleable${notes ? ' - ' + notes : ''}`
    });
    if (closed.error) { ui().toast(`Claim ${claimRef} not updated: ${closed.error.message}. Nothing was changed.`, 'error', 10000); return; }
    if (closed.already) {
      ui().closeModal();
      ui().toast(`Claim ${claimRef} is already closed. Nothing was changed.`, 'warning', 6000);
      loadLostOrders();
      return;
    }

    const restocked = await restockFoundParcel(orderId, orderNumber);

    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Parcel found - claim closed',
      notes:           `${claimRef} - parcel returned (saleable)${restocked ? ` - ${restocked} item line(s) restocked` : ''}${notes ? '. ' + notes : ''}`,
      created_at:      foundAt,
      user_name:       userName,
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, 'lost', 'lost', 'claim_recovered', `${claimRef} - parcel found (saleable)${restocked ? `, ${restocked} line(s) restocked` : ''}`);

    ui().closeModal();
    ui().toast(`Claim ${claimRef} closed - parcel found${restocked ? `, ${restocked} item line(s) added back to stock` : ', no stock change needed'}`, 'success', 7000);
    loadLostOrders();
  }

  // ─── WRITE OFF LOST GOODS ────────────────────────────────────────
  // When a lost case is closed for good (claim settled, claim rejected, or
  // closed without a claim), the goods are gone. An order that never
  // reached 'delivered' never had its on-hand stock reduced (only reserved
  // at order creation), so its stock would otherwise show units that no
  // longer exist, forever. This takes them off: on-hand down, reservation
  // released, and a 'write_off' movement logged (kept separate from sales).
  // Skipped if the goods were already taken off (a 'sale' or 'write_off'
  // movement exists) or were put back (the order is stamped restocked).
  // Returns { done, problems }.
  async function writeOffOrderStock(orderId, orderNumber, reason){
    const { data: moved } = await db().select('stock_movements', {
      eq: { reference: orderNumber }, in: { movement_type: ['sale', 'write_off'] }, limit: 1, noCompanyFilter: true
    });
    if (moved?.length) return { done: 0, problems: [] };

    const { data: ord } = await db().select('orders', {
      select: 'warehouse_id,restocked_at,fulfillment_type,order_items(product_id,product_name,quantity)',
      eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
    });
    // Dropship orders: off the shelf and out of the partner's reservation,
    // in the allocation's warehouse (ims-dropship.js).
    if (ord?.fulfillment_type === 'dropship') {
      if (!window.IMS_DROPSHIP?.writeOffDropshipStock) return { done: 0, problems: ['the Dropship module did not load'] };
      return await window.IMS_DROPSHIP.writeOffDropshipStock(orderId, orderNumber, reason);
    }
    if (!ord || ord.restocked_at) return { done: 0, problems: [] };
    const whId  = ord.warehouse_id;
    const items = ord.order_items || [];
    if (!whId || !items.length) return { done: 0, problems: [] };

    let done = 0;
    const problems = [];
    // One line at a time, so two lines of the same product cannot both
    // read the same starting figure and overwrite each other.
    for (const item of items) {
      if (!item.product_id) continue;
      const { data: sl } = await db().select('stock_levels', {
        eq: { product_id: item.product_id, warehouse_id: whId }, maybeSingle: true, noCompanyFilter: true
      });
      if (sl) {
        const { error } = await db().update('stock_levels', sl.id, {
          quantity:     Math.max(0, (sl.quantity || 0) - (item.quantity || 0)),
          reserved_qty: Math.max(0, (sl.reserved_qty || 0) - (item.quantity || 0))
        });
        if (error) { problems.push(item.product_name || item.product_id); continue; }
      }
      await db().insert('stock_movements', {
        product_id: item.product_id, warehouse_id: whId, movement_type: 'write_off',
        quantity: -(item.quantity || 0), reference: orderNumber || null,
        reason, user_id: window.SM.AUTH.user?.id
      });
      done++;
    }
    return { done, problems };
  }

  // ─── CLOSE WITHOUT CLAIM ─────────────────────────────────────────
  // For a lost order that will never be claimed (for example lost on our
  // own van, or a courier that will not accept a claim). Records the case
  // as closed ('written_off') in the claims history so it still shows in
  // Accounts > Claims and the Courier claims log, posts the loss as an
  // expense, and takes the goods off stock. Admin and Super Admin only.
  function openCloseNoClaim(orderId, orderNumber, totalValue){
    if(!window.SM.AUTH.isAdmin()){ ui().toast('Only an Admin can close a case without a claim','error'); return; }
    const todayStr = new Date().toISOString().split('T')[0];
    ui().openModal(`Close without claim - ${util().sanitize(orderNumber)}`, `
      <div class="ims__alert ims__alert--warning" style="margin-bottom:14px">
        <i class="ti ti-alert-triangle"></i>
        <span>Use this when <strong>no courier claim will be made</strong>. The amount below is recorded as an expense in Accounts, the goods are taken off stock, and the case moves to the claims history.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Amount to write off (£)</label>
          <input class="ims__input" type="number" step="0.01" min="0" id="ims-cnc-amount" value="${(parseFloat(totalValue) || 0).toFixed(2)}">
          <span class="ims__input-hint">Defaults to the order value</span>
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Date</label>
          <input class="ims__input" type="date" id="ims-cnc-date" value="${todayStr}" max="${todayStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Reason</label>
          <input class="ims__input" type="text" id="ims-cnc-reason" placeholder="e.g. Lost on own delivery run, courier will not accept a claim">
        </div>
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--danger" onclick="window.IMS_LOGISTICS.confirmCloseNoClaim('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-archive"></i> Close and write off</button>
    `, 'sm');
  }

  async function confirmCloseNoClaim(orderId, orderNumber){
    if(!window.SM.AUTH.isAdmin()){ ui().toast('Only an Admin can close a case without a claim','error'); return; }
    const amount  = parseFloat(document.getElementById('ims-cnc-amount')?.value);
    const dateVal = document.getElementById('ims-cnc-date')?.value;
    const reason  = document.getElementById('ims-cnc-reason')?.value?.trim();
    if(isNaN(amount) || amount < 0){ ui().toast('Please enter a valid amount','error'); return; }
    if(!dateVal){ ui().toast('Please select the date','error'); return; }
    if(!reason){ ui().toast('Please enter a reason','error'); return; }
    const closedAt = new Date(`${dateVal}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    // Only for a case with no claim at all: if one exists (for example
    // just raised on another screen), stop rather than double up.
    const { data: existing } = await client.from('claims').select('id').eq('order_id', orderId).limit(1);
    if (existing?.length) {
      ui().closeModal();
      ui().toast(`${orderNumber} already has a claim record. Nothing was changed.`, 'warning', 6000);
      loadLostOrders();
      return;
    }

    const ship = (window._lostShipMap || {})[orderId];
    const { error: insErr } = await client.from('claims').insert({
      company_id:       window.SM.AUTH.company?.id,
      order_id:         orderId,
      courier_id:       ship?.courier_id || null,
      claim_type:       'lost',
      status:           'written_off',
      amount_claimed:   amount,
      items_value:      amount,
      description:      `Closed without claim: ${reason}`,
      rejection_reason: reason,
      raised_at:        closedAt,
      rejected_at:      closedAt
    });
    if (insErr) { ui().toast(`Case not closed: ${insErr.message}. Nothing was changed.`, 'error', 10000); return; }

    const problems = [];
    if (amount > 0) {
      const { error: txErr } = await db().insert('transactions',{
        date:        dateVal,
        type:        'expense',
        category:    'Write-off: lost shipment (no claim)',
        description: `Lost shipment written off, no claim: ${orderNumber}, ${reason}`,
        amount:      amount,
        reference:   orderNumber
      });
      if (txErr) problems.push(`the write-off could not be recorded in Accounts (${txErr.message})`);
    }

    await client.from('order_tracking_events').insert({
      company_id:      window.SM.AUTH.company?.id,
      order_id:        orderId,
      tracking_status: 'Closed without claim',
      notes:           `£${amount.toFixed(2)} written off. ${reason}`,
      created_at:      closedAt,
      user_name:       window.SM.AUTH.user?.name || window.SM.AUTH.user?.email || 'User',
      user_id:         window.SM.AUTH.user?.id || null
    });
    await logActivity(orderId, 'lost', 'lost', 'closed_without_claim', `£${amount.toFixed(2)} written off: ${reason}`);

    const wo = await writeOffOrderStock(orderId, orderNumber, `Lost, closed without claim: ${reason}`);
    wo.problems.forEach(p => problems.push(`stock could not be written off for ${p}`));

    ui().closeModal();
    if (problems.length) {
      ui().toast(`${orderNumber} closed without claim, but ${problems.join('; ')}. Please put this right by hand.`, 'warning', 15000);
    } else {
      ui().toast(`${orderNumber} closed without claim: £${amount.toFixed(2)} written off${wo.done ? `, ${wo.done} line(s) taken off stock` : ''}`, 'success', 7000);
    }
    loadLostOrders();
  }

// ─── ACTIVITY LOG VIEWER ────────────────────────────────────────
  async function openActivityLog(orderId, orderNumber){
    const client = window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
    const { data: logs } = await client
      .from('order_activity_log')
      .select('*')
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });

    const items = logs || [];
    const statusLabels = {
      confirmed:'Confirmed', picking:'Picking', packed:'Packed',
      dispatched:'Dispatched', delivered:'Delivered',
      failed_delivery:'Failed delivery', re_attempted:'Re-attempted',
      return_in_transit:'Return in transit', return_received:'Return received',
      cancelled:'Cancelled', collected:'Collected', lost:'Lost', refunded:'Refunded'
    };
    const actionLabels = {
      delivered:'Delivered ✓',
      delivery_failed:'Delivery failed',
      re_attempt_requested:'Re-attempt requested',
      return_requested:'Return requested',
      return_received:'Return received ✓',
      status_update:'Status update'
    };

    const rows = items.map(log => {
      const oldS = log.old_value ? (statusLabels[log.old_value] || log.old_value) : '—';
      const newS = log.new_value ? (statusLabels[log.new_value] || log.new_value) : '—';
      const act  = log.action     ? (actionLabels[log.action]     || log.action)     : '—';
      const arrow = log.old_value && log.new_value ? `${oldS} → ${newS}` : newS;
      return `<tr>
        <td style="font-size:11px;white-space:nowrap">${log.created_at ? util().formatDateTime ? util().formatDateTime(log.created_at) : util().formatDate(log.created_at) : '—'}</td>
        <td style="font-size:12px">${util().sanitize(log.user_name||'System')}</td>
        <td>${util().sanitize(arrow)}</td>
        <td style="font-size:12px;color:var(--ims-text-sec)">${util().sanitize(act)}</td>
        <td style="font-size:12px;color:var(--ims-text-sec)">${util().sanitize(log.notes||'—')}</td>
      </tr>`;
    }).join('');

    ui().openModal(`Activity log — ${orderNumber}`, `
      ${!items.length
        ? `<div class="ims__empty"><i class="ti ti-history"></i><p>No activity logged yet</p><p style="font-size:12px;color:var(--ims-text-muted)">Activity is recorded when status changes are made from the tracking tab</p></div>`
        : `<div class="ims__table-wrap">
            <table class="ims__table">
              <thead><tr><th>Date / time</th><th>User</th><th>Status change</th><th>Action</th><th>Notes</th></tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </div>`
      }
    `, `<button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal()">Close</button>`, 'lg');
  }

  // ─── REASSIGN COURIER → OWN TRUCK (Super Admin only) ─────────────
  // Assigning a courier creates a shipments row, flips the job to
  // assigned_courier, dispatches the order, AND books a courier charge as
  // an expense. Undoing it has to reverse all of that — particularly the
  // charge, or you stay permanently out of pocket in Accounts for a courier
  // that was never used. The order itself stays 'dispatched' because it is
  // still going out, just on our own truck instead.
  async function reassignToOwnTruck(orderId, orderNumber){
    if(!window.SM.AUTH.isSuperAdmin()){ ui().toast('Only Super Admin can reassign a dispatched order','error'); return; }
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);

    const {data:shipment}=await client.from('shipments')
      .select('id,tracking_number,charge_amount,couriers(name)')
      .eq('order_id',orderId).eq('is_return_collection',false).order('created_at',{ascending:false}).limit(1).maybeSingle();

    const charge=shipment?.charge_amount||0;
    const courierName=shipment?.couriers?.name||'courier';

    ui().openModal(`Reassign to own truck — ${util().sanitize(orderNumber)}`,`
      <div class="ims__alert ims__alert--warning" style="margin-bottom:12px">
        <i class="ti ti-alert-triangle"></i>
        <span>This order is currently assigned to <strong>${util().sanitize(courierName)}</strong>${shipment?.tracking_number?` with tracking <strong>${util().sanitize(shipment.tracking_number)}</strong>`:''}.</span>
      </div>
      <p style="font-size:13px;color:var(--ims-text-sec);margin-bottom:12px">Reassigning will:</p>
      <ul style="font-size:13px;color:var(--ims-text-sec);margin:0 0 12px 18px;line-height:1.7">
        <li>Remove the courier shipment and its tracking number</li>
        <li>Move the job into the own-truck delivery queue</li>
        ${charge>0?`<li><strong>Reverse the courier charge of ${util().formatCurrency(charge)}</strong> from Accounts</li>`:''}
        <li>Keep the order as <strong>Dispatched</strong> — it is still going out, just on our own truck</li>
      </ul>
      <div class="ims__form-group">
        <label class="ims__label">Reason (recorded in the order's activity log)</label>
        <input class="ims__input" type="text" id="ims-rot-reason" placeholder="e.g. Assigned to courier in error — own truck delivery">
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--danger" onclick="window.IMS_LOGISTICS.confirmReassignToOwnTruck('${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')"><i class="ti ti-truck"></i> Reassign to own truck</button>
    `,'sm');
  }

  async function confirmReassignToOwnTruck(orderId, orderNumber){
    if(!window.SM.AUTH.isSuperAdmin()){ ui().toast('Only Super Admin can reassign a dispatched order','error'); return; }
    const reason=document.getElementById('ims-rot-reason')?.value?.trim()||'Reassigned from courier to own truck';
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);

    const {data:shipment}=await client.from('shipments')
      .select('id,tracking_number,charge_amount')
      .eq('order_id',orderId).eq('is_return_collection',false).order('created_at',{ascending:false}).limit(1).maybeSingle();

    // Reverse the courier charge. The transaction was written with
    // reference = tracking number, which is the only link back to it.
    if(shipment?.charge_amount>0 && shipment?.tracking_number){
      const {error:txErr}=await client.from('transactions')
        .delete()
        .eq('reference',shipment.tracking_number)
        .eq('category','Courier charges');
      if(txErr){
        ui().toast('Could not reverse the courier charge: '+txErr.message+' — reassignment stopped so your accounts stay correct.','error',8000);
        return;
      }
    }

    if(shipment?.id){
      const {error:shipErr}=await client.from('shipments').delete().eq('id',shipment.id);
      if(shipErr){ ui().toast('Failed to remove shipment: '+shipErr.message,'error',6000); return; }
    }

    const {data:job}=await client.from('logistics_jobs')
      .select('id').eq('order_id',orderId).eq('is_return_collection',false).order('created_at',{ascending:false}).limit(1).maybeSingle();
    if(job?.id){
      await db().update('logistics_jobs',job.id,{
        job_type:'own_truck', status:'scheduled', assigned_courier_id:null
      });
    }

    await logActivity(orderId,'dispatched','dispatched','reassigned_own_truck',`${reason}${shipment?.charge_amount>0?` — courier charge of ${util().formatCurrency(shipment.charge_amount)} reversed`:''}`);

    ui().closeModal();
    ui().toast(`${orderNumber} reassigned to own truck${shipment?.charge_amount>0?` — ${util().formatCurrency(shipment.charge_amount)} courier charge reversed`:''}`,'success',6000);
    loadTracking();
  }

  async function loadUnassigned(){
    const content=document.getElementById('ims-log-content');
    if(!content)return;
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:jobs}=await client.from('logistics_jobs')
      .select('id,job_type,status,collection_slot,orders(id,order_number,customer_name,postcode,address,total_value,order_type,customer_id,platforms(name),customers(delivery_arrangement))')
      .eq('status','unassigned')
      .order('created_at');
    const items=jobs||[];
    const deliveries=items.filter(j=>j.job_type==='delivery');
    const collections=items.filter(j=>['collection','courier_collection'].includes(j.job_type));
    const selfCollection=items.filter(j=>j.job_type==='self_collection');

    ui().updateBadge('ims-badge-logistics',items.length);

    content.innerHTML=`
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;flex-wrap:wrap;gap:8px">
        <div style="font-size:13px;color:var(--ims-text-sec)">
          <strong>${deliveries.length}</strong> deliveries &nbsp;|&nbsp;
          <strong>${collections.length}</strong> collections &nbsp;|&nbsp;
          <strong>${selfCollection.length}</strong> self-collection
        </div>
        <div class="ims__btn-group">
          <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.bulkAssignCourier()"><i class="ti ti-truck"></i> Assign to courier</button>
          <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_LOGISTICS.planRoute()"><i class="ti ti-route"></i> Plan route</button>
        </div>
      </div>
      ${!items.length?`<div class="ims__empty">
        <i class="ti ti-check" aria-hidden="true"></i>
        <p>No unassigned jobs</p>
        <p style="font-size:12px;color:var(--ims-text-muted);margin-top:6px">Jobs appear here automatically once orders are marked Packed in <span class="ims__link" onclick="window.SM.UI.showPanel('orders')" style="cursor:pointer">Order Management</span></p>
      </div>`:''}
      ${deliveries.length?`
        <div class="ims__card" style="margin-bottom:12px">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
            <div class="ims__card-title"><i class="ti ti-truck" aria-hidden="true"></i>Deliveries (${deliveries.length})</div>
            <div class="ims__btn-group">
              <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_LOGISTICS.printPicklist()">
                <i class="ti ti-printer"></i> Print picklist
              </button>
            </div>
          </div>
          <div class="ims__table-wrap">
            <table class="ims__table">
              <thead><tr><th><input type="checkbox" id="ims-delivery-all" onchange="window.IMS_LOGISTICS.toggleAll('delivery',this.checked)"></th><th>Order #</th><th>Customer</th><th>Postcode</th><th>Type</th><th>Value</th><th>Actions</th></tr></thead>
              <tbody>${deliveries.map(j=>{
                // Show the sales channel (Temu, Amazon, Website, Retailer /
                // Wholesaler...) rather than the internal retail/wholesale
                // flag — "Retail" told you nothing useful about where the
                // order actually came from.
                const orderType=j.orders?.order_type;
                const platName=j.orders?.platforms?.name;
                const typePill=platName
                  ?`<span class="ims__pill ims__pill--${orderType==='wholesale'?'info':'neutral'}">${util().sanitize(platName)}</span>`
                  :orderType==='wholesale'
                    ?'<span class="ims__pill ims__pill--info">Wholesale</span>'
                    :orderType==='retail'
                      ?'<span class="ims__pill ims__pill--neutral">Retail</span>'
                      :'';
                const arrangement=j.orders?.customers?.delivery_arrangement;
                const arrangementPill=arrangement==='customer_collects'
                  ?'<span class="ims__pill ims__pill--warning" title="Customer\'s standing arrangement on file — confirm before assigning a courier"><i class="ti ti-home"></i> Customer collects</span>'
                  :arrangement==='mdf_delivers'
                    ?'<span class="ims__pill ims__pill--success" title="Customer\'s standing arrangement on file"><i class="ti ti-truck"></i> We deliver</span>'
                    :'';
                return `<tr>
                <td><input type="checkbox" class="ims-job-check" value="${j.id}" data-order-id="${j.orders?.id||''}" data-type="delivery"></td>
                <td><span class="ims__link" onclick="window.IMS_ORDERS.openOrder('${util().jsArg(j.orders?.id||'')}')">${util().sanitize(j.orders?.order_number||'—')}</span></td>
                <td>${util().sanitize(j.orders?.customer_name||'—')}</td>
                <td>${util().sanitize(j.orders?.postcode||'—')}</td>
                <td style="white-space:nowrap">${typePill} ${arrangementPill}</td>
                <td>${util().formatCurrency(j.orders?.total_value)}</td>
                <td><div class="ims__btn-group">
                  <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.assignJobCourier('${util().jsArg(j.id)}')" title="Book courier"><i class="ti ti-truck-delivery"></i> Courier</button>
                  <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.assignOwnTruck('${util().jsArg(j.id)}','${util().jsArg(j.orders?.id||'')}')" title="Own truck delivers"><i class="ti ti-truck"></i> We deliver</button>
                  <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.markSelfCollection('${util().jsArg(j.id)}','${util().jsArg(j.orders?.id||'')}')" title="Customer collects"><i class="ti ti-home"></i> Self-collect</button>
                  <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.cancelUnassignedOrder('${util().jsArg(j.id)}','${util().jsArg(j.orders?.id||'')}','${util().jsArg(j.orders?.order_number||'')}')" title="Cancel order"><i class="ti ti-x"></i></button>
                </div></td>
              </tr>`;
              }).join('')}
              </tbody>
            </table>
          </div>
        </div>` : ''}
      ${collections.length?`
        <div class="ims__card" style="margin-bottom:12px">
          <div class="ims__card-title" style="margin-bottom:10px"><i class="ti ti-package-import" aria-hidden="true"></i>Collections (${collections.length})</div>
          <div class="ims__table-wrap">
            <table class="ims__table">
              <thead><tr><th>Order #</th><th>Customer</th><th>Postcode</th><th>Type</th><th>Actions</th></tr></thead>
              <tbody>${collections.map(j=>`<tr>
                <td><span class="ims__link" onclick="window.IMS_ORDERS.openOrder('${util().jsArg(j.orders?.id||'')}')">${util().sanitize(j.orders?.order_number||'—')}</span></td>
                <td>${util().sanitize(j.orders?.customer_name||'—')}</td>
                <td>${util().sanitize(j.orders?.postcode||'—')}</td>
                <td>${util().pill(j.job_type)}</td>
                <td><button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.assignJobCourier('${util().jsArg(j.id)}')"><i class="ti ti-truck"></i> Assign courier</button></td>
              </tr>`).join('')}
              </tbody>
            </table>
          </div>
        </div>` : ''}
      ${selfCollection.length?`
        <div class="ims__card">
          <div class="ims__card-title" style="margin-bottom:10px"><i class="ti ti-home" aria-hidden="true"></i>Ready for customer collection (${selfCollection.length})</div>
          <div class="ims__table-wrap">
            <table class="ims__table">
              <thead><tr><th>Order #</th><th>Customer</th><th>Postcode</th><th>Slot</th><th>Actions</th></tr></thead>
              <tbody>${selfCollection.map(j=>`<tr>
                <td><span class="ims__link" onclick="window.IMS_ORDERS.openOrder('${util().jsArg(j.orders?.id||'')}')">${util().sanitize(j.orders?.order_number||'—')}</span></td>
                <td>${util().sanitize(j.orders?.customer_name||'—')}</td>
                <td>${util().sanitize(j.orders?.postcode||'—')}</td>
                <td>${j.collection_slot?util().formatDateTime(j.collection_slot):'No slot set'}</td>
                <td>
                  ${j.status==='collected'
                    ? `<span class="ims__pill ims__pill--success"><i class="ti ti-check"></i> Collected</span>`
                    : `<div class="ims__btn-group">
                        <button class="ims__btn ims__btn--success ims__btn--sm" onclick="window.IMS_LOGISTICS.showSelfCollectionModal('${util().jsArg(j.id)}','${util().jsArg(j.orders?.id||'')}','${util().jsArg(j.orders?.order_number||'')}')"><i class="ti ti-check"></i> Mark collected</button>
                        <button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.cancelSelfCollection('${util().jsArg(j.id)}','${util().jsArg(j.orders?.id||'')}')" title="Move back to delivery"><i class="ti ti-arrow-back-up"></i></button>
                      </div>`}
                </td>
              </tr>`).join('')}
              </tbody>
            </table>
          </div>
        </div>` : ''}`;
  }

  function toggleAll(type,checked){
    document.querySelectorAll(`.ims-job-check[data-type="${type}"]`).forEach(cb=>{cb.checked=checked;});
  }

  // ─── AUTOMATIC COURIER CHARGE (rate cards) ────────────────────────
  // Works out what a courier priced by product type and zone (e.g. V1)
  // should charge for an order, per order (consignment), as V1 invoices:
  //  1. zone from the postcode (district beats area; else the default zone);
  //  2. if any item is in a weight band, the whole order takes the band
  //     that fits its total weight; otherwise the order takes the price of
  //     its highest-priced item type;
  //  3. plus extras (those linked to services on the order are ticked
  //     automatically), room of choice floors, then fuel %.
  // The result pre-fills the charge, which staff can still change; the
  // calculated figure and its breakdown are saved for the invoice check.
  const _acState = {};   // per job: { calc, ticked:Set|null, touched }
  const _rcOutward = pc => { const c = _rcPc(pc); return c.length >= 5 ? c.slice(0, -3) : c; };
  const _rcArea = out => (String(out).match(/^[A-Z]{1,2}/) || [''])[0];
  const _money = n => Math.round((Number(n) || 0) * 100) / 100;

  async function _rateCardCharge({ courierId, orderId, postcode, weightKg, floor, ticked, date }) {
    const db_ = _rcDb(), today = date || _rcToday();   // the rate card in force on this date
    const { data: cards } = await db_.from('courier_rate_cards').select('*').eq('courier_id', courierId).eq('pricing_method', 'type_zone').order('valid_from', { ascending: false });
    const card = (cards || []).find(c => String(c.valid_from) <= today);
    if (!card) return null;   // not a rate card courier (or no version in force yet)
    const [zonesR, pcsR, typesR, ratesR, extrasR, itemsR] = await Promise.all([
      db_.from('courier_zones').select('*').eq('courier_id', courierId).order('sort_order', { ascending: true }),
      db_.from('courier_zone_postcodes').select('id,zone_id,prefix').eq('courier_id', courierId),
      db_.from('courier_product_types').select('*').eq('courier_id', courierId),
      db_.from('courier_card_rates').select('*').eq('card_id', card.id),
      db_.from('courier_card_extras').select('*').eq('card_id', card.id).order('sort_order', { ascending: true }),
      db_.from('order_items').select('product_id,product_name,quantity,service_id').eq('order_id', orderId)
    ]);
    const zones = zonesR.data || [], pcs = pcsR.data || [], types = typesR.data || [], rates = ratesR.data || [], extras = extrasR.data || [], items = itemsR.data || [];
    const flags = [];
    // 1. zone
    const outward = _rcOutward(postcode), area = _rcArea(outward);
    const hit = pcs.find(p => p.prefix === outward) || pcs.find(p => p.prefix === area);
    const zone = hit ? zones.find(z => z.id === hit.zone_id) : zones.find(z => z.kind === 'default');
    if (!zone) flags.push('No zone found for this postcode');
    if (zone?.kind === 'not_served') flags.push(`${outward || 'This postcode'} is in "${zone.name}" for this courier`);
    // 2. the order's rate
    const products = items.filter(i => i.product_id);
    const { data: map } = products.length
      ? await db_.from('courier_product_map').select('product_id,type_id').eq('courier_id', courierId).in('product_id', products.map(i => i.product_id))
      : { data: [] };
    const typeOf = pid => types.find(t => t.id === (map || []).find(m => m.product_id === pid)?.type_id);
    const priceOf = t => (zone && t) ? rates.find(r => r.type_id === t.id && r.zone_id === zone.id)?.price : null;
    products.forEach(i => { if (!typeOf(i.product_id)) flags.push(`${i.product_name} has no type on this rate card`); });
    const itemTypes = products.map(i => typeOf(i.product_id)).filter(Boolean);
    let rateLine = null;
    if (itemTypes.some(t => t.max_weight_kg != null)) {
      const bands = types.filter(t => t.max_weight_kg != null).sort((a, b) => a.max_weight_kg - b.max_weight_kg);
      const w = Number(weightKg) || 0;
      let band = bands.find(t => w <= Number(t.max_weight_kg));
      if (!band) { band = bands[bands.length - 1]; flags.push(`The order weighs ${w} kg, more than the largest band (${band.max_weight_kg} kg)`); }
      if (!w) flags.push('No weight entered: the smallest weight band was used');
      rateLine = { label: `${band.name} (order weight ${w} kg)`, type: band.name, price: priceOf(band) };
    } else if (itemTypes.length) {
      const top = itemTypes.map(t => ({ t, p: priceOf(t) })).sort((a, b) => (b.p ?? -1) - (a.p ?? -1))[0];
      rateLine = { label: top.t.name, type: top.t.name, price: top.p };
    }
    if (rateLine && (rateLine.price === null || rateLine.price === undefined) && zone?.kind !== 'not_served') flags.push(`No price set for "${rateLine.type}" in ${zone?.name || 'this zone'}`);
    // 3. extras, floors, fuel
    const svcQty = {}; items.filter(i => i.service_id).forEach(i => { svcQty[i.service_id] = (svcQty[i.service_id] || 0) + (i.quantity || 1); });
    const extraRows = extras.map(e => {
      const auto = !!(e.service_id && svcQty[e.service_id]);
      const on = ticked ? ticked.has(e.id) : auto;
      const qty = e.per === 'item' ? (svcQty[e.service_id] || 1) : 1;
      return { id: e.id, name: e.name, unit: Number(e.price) || 0, per: e.per, qty, auto, on, amount: on ? _money((Number(e.price) || 0) * qty) : 0 };
    });
    const fl = Math.max(0, parseInt(floor, 10) || 0);
    const floorAmt = fl >= 1 ? _money((Number(card.floor_first) || 0) + (fl - 1) * (Number(card.floor_extra) || 0)) : 0;
    const base = _money((rateLine?.price || 0) + extraRows.reduce((t, e) => t + e.amount, 0) + floorAmt);
    const fuel = _money(base * (Number(card.fuel_pct) || 0) / 100);
    return { method: 'type_zone', card, zone, outward, area, matched: hit?.prefix || null, zones, rateLine, extras: extraRows,
             floor: fl, floorAmt, base, fuelPct: Number(card.fuel_pct) || 0, fuel, total: _money(base + fuel), flags,
             excludesVat: !!card.prices_exclude_vat };
  }

  // For checking a courier invoice: what a delivery should have cost, from
  // the rate card in force on its dispatch date, using the same rules as
  // "Assign to courier". The weight is the one saved on the shipment, or
  // else the order's box weight. Returns null for couriers without a rate
  // card. Used by Accounts for deliveries sent before the charge was saved.
  async function calcExpectedForShipment({ courierId, orderId, postcode, date, weightKg }) {
    let w = Number(weightKg) || 0;
    if (!w && orderId) {
      const { data: items } = await _rcDb().from('order_items').select('product_id,product_name,sku,quantity').eq('order_id', orderId);
      const sum = await util().getOrderBoxSummary(items || []);
      w = sum?.hasData ? (Number(sum.totalWeightKg) || 0) : 0;
    }
    return _rateCardCharge({ courierId, orderId, postcode, weightKg: w, floor: 0, ticked: null, date });
  }

  // Refreshes the charge panel in "Assign to courier".
  async function refreshCourierCharge(jobId) {
    const panel = document.getElementById('ims-ac-breakdown'); if (!panel) return;
    const st = _acState[jobId] = _acState[jobId] || { ticked: null, touched: false };
    const courierId = document.getElementById('ims-ac-courier')?.value;
    const chargeEl = document.getElementById('ims-ac-charge');
    if (!courierId) { panel.innerHTML = ''; st.calc = null; return; }
    panel.innerHTML = '<div style="font-size:12px;color:var(--ims-text-muted)"><i class="ti ti-loader-2 ims__spin"></i> Working out the charge...</div>';
    const { data: job } = await db().select('logistics_jobs', { eq: { id: jobId }, maybeSingle: true, noCompanyFilter: true });
    const { data: order } = await db().select('orders', { select: 'postcode', eq: { id: job?.order_id }, maybeSingle: true, noCompanyFilter: true });
    const weight = parseFloat(document.getElementById('ims-ac-weight')?.value) || 0;
    const floor = document.getElementById('ims-ac-floor')?.value || 0;
    let calc = await _rateCardCharge({ courierId, orderId: job?.order_id, postcode: order?.postcode || '', weightKg: weight, floor, ticked: st.ticked });
    if (!calc) {
      // A courier priced by weight: the same calculation as on saving, shown first.
      const dims = (document.getElementById('ims-ac-dims')?.value || '').split(/[x×]/i).map(d => parseFloat(d) || 0);
      let total = 0;
      if (weight > 0) {
        const { data: r } = await _rcDb().rpc('calculate_courier_charge', { p_courier_id: courierId, p_weight_kg: weight, p_length_cm: dims[0] || 0, p_width_cm: dims[1] || 0, p_height_cm: dims[2] || 0, p_postcode: order?.postcode || '' });
        total = _money(r?.total || 0);
      }
      calc = { method: 'weight', total, flags: weight > 0 ? [] : ['Enter the weight to work out the charge'] };
    }
    st.calc = calc;
    if (chargeEl && !st.touched) chargeEl.value = calc.total ? calc.total.toFixed(2) : '';
    const fm = n => util().formatCurrency(n);
    const flagsHtml = calc.flags.length ? `<div class="ims__alert ims__alert--warning" style="margin:6px 0"><i class="ti ti-alert-triangle"></i><span>${calc.flags.map(x => util().sanitize(x)).join('<br>')}</span></div>` : '';
    if (calc.method === 'weight') {
      panel.innerHTML = `<div style="font-size:12px">Priced by weight bands: <strong>${calc.total ? fm(calc.total) : '-'}</strong></div>${flagsHtml}`;
      return;
    }
    const moveOpts = calc.zones.filter(z => z.id !== calc.zone?.id).map(z => `<option value="${z.id}">${util().sanitize(z.name)}</option>`).join('');
    panel.innerHTML = `
      <div style="border:1px solid var(--ims-border);border-radius:var(--ims-radius);padding:8px 10px;font-size:12px">
        <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap">
          <span>Zone: <strong>${util().sanitize(calc.zone?.name || '-')}</strong> <span style="color:var(--ims-text-muted)">(${util().sanitize(calc.outward || '-')}${calc.matched ? `, listed as ${util().sanitize(calc.matched)}` : ', not listed: default zone'})</span></span>
          ${_rcAdmin() && calc.outward ? `<span>Wrong zone? <select class="ims__select" id="ims-ac-movezone" style="width:auto;font-size:11px;padding:2px 4px"><option value="">Move ${util().sanitize(calc.outward)} to...</option>${moveOpts}</select> <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_LOGISTICS.moveChargePostcode('${util().jsArg(jobId)}','${util().jsArg(calc.outward)}')">Move</button></span>` : ''}
        </div>
        <table style="width:100%;margin-top:6px;border-collapse:collapse">
          <tr><td>${util().sanitize(calc.rateLine?.label || 'No priced items')}</td><td style="text-align:right">${calc.rateLine?.price != null ? fm(calc.rateLine.price) : '-'}</td></tr>
          ${calc.extras.map(e => `<tr><td><label style="display:flex;gap:6px;align-items:center"><input type="checkbox" ${e.on ? 'checked' : ''} onchange="window.IMS_LOGISTICS.toggleChargeExtra('${util().jsArg(jobId)}','${util().jsArg(e.id)}',this.checked)"> ${util().sanitize(e.name)}${e.qty > 1 ? ` x${e.qty}` : ''}${e.auto ? ' <span class="ims__pill ims__pill--info" style="font-size:10px">on the order</span>' : ''}</label></td><td style="text-align:right">${e.on ? fm(e.amount) : ''}</td></tr>`).join('')}
          ${calc.floor ? `<tr><td>Room of choice: floor ${calc.floor}</td><td style="text-align:right">${fm(calc.floorAmt)}</td></tr>` : ''}
          <tr><td>Fuel ${calc.fuelPct}%</td><td style="text-align:right">${fm(calc.fuel)}</td></tr>
          <tr style="border-top:1px solid var(--ims-border);font-weight:700"><td>Expected charge${calc.excludesVat ? ' (excl. VAT)' : ''}</td><td style="text-align:right">${fm(calc.total)}</td></tr>
        </table>
      </div>${flagsHtml}`;
  }
  function toggleChargeExtra(jobId, extraId, on) {
    const st = _acState[jobId]; if (!st?.calc?.extras) return;
    st.ticked = new Set(st.calc.extras.filter(e => e.on).map(e => e.id));
    on ? st.ticked.add(extraId) : st.ticked.delete(extraId);
    refreshCourierCharge(jobId);
  }
  function chargeEdited(jobId) { (_acState[jobId] = _acState[jobId] || {}).touched = true; }
  // "Wrong zone?": move this postcode district for this courier, then recalculate.
  async function moveChargePostcode(jobId, outward) {
    if (!_rcAdmin()) { ui().toast('Only an Admin can change courier zones', 'error'); return; }
    const zoneId = document.getElementById('ims-ac-movezone')?.value;
    if (!zoneId) { ui().toast('Choose the zone to move it to', 'warning'); return; }
    const courierId = document.getElementById('ims-ac-courier')?.value;
    const db_ = _rcDb();
    const { data: ex } = await db_.from('courier_zone_postcodes').select('id').eq('courier_id', courierId).eq('prefix', outward).maybeSingle();
    const res = ex ? await db_.from('courier_zone_postcodes').update({ zone_id: zoneId }).eq('id', ex.id)
                   : await db_.from('courier_zone_postcodes').insert({ company_id: window.SM.AUTH.company?.id, courier_id: courierId, zone_id: zoneId, prefix: outward });
    if (res.error) { ui().toast('Not moved: ' + res.error.message, 'error', 8000); return; }
    ui().toast(`${outward} moved for this courier; the charge has been recalculated`, 'success');
    refreshCourierCharge(jobId);
  }

  async function assignJobCourier(jobId){
    const [{data:couriers},{data:job}]=await Promise.all([
      db().select('couriers',{eq:{is_active:true},order:{col:'name'}}),
      db().select('logistics_jobs',{eq:{id:jobId},maybeSingle:true,noCompanyFilter:true})
    ]);
    const courierOpts=(couriers||[]).map(c=>`<option value="${c.id}">${util().sanitize(c.name)}</option>`).join('');

    // Auto-fetch real weight/dimensions from the product catalogue's
    // packaging (box) data via the shared UTIL.getOrderBoxSummary()
    // helper, so staff don't have to look this up and type it in
    // manually every time. Multiple boxes can't be meaningfully summed
    // into one L×W×H, so in that case only weight is auto-filled and a
    // per-box breakdown is shown as a hint instead of a fabricated
    // combined dimension.
    let autoWeight='', autoDims='', boxHint='';
    if(job?.order_id){
      const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
      const {data:orderItems}=await client.from('order_items')
        .select('product_id,product_name,sku,quantity').eq('order_id',job.order_id);
      const summary=await util().getOrderBoxSummary(orderItems||[]);
      if(summary.hasData){
        if(summary.totalWeightKg>0) autoWeight=summary.totalWeightKg;
        if(summary.totalBoxes===1){
          const onlyBox=summary.lines.find(l=>l.boxes.length)?.boxes[0];
          if(onlyBox) autoDims=`${onlyBox.length_cm||''}x${onlyBox.width_cm||''}x${onlyBox.height_cm||''}`;
        } else if(summary.totalBoxes>1){
          const breakdown=summary.lines.flatMap(l=>l.boxes.map(b=>`${b.length_cm||'?'}×${b.width_cm||'?'}×${b.height_cm||'?'}cm`)).join(', ');
          boxHint=`<span class="ims__input-hint">${summary.totalBoxes} boxes total — enter combined or largest box dimensions. Breakdown: ${util().sanitize(breakdown)}</span>`;
        }
      }
    }

    delete _acState[jobId];
    const R = `window.IMS_LOGISTICS.refreshCourierCharge('${util().jsArg(jobId)}')`;
    ui().openModal('Assign to courier',`
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full"><label class="ims__label ims__label--req">Courier</label><select class="ims__select" id="ims-ac-courier" onchange="${R}"><option value="">Select courier</option>${courierOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Tracking number</label><input class="ims__input" type="text" id="ims-ac-tracking" placeholder="Enter tracking number"></div>
        <div class="ims__form-group"><label class="ims__label">Courier order no.</label><input class="ims__input" type="text" id="ims-ac-corder" placeholder="Optional, e.g. VDI242546"></div>
        <div class="ims__form-group"><label class="ims__label">Weight (kg)</label><input class="ims__input" type="number" step="0.1" id="ims-ac-weight" placeholder="Parcel weight" value="${autoWeight}" onchange="${R}"></div>
        <div class="ims__form-group"><label class="ims__label">Dimensions (L×W×H cm)</label><input class="ims__input" type="text" id="ims-ac-dims" placeholder="e.g. 60x50x40" value="${autoDims}" onchange="${R}">${boxHint}</div>
        <div class="ims__form-group"><label class="ims__label">Room of choice: floor</label><input class="ims__input" type="number" min="0" step="1" id="ims-ac-floor" value="0" title="0 = ground floor / doorstep" onchange="${R}"></div>
        <div class="ims__form-group"><label class="ims__label">Charge (£)</label><input class="ims__input" type="number" min="0" step="0.01" id="ims-ac-charge" placeholder="Worked out automatically" oninput="window.IMS_LOGISTICS.chargeEdited('${util().jsArg(jobId)}')"></div>
        <div class="ims__form-group ims__form-group--full" id="ims-ac-breakdown"></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.confirmCourierAssignment('${util().jsArg(jobId)}')"><i class="ti ti-check"></i> Assign &amp; dispatch</button>
    `,'');
  }

  async function confirmCourierAssignment(jobId){
    const courierId=document.getElementById('ims-ac-courier')?.value;
    const tracking=document.getElementById('ims-ac-tracking')?.value?.trim();
    if(!courierId){ui().toast('Please select a courier','error');return;}
    if(!tracking){ui().toast('Tracking number is required','error');return;}
    const {data:job}=await db().select('logistics_jobs',{eq:{id:jobId},maybeSingle:true,noCompanyFilter:true});
    if(!job){ui().toast('Job not found','error');return;}
    let chargeAmount=0;
    const weight=parseFloat(document.getElementById('ims-ac-weight')?.value)||0;
    const dimsRaw=document.getElementById('ims-ac-dims')?.value||'';
    const dims=dimsRaw.split(/[x×]/i).map(d=>parseFloat(d)||0);
    // The charge box (worked out automatically, or changed by staff) is
    // what is saved. If it is empty, the charge is worked out as before.
    const typedCharge=document.getElementById('ims-ac-charge')?.value;
    const st=_acState[jobId]||{}; const calc=st.calc||null;
    if(typedCharge!==undefined&&typedCharge!==''&&!isNaN(parseFloat(typedCharge))){
      chargeAmount=_money(parseFloat(typedCharge));
    } else if(calc?.total){
      chargeAmount=calc.total;
    } else if(weight>0&&courierId){
      const {data:order}=await db().select('orders',{eq:{id:job.order_id},maybeSingle:true,noCompanyFilter:true,select:'postcode'});
      const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
      const {data:chargeResult}=await client.rpc('calculate_courier_charge',{
        p_courier_id:courierId,p_weight_kg:weight,
        p_length_cm:dims[0]||0,p_width_cm:dims[1]||0,p_height_cm:dims[2]||0,
        p_postcode:order?.postcode||''
      });
      if(chargeResult?.total) chargeAmount=chargeResult.total;
    }
    await db().insert('shipments',{
      order_id:job.order_id,courier_id:courierId,tracking_number:tracking,
      ship_mode:'courier',status:'dispatched',
      dispatched_at:new Date().toISOString(),
      weight_actual:weight||null,
      dimensions_cm:dimsRaw||null,
      charge_amount:chargeAmount,
      courier_order_ref:document.getElementById('ims-ac-corder')?.value?.trim()||null,
      expected_charge:calc?.total??null,
      charge_breakdown:calc?(calc.method==='type_zone'?{method:'type_zone',zone:calc.zone?.name||null,outward:calc.outward,rate:calc.rateLine,extras:calc.extras.filter(e=>e.on).map(e=>({name:e.name,qty:e.qty,amount:e.amount})),floor:calc.floor,floor_amount:calc.floorAmt,base:calc.base,fuel_pct:calc.fuelPct,fuel:calc.fuel,total:calc.total,flags:calc.flags}:{method:'weight',total:calc.total}):null,
      rate_card_id:calc?.card?.id||null,
      zone_name:calc?.zone?.name||null
    });
    await db().update('logistics_jobs',jobId,{status:'assigned_courier',assigned_courier_id:courierId});
    if(job.order_id){
      const { data: ord } = await db().select('orders',{select:'status',eq:{id:job.order_id},maybeSingle:true,noCompanyFilter:true});
      await db().update('orders',job.order_id,{status:'dispatched',dispatched_at:new Date().toISOString()});
      await logActivity(job.order_id, ord?.status||'packed', 'dispatched', 'dispatched', `Courier: tracking ${tracking}`+(calc?.total!=null&&Math.abs(calc.total-chargeAmount)>0.004?`. Charge changed from the expected ${util().formatCurrency(calc.total)} to ${util().formatCurrency(chargeAmount)}`:''));
    }
    if(chargeAmount>0){
      await db().insert('transactions',{
        date:new Date().toISOString().split('T')[0],
        type:'expense',category:'Courier charges',
        description:`Courier charge: ${tracking}`,
        amount:chargeAmount,reference:tracking
      });
    }
    ui().closeModal();
    ui().toast(`Dispatched via courier. Tracking: ${tracking}`+(chargeAmount>0?` | Charge: ${util().formatCurrency(chargeAmount)}`:''),'success');
    window.SM.EMAIL.send(job.order_id, 'dispatched');
    refreshTabBadges();
    loadUnassigned();
  }

  async function assignOwnTruck(jobId,orderId){
    ui().openModal('Add to own truck queue',`
      <p style="font-size:13px;color:var(--ims-text-sec);margin-bottom:12px">This order will be added to your own truck delivery queue for route planning.</p>
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label">Planned delivery date</label>
          <input class="ims__input" type="date" id="ims-owntruck-date" value="${new Date().toISOString().split('T')[0]}"></div>
        <div class="ims__form-group"><label class="ims__label">Notes</label>
          <input class="ims__input" type="text" id="ims-owntruck-notes" placeholder="e.g. Call before delivery"></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.confirmOwnTruck('${util().jsArg(jobId)}','${util().jsArg(orderId||'')}')"><i class="ti ti-check"></i> Add to queue</button>
    `,'sm');
  }

  async function confirmOwnTruck(jobId,orderId){
    const date=document.getElementById('ims-owntruck-date')?.value;
    const notes=document.getElementById('ims-owntruck-notes')?.value?.trim();
    await db().update('logistics_jobs',jobId,{job_type:'own_truck',status:'scheduled',notes:notes||null});
    if(orderId){
      // Deliberately does NOT mark the order dispatched here — this only
      // moves it to the Own Truck Queue. It becomes genuinely dispatched
      // once a real manifest is created from that queue, the same point
      // every other delivery path marks an order dispatched.
      const { data: ord } = await db().select('orders',{select:'status',eq:{id:orderId},maybeSingle:true,noCompanyFilter:true});
      await logActivity(orderId, ord?.status||'packed', ord?.status||'packed', 'queued_own_truck', 'Queued for own-truck delivery — awaiting manifest');
    }
    ui().closeModal();
    ui().toast('Order moved to Own Truck Queue — awaiting manifest','success');
    loadUnassigned();
  }

  // ─── OWN TRUCK QUEUE ─────────────────────────────────────────────
  // Orders marked "We deliver" land here (logistics_jobs.status =
  // 'scheduled', job_type = 'own_truck') instead of remaining visible in
  // Unassigned Jobs or Tracking — they are not yet dispatched, and stay
  // off both screens until a real manifest is actually created for them.
  // Sends queued orders back to Unassigned jobs, where the delivery
  // decision can be changed (for example to a courier or collection).
  // One order (jobId), or every order ticked in the queue.
  async function backToUnassigned(jobId){
    const ids=jobId?[jobId]:[...document.querySelectorAll('.ims-otq-check:checked')].map(cb=>cb.value);
    if(!ids.length){ui().toast('Please select at least one order','warning');return;}
    ui().confirm(`Send ${ids.length===1?'this order':ids.length+' orders'} back to Unassigned jobs? You can then choose another way to send ${ids.length===1?'it':'them'}, for example a courier.`, async()=>{
      const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
      // Only orders still waiting in the queue (not already on a manifest).
      const {data:moved,error}=await client.from('logistics_jobs')
        .update({status:'unassigned',job_type:'delivery'})
        .in('id',ids).eq('status','scheduled').eq('job_type','own_truck').eq('is_return_pickup',false).is('manifest_id',null).select('id,order_id');   // return pick-ups stay on the van
      if(error){ui().toast('Failed: '+error.message,'error');return;}
      for(const j of (moved||[])) if(j.order_id) await logActivity(j.order_id,'','','back_to_unassigned','Moved from the Own Truck Queue back to Unassigned jobs');
      ui().toast(`${(moved||[]).length} order(s) back in Unassigned jobs`,'success');
      loadOwnTruckQueue();
    },'Back to Unassigned',false);
  }

  async function loadOwnTruckQueue(){
    const content = document.getElementById('ims-log-content');
    if(!content) return;
    content.innerHTML = `<div class="ims__loading"><i class="ti ti-loader-2 ims__spin"></i></div>`;

    const { data: jobs } = await db().select('logistics_jobs', {
      select: 'id,job_type,notes,created_at,is_return_pickup,orders(id,order_number,customer_name,postcode,address,total_value)',
      eq: { status: 'scheduled', job_type: 'own_truck' },
      order: { col: 'created_at', asc: true }
    });
    const items = jobs || [];

    if(!items.length){
      content.innerHTML = `<div class="ims__empty"><i class="ti ti-truck"></i><p>No orders queued for own-truck delivery</p><p style="font-size:12px;color:var(--ims-text-muted);margin-top:6px">Orders marked "We deliver" from Unassigned Jobs appear here, waiting to be placed on a manifest</p></div>`;
      return;
    }

    content.innerHTML = `
      <div class="ims__alert ims__alert--info" style="margin-bottom:12px">
        <i class="ti ti-info-circle"></i>
        <span>These orders are marked for your own fleet but are not yet dispatched — they stay here, and stay off Tracking, until a real manifest is created for them.</span>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
        <strong style="font-size:13px">${items.length} order${items.length===1?'':'s'} queued</strong>
        <div class="ims__btn-group">
          <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.backToUnassigned()" title="Send the selected orders back to Unassigned jobs, to choose another way (for example a courier)"><i class="ti ti-arrow-back-up"></i> Back to Unassigned</button>
          <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_LOGISTICS.planRouteFromQueue()"><i class="ti ti-route"></i> Create manifest from selected</button>
        </div>
      </div>
      <table class="ims__table">
        <thead><tr>
          <th><input type="checkbox" id="ims-otq-all" onchange="document.querySelectorAll('.ims-otq-check').forEach(c=>c.checked=this.checked)"></th>
          <th>Order #</th><th>Customer</th><th>Postcode</th><th>Value</th><th>Notes</th><th>Queued</th><th></th>
        </tr></thead>
        <tbody>
          ${items.map(j => `<tr>
            <td><input type="checkbox" class="ims-otq-check" value="${j.id}"></td>
            <td><span class="ims__link" onclick="window.IMS_ORDERS?.openOrder('${util().jsArg(j.orders?.id||'')}')">${util().sanitize(j.orders?.order_number||'—')}</span>${j.is_return_pickup?' <span class="ims__pill ims__pill--warning" style="font-size:10px">Return pick-up</span>':''}</td>
            <td>${util().sanitize(j.orders?.customer_name||'—')}</td>
            <td>${util().sanitize(j.orders?.postcode||'—')}</td>
            <td>${util().formatCurrency(j.orders?.total_value)}</td>
            <td style="font-size:12px;color:var(--ims-text-muted)">${util().sanitize(j.notes||'—')}</td>
            <td style="font-size:11px;color:var(--ims-text-muted)">${util().formatDate(j.created_at)}</td>
            <td>${j.is_return_pickup
              ? `<button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_LOGISTICS.cancelPickup('${util().jsArg(j.id)}')" title="Cancel this van pick-up"><i class="ti ti-x"></i></button>`
              : `<button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_LOGISTICS.backToUnassigned('${util().jsArg(j.id)}')" title="Back to Unassigned jobs, to choose another way"><i class="ti ti-arrow-back-up"></i></button>`}</td>
          </tr>`).join('')}
        </tbody>
      </table>
    `;
  }

  // Mirrors planRoute() exactly (same modal, same fields, same footer
  // calling the existing createManifest() unchanged) — only the source
  // of eligible jobs differs: the Own Truck Queue instead of Unassigned
  // Jobs. createManifest() itself needed no changes at all, since it
  // never assumes anything about a job's prior status, it just processes
  // whatever was checked.
  async function planRouteFromQueue(){
    const checked=[...document.querySelectorAll('.ims-otq-check:checked')].map(cb=>cb.value);
    if(!checked.length){ui().toast('Please select at least one order','warning');return;}
    const {data:jobs}=await db().select('logistics_jobs',{
      select:'id,job_type,is_return_pickup,orders(id,order_number,customer_name,postcode,address,total_value)',
      eq:{status:'scheduled',job_type:'own_truck'},order:{col:'created_at'}
    });
    const available=(jobs||[]).filter(j=>checked.includes(j.id));
    if(!available.length){ui().toast('Selected orders are no longer available','warning');loadOwnTruckQueue();return;}
    const {data:drivers}=await db().select('drivers',{eq:{is_active:true}});
    const {data:vehicles}=await db().select('vehicles',{eq:{is_active:true}});
    const {data:routes}=await db().select('routes',{eq:{is_active:true}});
    const driverOpts=(drivers||[]).map(d=>`<option value="${d.id}">${util().sanitize(d.name)}</option>`).join('');
    const vehicleOpts=(vehicles||[]).map(v=>`<option value="${v.id}">${util().sanitize(v.registration)} — ${util().sanitize(v.make_model||v.vehicle_type||'')}</option>`).join('');
    const routeOpts=(routes||[]).map(r=>`<option value="${r.id}">${util().sanitize(r.name)}</option>`).join('');
    ui().openModal('Plan delivery route',`
      <div class="ims__form-grid" style="margin-bottom:14px">
        <div class="ims__form-group"><label class="ims__label">Driver</label><select class="ims__select" id="ims-rt-driver"><option value="">Select driver</option>${driverOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Vehicle</label><select class="ims__select" id="ims-rt-vehicle"><option value="">Select vehicle</option>${vehicleOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Route</label><select class="ims__select" id="ims-rt-route"><option value="">None</option>${routeOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Dispatch date</label><input class="ims__input" type="date" id="ims-rt-date" value="${new Date().toISOString().split('T')[0]}"></div>
      </div>
      <strong style="font-size:13px;display:block;margin-bottom:8px">Select orders for this route</strong>
      <div style="max-height:300px;overflow-y:auto">
        <table class="ims__table">
          <thead><tr><th><input type="checkbox" id="ims-rt-all" onchange="document.querySelectorAll('.ims-rt-check').forEach(c=>c.checked=this.checked)"></th><th>Order #</th><th>Customer</th><th>Postcode</th><th>Type</th></tr></thead>
          <tbody>${available.map(j=>`<tr>
            <td><input type="checkbox" class="ims-rt-check" value="${j.id}" checked></td>
            <td>${util().sanitize(j.orders?.order_number||'—')}</td>
            <td>${util().sanitize(j.orders?.customer_name||'—')}</td>
            <td>${util().sanitize(j.orders?.postcode||'—')}</td>
            <td><span class="ims__pill ims__pill--neutral">Own truck</span></td>
          </tr>`).join('')}
          </tbody>
        </table>
      </div>
      <div class="ims__alert ims__alert--info" style="margin-top:12px">
        <i class="ti ti-info-circle"></i>
        <span>Loading order is automatically reversed from delivery sequence (LIFO). Last delivery loaded first into truck.</span>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.createManifest()"><i class="ti ti-check"></i> Create manifest</button>
    `,'xl');
  }

  async function markSelfCollection(jobId,orderId){
    const slot=prompt('Collection slot (optional, e.g. 2026-05-25 14:00):','');
    await db().update('logistics_jobs',jobId,{
      job_type:'self_collection',status:'unassigned',
      collection_slot:slot?new Date(slot).toISOString():null
    });
    if(orderId){
      const { data: ord } = await db().select('orders',{select:'status',eq:{id:orderId},maybeSingle:true,noCompanyFilter:true});
      await db().update('orders',orderId,{status:'dispatched',dispatched_at:new Date().toISOString()});
      await logActivity(orderId, ord?.status||'packed', 'dispatched', 'dispatched', 'Marked for customer self-collection');
    }
    ui().toast('Marked for customer self-collection','success');
    loadUnassigned();
  }

  async function cancelSelfCollection(jobId,orderId){
    ui().confirm('Move this order back to unassigned deliveries? The order status will revert to Packed.',async()=>{
      await db().update('logistics_jobs',jobId,{job_type:'delivery',status:'unassigned',collection_slot:null});
      if(orderId){
        const { data: ord } = await db().select('orders',{select:'status',eq:{id:orderId},maybeSingle:true,noCompanyFilter:true});
        if(ord?.status === 'dispatched'){
          await db().update('orders',orderId,{status:'packed',dispatched_at:null});
          await logActivity(orderId, 'dispatched', 'packed', 'status_update', 'Self-collection undone — moved back to deliveries queue');
        }
      }
      ui().toast('Order moved back to deliveries queue','success');
      loadUnassigned();
    },'Undo self-collection',false);
  }

  async function markCollected(jobId,orderId){
    await db().update('logistics_jobs',jobId,{status:'collected'});
    if(orderId){
      const { data: ord } = await db().select('orders',{
        select:'status,order_type,customer_id,total_value,order_number,payment_status',
        eq:{id:orderId},maybeSingle:true,noCompanyFilter:true
      });
      await db().update('orders',orderId,{status:'delivered',delivered_at:new Date().toISOString()});
      await logActivity(orderId, ord?.status||'dispatched', 'delivered', 'delivered', 'Customer self-collected');
      await deductStockOnDelivery(orderId, ord?.order_number||'');

      if(ord?.order_type === 'wholesale' && ord?.customer_id){
        const { data: existing } = await db().select('accounts_receivable',{
          eq:{ order_id: orderId }, maybeSingle:true, noCompanyFilter:true
        });
        if(!existing){
          const { data: cust } = await db().select('customers',{select:'credit_terms',eq:{id:ord.customer_id},maybeSingle:true,noCompanyFilter:true});
          const dueDate = util().resolveReceivableDueDate(ord.payment_status, cust?.credit_terms);
          if(dueDate){
            await db().insert('accounts_receivable',{
              order_id:    orderId,
              customer_id: ord.customer_id,
              amount:      ord.total_value || 0,
              paid_amount: 0,
              status:      'unpaid',
              invoice_ref: ord.order_number,
              due_date:    dueDate
            });
            ui().toast('Order marked as collected — receivable created in Accounts','success',5000);
          } else {
            ui().toast('Order marked as collected and delivered','success');
          }
        } else {
          ui().toast('Order marked as collected and delivered','success');
        }
      } else {
        ui().toast('Order marked as collected and delivered','success');
      }
    } else {
      ui().toast('Order marked as collected and delivered','success');
    }
    loadUnassigned();
  }

  // ─── SELF-COLLECTION CONFIRMATION ────────────────────────────────
  // markCollected() above previously ran silently with no confirmation
  // step at all — this adds a proper modal asking who actually
  // collected the order before processing it, matching the equivalent
  // dropship collection flow.
  function showSelfCollectionModal(jobId, orderId, orderNumber){
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().slice(0,5);
    ui().openModal(`Confirm collection: ${orderNumber}`,`
      <div class="ims__alert ims__alert--info" style="margin-bottom:16px">
        <i class="ti ti-home"></i>
        <span>Confirm the customer (or their representative) has collected this order. This will mark the order as delivered.</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label ims__label--req">Collected by</label>
          <input class="ims__input" type="text" id="ims-sc-by" placeholder="e.g. John Smith (customer) or driver name">
        </div>
        <div class="ims__form-group">
          <label class="ims__label">Vehicle registration</label>
          <input class="ims__input" type="text" id="ims-sc-vehicle" placeholder="e.g. AB12 CDE" style="text-transform:uppercase">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Collection date</label>
          <input class="ims__input" type="date" id="ims-sc-date" value="${dateStr}">
        </div>
        <div class="ims__form-group">
          <label class="ims__label ims__label--req">Collection time</label>
          <input class="ims__input" type="time" id="ims-sc-time" value="${timeStr}">
        </div>
        <div class="ims__form-group ims__form-group--full">
          <label class="ims__label">Notes</label>
          <input class="ims__input" type="text" id="ims-sc-notes" placeholder="e.g. ID checked, signed for goods">
        </div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success" onclick="window.IMS_LOGISTICS.confirmMarkCollected('${util().jsArg(jobId)}','${util().jsArg(orderId)}','${util().jsArg(orderNumber)}')">
        <i class="ti ti-circle-check"></i> Confirm collected
      </button>
    `,'sm');
    setTimeout(()=>document.getElementById('ims-sc-by')?.focus(),150);
  }

  async function confirmMarkCollected(jobId, orderId, orderNumber){
    const collectedBy = document.getElementById('ims-sc-by')?.value?.trim();
    const vehicle = document.getElementById('ims-sc-vehicle')?.value?.trim().toUpperCase();
    const dateVal = document.getElementById('ims-sc-date')?.value;
    const timeVal = document.getElementById('ims-sc-time')?.value || '00:00';
    const notes = document.getElementById('ims-sc-notes')?.value?.trim();

    if(!collectedBy){ ui().toast('Please enter who collected the order','error'); return; }
    if(!dateVal){ ui().toast('Please enter the collection date','error'); return; }

    if(orderId && await _isAlreadyDelivered(orderId)){
      ui().toast(`Order is already marked delivered — no duplicate actions taken.`,'warning',5000);
      ui().closeModal();
      loadUnassigned();
      return;
    }

    const collectedAt = new Date(`${dateVal}T${timeVal}:00`).toISOString();

    await db().update('logistics_jobs',jobId,{status:'collected'});
    if(orderId){
      const { data: ord } = await db().select('orders',{
        select:'status,order_type,customer_id,total_value,order_number,payment_status',
        eq:{id:orderId},maybeSingle:true,noCompanyFilter:true
      });
      await db().update('orders',orderId,{
        status:'delivered',
        delivered_at:new Date().toISOString(),
        collected_by:collectedBy,
        collection_vehicle:vehicle||null,
        collected_at:collectedAt,
        collection_notes:notes||null
      });
      await logActivity(orderId, ord?.status||'dispatched', 'delivered', 'delivered', `Customer self-collected — collected by ${collectedBy}`);
      await deductStockOnDelivery(orderId, ord?.order_number||orderNumber||'');

      if(ord?.order_type === 'wholesale' && ord?.customer_id){
        const { data: existing } = await db().select('accounts_receivable',{
          eq:{ order_id: orderId }, maybeSingle:true, noCompanyFilter:true
        });
        if(!existing){
          const { data: cust } = await db().select('customers',{select:'credit_terms',eq:{id:ord.customer_id},maybeSingle:true,noCompanyFilter:true});
          const dueDate = util().resolveReceivableDueDate(ord.payment_status, cust?.credit_terms);
          if(dueDate){
            await db().insert('accounts_receivable',{
              order_id:    orderId,
              customer_id: ord.customer_id,
              amount:      ord.total_value || 0,
              paid_amount: 0,
              status:      'unpaid',
              invoice_ref: ord.order_number,
              due_date:    dueDate
            });
          }
        }
      }
    }
    ui().closeModal();
    ui().toast(`Order collected by ${collectedBy}`,'success',5000);
    loadUnassigned();
  }
  async function cancelUnassignedOrder(jobId,orderId,orderNumber){
    ui().confirm(`Cancel order <strong>${util().sanitize(orderNumber)}</strong>?<br><br>Stock reservations will be released and it will be removed from Logistics.`, async () => {
      const { data: ord } = await db().select('orders', {
        select: 'status,warehouse_id,fulfillment_type,order_items(product_id,product_name,quantity)', eq: { id: orderId }, maybeSingle: true, noCompanyFilter: true
      });
      // Dropship orders hold the partner's allocation, not normal
      // reservations, and may carry partner charges. They are cancelled or
      // deleted from the Dropship screen, which handles both.
      if (ord?.fulfillment_type === 'dropship') {
        ui().toast(`${orderNumber} is a dropship order. Cancel or delete it from the Dropship screen, so the partner's allocation and charges are handled.`, 'warning', 8000);
        return;
      }
      if (!['confirmed','picking','packed'].includes(ord?.status)) {
        ui().toast('This order can no longer be cancelled from here', 'error');
        return;
      }
      const items = ord?.order_items || [];
      const whId = ord?.warehouse_id;
      // Release each line's reservation one at a time, in the order's own
      // warehouse. (All at once, two lines of the same product both read
      // the same figure and one release was lost; and with no warehouse
      // set, it used to guess one.)
      const problems = [];
      if (!whId && items.some(i => i.product_id)) {
        problems.push('the order has no warehouse set');
      } else {
        for (const item of items) {
          if (!item.product_id) continue;
          const { data: sl } = await db().select('stock_levels', { eq: { product_id: item.product_id, warehouse_id: whId }, maybeSingle: true, noCompanyFilter: true });
          if (!sl) { problems.push(item.product_name || item.product_id); continue; }
          const { error } = await db().update('stock_levels', sl.id, { reserved_qty: Math.max(0, (sl.reserved_qty||0) - (item.quantity||0)) });
          if (error) problems.push(item.product_name || item.product_id);
        }
      }
      await db().remove('logistics_jobs', jobId);
      await db().update('orders', orderId, { status: 'cancelled' });
      await logActivity(orderId, 'Order cancelled', ord?.status||'', 'cancelled', 'Cancelled from Logistics (unassigned job)');
      if (problems.length) {
        ui().toast(`${orderNumber} cancelled, but stock reservations could not be released for: ${problems.join(', ')}. Please check stock levels.`, 'warning', 10000);
      } else {
        ui().toast('Order cancelled: stock released', 'success');
      }
      loadUnassigned();
    }, 'Cancel order', true);
  }

  async function bulkAssignCourier(){
    const checked=[...document.querySelectorAll('.ims-job-check:checked')].map(cb=>cb.value);
    if(!checked.length){ui().toast('Please select at least one order','warning');return;}
    const {data:couriers}=await db().select('couriers',{eq:{is_active:true},order:{col:'name'}});
    const courierOpts=(couriers||[]).map(c=>`<option value="${c.id}">${util().sanitize(c.name)}</option>`).join('');
    ui().openModal(`Assign ${checked.length} order(s) to courier`,`
      <div class="ims__form-group"><label class="ims__label ims__label--req">Courier</label><select class="ims__select" id="ims-bulk-courier"><option value="">Select courier</option>${courierOpts}</select></div>
      <div class="ims__alert ims__alert--info" style="margin-top:12px"><i class="ti ti-info-circle"></i><span>Tracking numbers will need to be entered individually for each shipment after bulk assignment.</span></div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.confirmBulkAssign(${JSON.stringify(checked).replace(/"/g,'&quot;')})"><i class="ti ti-check"></i> Assign</button>
    `,'sm');
  }

  async function confirmBulkAssign(jobIds){
    const courierId=document.getElementById('ims-bulk-courier')?.value;
    if(!courierId){ui().toast('Please select a courier','error');return;}
    await Promise.all(jobIds.map(async jobId=>{
      const {data:job}=await db().select('logistics_jobs',{eq:{id:jobId},maybeSingle:true,noCompanyFilter:true});
      await db().update('logistics_jobs',jobId,{status:'assigned_courier',assigned_courier_id:courierId});
      if(job?.order_id){
        const { data: ord } = await db().select('orders',{select:'status',eq:{id:job.order_id},maybeSingle:true,noCompanyFilter:true});
        await db().update('orders',job.order_id,{status:'dispatched',dispatched_at:new Date().toISOString()});
        await logActivity(job.order_id, ord?.status||'packed', 'dispatched', 'dispatched', 'Bulk courier assignment');
      }
    }));
    ui().closeModal();
    ui().toast(`${jobIds.length} jobs assigned to courier`,'success');
    loadUnassigned();
  }

  // ─── ROUTE PLANNING ──────────────────────────────────────────────
  // Delivery workflow, one way: Unassigned jobs (where the delivery
  // decision is made: we deliver, courier or collection) > "We deliver" >
  // Own Truck Queue > manifest. Every way of building or changing a
  // manifest takes orders only from the Own Truck Queue, and orders taken
  // off a manifest go back to the queue. "Back to Unassigned" in the queue
  // is the way to change the decision (for example to use a courier).
  async function planRoute(){
    // Anything ticked in the Own Truck Queue starts ticked here.
    const checked=[...document.querySelectorAll('.ims-otq-check:checked')].map(cb=>cb.value);
    const {data:jobs}=await db().select('logistics_jobs',{
      select:'id,job_type,is_return_pickup,orders(id,order_number,customer_name,postcode,address,total_value)',
      eq:{status:'scheduled',job_type:'own_truck'},order:{col:'created_at'}
    });
    const available=jobs||[];
    if(!available.length){ui().toast('Nothing in the Own Truck Queue yet. Use "We deliver" on Unassigned jobs first.','warning',7000);return;}
    const {data:drivers}=await db().select('drivers',{eq:{is_active:true}});
    const {data:vehicles}=await db().select('vehicles',{eq:{is_active:true}});
    const {data:routes}=await db().select('routes',{eq:{is_active:true}});
    const driverOpts=(drivers||[]).map(d=>`<option value="${d.id}">${util().sanitize(d.name)}</option>`).join('');
    const vehicleOpts=(vehicles||[]).map(v=>`<option value="${v.id}">${util().sanitize(v.registration)} — ${util().sanitize(v.make_model||v.vehicle_type||'')}</option>`).join('');
    const routeOpts=(routes||[]).map(r=>`<option value="${r.id}">${util().sanitize(r.name)}</option>`).join('');
    const selectedIds=new Set(checked);
    ui().openModal('Plan delivery route',`
      <div class="ims__form-grid" style="margin-bottom:14px">
        <div class="ims__form-group"><label class="ims__label">Driver</label><select class="ims__select" id="ims-rt-driver"><option value="">Select driver</option>${driverOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Vehicle</label><select class="ims__select" id="ims-rt-vehicle"><option value="">Select vehicle</option>${vehicleOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Route</label><select class="ims__select" id="ims-rt-route"><option value="">None</option>${routeOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Dispatch date</label><input class="ims__input" type="date" id="ims-rt-date" value="${new Date().toISOString().split('T')[0]}"></div>
      </div>
      <strong style="font-size:13px;display:block;margin-bottom:8px">Select orders for this route</strong>
      <div style="max-height:300px;overflow-y:auto">
        <table class="ims__table">
          <thead><tr><th><input type="checkbox" id="ims-rt-all" onchange="document.querySelectorAll('.ims-rt-check').forEach(c=>c.checked=this.checked)"></th><th>Order #</th><th>Customer</th><th>Postcode</th><th>Type</th></tr></thead>
          <tbody>${available.map(j=>`<tr>
            <td><input type="checkbox" class="ims-rt-check" value="${j.id}" ${selectedIds.has(j.id)?'checked':''}></td>
            <td>${util().sanitize(j.orders?.order_number||'—')}</td>
            <td>${util().sanitize(j.orders?.customer_name||'—')}</td>
            <td>${util().sanitize(j.orders?.postcode||'—')}</td>
            <td><span class="ims__pill ims__pill--${j.job_type==='delivery'?'info':'neutral'}">${j.job_type==='delivery'?'Delivery':'Collection'}</span></td>
          </tr>`).join('')}
          </tbody>
        </table>
      </div>
      <div class="ims__alert ims__alert--info" style="margin-top:12px">
        <i class="ti ti-info-circle"></i>
        <span>Loading order is automatically reversed from delivery sequence (LIFO). Last delivery loaded first into truck.</span>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.createManifest()"><i class="ti ti-check"></i> Create manifest</button>
    `,'xl');
  }

  async function createManifest(){
    const selected=[...document.querySelectorAll('.ims-rt-check:checked')].map(cb=>cb.value);
    if(!selected.length){ui().toast('Please select at least one order','error');return;}
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const driverId=g('ims-rt-driver')||null;
    const vehicleId=g('ims-rt-vehicle')||null;
    const routeId=g('ims-rt-route')||null;
    const dispatchDate=g('ims-rt-date');

    // Only orders in the Own Truck Queue can go on a manifest. Checked
    // before the manifest is created, so it is never made for nothing.
    const {data:queued}=await db().select('logistics_jobs',{
      select:'id', in:{id:selected}, eq:{status:'scheduled',job_type:'own_truck'}, noCompanyFilter:true
    });
    const eligible=(queued||[]).map(j=>j.id);
    if(!eligible.length){ui().toast('The selected orders are no longer in the Own Truck Queue. Nothing was created.','warning',7000);loadOwnTruckQueue();return;}
    if(eligible.length<selected.length) ui().toast(`${selected.length-eligible.length} selected order(s) are no longer in the Own Truck Queue and were left out.`,'warning',7000);

    const {data:lastMF}=await db().select('manifests',{select:'manifest_number',order:{col:'created_at',asc:false},limit:1});
    const manifestNumber=util().generateRef('MF',lastMF?.[0]?.manifest_number);

    const {data:manifest,error}=await db().insert('manifests',{
      manifest_number:manifestNumber,driver_id:driverId,vehicle_id:vehicleId,route_id:routeId,
      status:'dispatched',dispatched_at:new Date(dispatchDate||Date.now()).toISOString()
    });
    if(error){ui().toast('Failed: '+error.message,'error');return;}

    const {data:jobs}=await db().select('logistics_jobs',{
      select:'id,job_type,order_id,orders(postcode,order_number,order_type,customer_id,total_value)',
      in:{id:eligible},noCompanyFilter:true
    });
    const sorted=(jobs||[]).sort((a,b)=>(a.orders?.postcode||'').localeCompare(b.orders?.postcode||''));
    const loadOrder=sorted.length;

    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    await Promise.all(sorted.map(async(job,i)=>{
      const loadSeq=loadOrder-i;
      await client.from('manifest_items').insert({
        manifest_id:manifest.id,logistics_job_id:job.id,
        order_id:null,load_order:loadSeq,delivery_status:'pending'
      });
      await db().update('logistics_jobs',job.id,{manifest_id:manifest.id,status:'assigned_truck'});
      const {data:fullJob}=await db().select('logistics_jobs',{eq:{id:job.id},maybeSingle:true,noCompanyFilter:true});
      if(fullJob?.is_return_pickup){
        // A return pick-up: the order stays "Return requested" until the
        // driver collects it (no dispatch, no receivable).
        if(fullJob.order_id) await logActivity(fullJob.order_id,'','','return_pickup_planned',`Van pick-up on manifest ${manifestNumber}`);
        return;
      }
      if(fullJob?.order_id){
        const { data: ord } = await db().select('orders',{select:'status',eq:{id:fullJob.order_id},maybeSingle:true,noCompanyFilter:true});
        await db().update('orders',fullJob.order_id,{status:'dispatched',dispatched_at:new Date().toISOString()});
        await logActivity(fullJob.order_id, ord?.status||'packed', 'dispatched', 'dispatched', `Manifest: ${manifestNumber}`);
      }

      // Provisional receivable - raised the moment this wholesale order
      // goes out on the manifest, not once it's confirmed delivered.
      // updateDelivery() flips it from 'provisional' to 'unpaid' (due)
      // when this specific stop is later marked delivered/collected.
      if(job.orders?.order_type==='wholesale' && job.orders?.customer_id && job.order_id){
        const {data:existingAR}=await db().select('accounts_receivable',{
          eq:{order_id:job.order_id},maybeSingle:true,noCompanyFilter:true
        });
        if(!existingAR){
          await db().insert('accounts_receivable',{
            order_id:job.order_id,
            manifest_id:manifest.id,
            customer_id:job.orders.customer_id,
            amount:job.orders.total_value||0,
            paid_amount:0,
            status:'provisional',
            invoice_ref:job.orders.order_number
          });
        }
      }
    }));

    ui().closeModal();
    ui().toast(`Manifest ${manifestNumber} created with ${selected.length} stops`,'success');

    if(driverId){
      const {data:manifestOrders}=await client.from('manifest_items')
        .select('logistics_jobs(order_id,is_return_pickup)')
        .eq('manifest_id',manifest.id);
      const orderIds=(manifestOrders||[]).filter(mi=>!mi.logistics_jobs?.is_return_pickup).map(mi=>mi.logistics_jobs?.order_id).filter(Boolean);   // no COD on return pick-ups
      if(orderIds.length){
        const {data:codOrders}=await client.from('orders')
          .select('id,order_number,customer_name,total_value,payment_status')
          .in('id',orderIds)
          .eq('payment_status','cod');
        const {data:driverData}=await client.from('drivers').select('name').eq('id',driverId).maybeSingle();
        const resolvedDriverName=driverData?.name||'driver';
        let codTotal=0;
        for(const order of(codOrders||[])){
          // Charge the driver unless this order's COD is still outstanding.
          // A failed delivery (or bank transfer / not collected) reverses the
          // charge, so the order is charged again when it goes out once more.
          const {data:codRows}=await client.from('cod_transactions')
            .select('type,amount').eq('order_id',order.id);
          const outstanding=(codRows||[]).reduce((t,x)=>t+(x.type==='debit'?(Number(x.amount)||0):x.type==='reversal'?-(Number(x.amount)||0):0),0);
          if(!(outstanding>0.005)){
            await client.from('cod_transactions').insert({
              company_id:window.SM.AUTH.company?.id,
              driver_id:driverId,order_id:order.id,type:'debit',
              amount:order.total_value||0,
              notes:`COD order ${order.order_number} — ${order.customer_name||''}`,
              created_by:window.SM.AUTH.user?.id
            });
            const {data:acc}=await client.from('driver_cod_accounts').select('id,balance').eq('driver_id',driverId).maybeSingle();
            if(acc){
              await client.from('driver_cod_accounts').update({balance:(acc.balance||0)+(order.total_value||0)}).eq('id',acc.id);
            } else {
              await client.from('driver_cod_accounts').insert({
                company_id:window.SM.AUTH.company?.id,driver_id:driverId,balance:order.total_value||0
              });
            }
            codTotal+=order.total_value||0;
          }
        }
        if(codTotal>0){
          ui().toast(`COD debit of ${util().formatCurrency(codTotal)} applied to ${resolvedDriverName}'s account`,'info',4000);
        }
      }
    }

    setTimeout(()=>{
      ui().confirm(`Print loading sheet and manifest for ${manifestNumber}?`,()=>printManifest(manifest.id),'Print manifest');
    },500);

    const logTabs=document.getElementById('ims-log-tabs');
    logTabs?.querySelectorAll('.ims__tab').forEach(t=>t.classList.remove('ims__tab--active'));
    logTabs?.querySelector('[data-tab="manifests"]')?.classList.add('ims__tab--active');
    loadManifests();
  }

  // ─── SHIPMENTS ───────────────────────────────────────────────────
  async function loadShipments(){
    const content=document.getElementById('ims-log-content');
    if(!content)return;
    const {data:shipments}=await db().select('shipments',{
      select:'*, orders(order_number,customer_name,postcode,delivered_at,status), couriers(name,tracking_url_base,requires_postcode,postcode_param,tracking_form_only)',
      eq:{is_return_collection:false},   // delivery analytics: return collections are shown on the order instead
      order:{col:'created_at',asc:false},limit:1000
    });
    window._shipmentsAll=shipments||[];
    content.innerHTML = buildShipmentsFilterBar(window._shipmentsAll)+
      '<div id="ims-shipments-stats-wrap">'+buildShipmentsStats(window._shipmentsAll)+'</div>'+
      '<div id="ims-shipments-table-wrap">'+buildShipmentsTable(window._shipmentsAll)+'</div>';
  }

  // Computes calendar days between dispatch and actual delivery for a
  // single shipment. Returns null when not yet delivered or when either
  // date is missing — callers must handle null (shown as '—').
  function _shipDaysToDeliver(s){
    // Classify by orders.status, not shipments.status — shipments.status
    // is constrained (see shipments_status_check) to a coarser set with
    // no distinct 'lost' state, so orders.status is authoritative here.
    if(s.orders?.status!=='delivered'||!s.dispatched_at||!s.orders?.delivered_at) return null;
    const days=Math.round((new Date(s.orders.delivered_at)-new Date(s.dispatched_at))/(1000*60*60*24));
    return Math.max(0,days);
  }

  function buildShipmentsFilterBar(items){
    const couriers=[...new Set(items.map(s=>s.couriers?.name).filter(Boolean))].sort();
    const statuses=[...new Set(items.map(s=>s.orders?.status).filter(Boolean))].sort();
    return `
      <div class="ims__filters" style="margin-bottom:14px">
        <select class="ims__select" id="ims-ship-filter-status">
          <option value="">All statuses</option>
          ${statuses.map(s=>`<option value="${s}">${util().sanitize(s.replace(/_/g,' '))}</option>`).join('')}
        </select>
        <select class="ims__select" id="ims-ship-filter-courier">
          <option value="">All couriers</option>
          ${couriers.map(c=>`<option value="${util().sanitize(c)}">${util().sanitize(c)}</option>`).join('')}
        </select>
        <input class="ims__input" type="date" id="ims-ship-filter-from" title="Dispatched from">
        <input class="ims__input" type="date" id="ims-ship-filter-to" title="Dispatched to">
        <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.filterShipments()"><i class="ti ti-filter"></i> Apply</button>
        <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="document.getElementById('ims-ship-filter-status').value='';document.getElementById('ims-ship-filter-courier').value='';document.getElementById('ims-ship-filter-from').value='';document.getElementById('ims-ship-filter-to').value='';window.IMS_LOGISTICS.filterShipments()">Clear</button>
      </div>`;
  }

  function buildShipmentsStats(items){
    const delivered=items.filter(s=>s.orders?.status==='delivered'&&_shipDaysToDeliver(s)!==null);
    const overallAvg=delivered.length
      ? (delivered.reduce((sum,s)=>sum+_shipDaysToDeliver(s),0)/delivered.length).toFixed(1)
      : '—';

    const byCourier={};
    items.forEach(s=>{
      const name=s.couriers?.name||'Unassigned';
      if(!byCourier[name]) byCourier[name]={total:0,delivered:0,failed:0,lost:0,dispatched:0,daysSum:0,daysCount:0};
      const c=byCourier[name];
      c.total++;
      const orderStatus=s.orders?.status;
      if(orderStatus==='delivered') c.delivered++;
      else if(orderStatus==='failed_delivery') c.failed++;
      else if(orderStatus==='lost') c.lost++;
      else c.dispatched++;
      const days=_shipDaysToDeliver(s);
      if(days!==null){ c.daysSum+=days; c.daysCount++; }
    });

    const courierRows=Object.entries(byCourier)
      .sort((a,b)=>b[1].total-a[1].total)
      .map(([name,c])=>`
        <tr>
          <td>${util().sanitize(name)}</td>
          <td style="text-align:center">${c.total}</td>
          <td style="text-align:center;color:var(--ims-success)">${c.delivered}</td>
          <td style="text-align:center;color:var(--ims-warning)">${c.failed}</td>
          <td style="text-align:center;color:var(--ims-danger)">${c.lost}</td>
          <td style="text-align:center;font-weight:600">${c.daysCount?(c.daysSum/c.daysCount).toFixed(1)+'d':'—'}</td>
        </tr>`).join('');

    return `
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:14px">
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Total shipments (filtered view)</div>
          <div class="ims__stat-value" style="font-size:22px">${items.length}</div>
        </div>
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Delivered</div>
          <div class="ims__stat-value ims__stat-value--success" style="font-size:22px">${delivered.length}</div>
        </div>
        <div class="ims__stat" style="padding:12px">
          <div class="ims__stat-label">Avg days to deliver</div>
          <div class="ims__stat-value" style="font-size:22px">${overallAvg}${overallAvg!=='—'?'d':''}</div>
        </div>
      </div>
      <div class="ims__card" style="margin-bottom:14px">
        <div style="padding:12px 14px;font-weight:600;font-size:13px;border-bottom:1px solid var(--ims-border)"><i class="ti ti-chart-bar" style="color:var(--ims-accent)"></i> Courier performance</div>
        <div class="ims__table-wrap">
          <table class="ims__table">
            <thead><tr><th>Courier</th><th style="text-align:center">Total</th><th style="text-align:center">Delivered</th><th style="text-align:center">Failed</th><th style="text-align:center">Lost</th><th style="text-align:center">Avg days to deliver</th></tr></thead>
            <tbody>${courierRows||'<tr><td colspan="6" class="ims__table-empty">No data</td></tr>'}</tbody>
          </table>
        </div>
      </div>`;
  }

  function buildShipmentsTable(items){
    return `
      <div class="ims__card">
        <div class="ims__table-wrap">
          <table class="ims__table">
            <thead><tr><th>Order #</th><th>Customer</th><th>Mode</th><th>Courier</th><th>Tracking #</th><th>Charge</th><th>Status</th><th>Dispatched</th><th>Days to deliver</th></tr></thead>
            <tbody>${items.map(s=>{
              const trackUrl=util().buildTrackingUrl(s.couriers,s.tracking_number,s.orders?.postcode);
              const days=_shipDaysToDeliver(s);
              return `<tr>
                <td>${s.orders?.order_number
                  ? `<span class="ims__link" onclick="window.IMS_ORDERS?.openOrder('${util().jsArg(s.order_id)}')">${util().sanitize(s.orders.order_number)}</span>`
                  : '—'}</td>
                <td>${util().sanitize(s.orders?.customer_name||'—')}</td>
                <td><span class="ims__pill ims__pill--${s.ship_mode==='courier'?'info':'neutral'}">${s.ship_mode==='courier'?'Courier':'Own truck'}</span></td>
                <td>${util().sanitize(s.couriers?.name||'—')}</td>
                <td>${trackUrl?`<a href="${trackUrl}" target="_blank" class="ims__link">${util().sanitize(s.tracking_number||'—')}</a>`:util().sanitize(s.tracking_number||'—')}</td>
                <td>${s.charge_amount?util().formatCurrency(s.charge_amount):'—'}</td>
                <td>${util().pill(s.orders?.status||s.status)}</td>
                <td>${s.dispatched_at?util().formatDate(s.dispatched_at):'—'}</td>
                <td style="text-align:center">${days!==null?days+'d':'—'}</td>
              </tr>`;
            }).join('')||'<tr><td colspan="9" class="ims__table-empty">No shipments match this filter</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>`;
  }

  function filterShipments(){
    const status  = document.getElementById('ims-ship-filter-status')?.value||'';
    const courier = document.getElementById('ims-ship-filter-courier')?.value||'';
    const from    = document.getElementById('ims-ship-filter-from')?.value||'';
    const to      = document.getElementById('ims-ship-filter-to')?.value||'';

    let filtered = window._shipmentsAll||[];
    if(status)  filtered = filtered.filter(s=>s.orders?.status===status);
    if(courier) filtered = filtered.filter(s=>s.couriers?.name===courier);
    if(from)    filtered = filtered.filter(s=>s.dispatched_at && s.dispatched_at.split('T')[0]>=from);
    if(to)      filtered = filtered.filter(s=>s.dispatched_at && s.dispatched_at.split('T')[0]<=to);

    const statsWrap = document.getElementById('ims-shipments-stats-wrap');
    if(statsWrap) statsWrap.innerHTML = buildShipmentsStats(filtered);
    const tableWrap = document.getElementById('ims-shipments-table-wrap');
    if(tableWrap) tableWrap.innerHTML = buildShipmentsTable(filtered);
  }

  // ─── MANIFESTS ───────────────────────────────────────────────────
  async function loadManifests(){
    const content=document.getElementById('ims-log-content');
    if(!content)return;
    const {data:manifests}=await db().select('manifests',{
      select:'*, drivers(name,phone), vehicles(registration,make_model), routes(name)',
      order:{col:'created_at',asc:false}
    });
    const items=manifests||[];
    const isAdmin=window.SM.AUTH.isAdmin();
    content.innerHTML=`
      <div class="ims__card">
        <div class="ims__table-wrap">
          <table class="ims__table">
            <thead><tr><th>Manifest #</th><th>Driver</th><th>Vehicle</th><th>Route</th><th>Status</th><th>Dispatched</th><th>Van</th><th>Actions</th></tr></thead>
            <tbody>${items.map(m=>`<tr>
              <td><span class="ims__link" onclick="window.IMS_LOGISTICS.openManifest('${util().jsArg(m.id)}')">${util().sanitize(m.manifest_number)}</span></td>
              <td>${util().sanitize(m.drivers?.name||'—')}</td>
              <td>${util().sanitize(m.vehicles?.registration||'—')}</td>
              <td>${util().sanitize(m.routes?.name||'—')}</td>
              <td>${util().pill(m.status)}</td>
              <td>${m.dispatched_at?util().formatDate(m.dispatched_at):'-'}</td>
              <td>${m.departed_at
                ? `<span class="ims__pill ims__pill--neutral" title="Its orders can no longer be amended">Departed ${util().formatDate(m.departed_at)}</span>`
                : `<button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_LOGISTICS.markVanDeparted('${util().jsArg(m.id)}','${util().jsArg(m.manifest_number)}')" title="Press when the van leaves: the manifest and its orders are then locked"><i class="ti ti-truck-delivery"></i> Van departed</button>`}</td>
              <td><div class="ims__btn-group">
                <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openManifest('${util().jsArg(m.id)}')" title="View"><i class="ti ti-eye"></i></button>
                ${m.departed_at?'':`<button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.openEditManifest('${util().jsArg(m.id)}')" title="Edit"><i class="ti ti-pencil"></i></button>`}
                <button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.printManifest('${util().jsArg(m.id)}')" title="Print"><i class="ti ti-printer"></i></button>
                ${isAdmin?`<button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.deleteManifest('${util().jsArg(m.id)}','${util().jsArg(m.manifest_number)}')" title="Delete manifest"><i class="ti ti-trash"></i></button>`:''}
              </div></td>
            </tr>`).join('')||'<tr><td colspan="8" class="ims__table-empty">No manifests yet</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>`;
  }

  // "Van departed": pressed when the van leaves. Until then the manifest
  // and every order on it can still be changed (Amend order in Orders);
  // after it, both are locked. Also set automatically when the first stop
  // outcome is recorded (see checkManifestCompletion).
  async function markVanDeparted(manifestId, manifestNumber){
    ui().confirm(`Mark the van for <strong>${util().sanitize(manifestNumber)}</strong> as departed? The manifest and its orders can then no longer be changed.`, async ()=>{
      const {data:mf}=await db().select('manifests',{select:'departed_at',eq:{id:manifestId},maybeSingle:true,noCompanyFilter:true});
      if(mf?.departed_at){ ui().toast(`${manifestNumber} was already marked as departed`,'info'); loadManifests(); return; }
      const {error}=await db().update('manifests',manifestId,{departed_at:new Date().toISOString(),departed_by:window.SM.AUTH.user?.id||null});
      if(error){ ui().toast('Failed: '+error.message,'error'); return; }
      ui().toast(`${manifestNumber}: van departed. The manifest and its orders are now locked.`,'success');
      loadManifests();
    }, 'Van departed', false);
  }

  async function _manifestDeparted(manifestId){
    const {data:mf}=await db().select('manifests',{select:'departed_at,manifest_number',eq:{id:manifestId},maybeSingle:true,noCompanyFilter:true});
    if(mf?.departed_at){ ui().toast(`${mf.manifest_number||'This manifest'}: the van has departed, so it can no longer be changed.`,'warning',7000); return true; }
    return false;
  }

  async function openEditManifest(manifestId){
    if(await _manifestDeparted(manifestId)) return;
    const { data: manifest } = await db().select('manifests', {
      select: '*', eq: { id: manifestId }, maybeSingle: true,
    });
    if(!manifest){ ui().toast('Manifest not found', 'error'); return; }

    const { data: items } = await db().select('manifest_items', {
      select: 'id,load_order,delivery_status,logistics_jobs(id,order_id,orders(order_number,customer_name,postcode))',
      eq: { manifest_id: manifestId }, order: { col: 'load_order', asc: false }, noCompanyFilter: true,
    });

    const { data: drivers } = await db().select('drivers', { eq: { is_active: true } });
    const { data: vehicles } = await db().select('vehicles', { eq: { is_active: true } });
    const { data: routes } = await db().select('routes', { eq: { is_active: true } });
    const driverOpts = (drivers||[]).map(d=>`<option value="${d.id}" ${d.id===manifest.driver_id?'selected':''}>${util().sanitize(d.name)}</option>`).join('');
    const vehicleOpts = (vehicles||[]).map(v=>`<option value="${v.id}" ${v.id===manifest.vehicle_id?'selected':''}>${util().sanitize(v.registration)} — ${util().sanitize(v.make_model||v.vehicle_type||'')}</option>`).join('');
    const routeOpts = (routes||[]).map(r=>`<option value="${r.id}" ${r.id===manifest.route_id?'selected':''}>${util().sanitize(r.name)}</option>`).join('');

    // Stops can be added only from the Own Truck Queue.
    const { data: unassignedJobs } = await db().select('logistics_jobs', {
      select: 'id,job_type,orders(id,order_number,customer_name,postcode)',
      eq: { status: 'scheduled', job_type: 'own_truck' }, order: { col: 'created_at' },
    });

    const dispatchDateVal = manifest.dispatched_at ? new Date(manifest.dispatched_at).toISOString().split('T')[0] : '';

    ui().openModal(`Edit manifest — ${util().sanitize(manifest.manifest_number)}`, `
      <div class="ims__form-grid" style="margin-bottom:14px">
        <div class="ims__form-group"><label class="ims__label">Driver</label><select class="ims__select" id="ims-em-driver"><option value="">Select driver</option>${driverOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Vehicle</label><select class="ims__select" id="ims-em-vehicle"><option value="">Select vehicle</option>${vehicleOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Route</label><select class="ims__select" id="ims-em-route"><option value="">None</option>${routeOpts}</select></div>
        <div class="ims__form-group"><label class="ims__label">Dispatch date</label><input class="ims__input" type="date" id="ims-em-date" value="${dispatchDateVal}"></div>
      </div>

      <strong style="font-size:13px;display:block;margin-bottom:8px">Current stops (${(items||[]).length})</strong>
      <div style="max-height:200px;overflow-y:auto;margin-bottom:14px">
        <table class="ims__table">
          <thead><tr><th>Order #</th><th>Customer</th><th>Postcode</th><th>Status</th><th></th></tr></thead>
          <tbody>
            ${(items||[]).length ? (items||[]).map(mi => `
              <tr>
                <td>${util().sanitize(mi.logistics_jobs?.orders?.order_number||'—')}</td>
                <td>${util().sanitize(mi.logistics_jobs?.orders?.customer_name||'—')}</td>
                <td>${util().sanitize(mi.logistics_jobs?.orders?.postcode||'—')}</td>
                <td>${util().pill(mi.delivery_status)}</td>
                <td>${mi.delivery_status==='pending' ? `<button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.removeManifestStop('${util().jsArg(mi.id)}','${util().jsArg(manifestId)}')" title="Remove this stop"><i class="ti ti-x"></i></button>` : `<span style="font-size:11px;color:var(--ims-text-sec)">Already recorded</span>`}</td>
              </tr>
            `).join('') : '<tr><td colspan="5" style="text-align:center;color:var(--ims-text-sec)">No stops</td></tr>'}
          </tbody>
        </table>
      </div>

      <strong style="font-size:13px;display:block;margin-bottom:8px">Add a stop</strong>
      <div style="max-height:180px;overflow-y:auto;margin-bottom:14px">
        ${(unassignedJobs||[]).length ? `
        <table class="ims__table">
          <thead><tr><th></th><th>Order #</th><th>Customer</th><th>Postcode</th><th>Type</th></tr></thead>
          <tbody>
            ${(unassignedJobs||[]).map(j => `<tr>
              <td><input type="checkbox" class="ims-em-add-check" value="${j.id}"></td>
              <td>${util().sanitize(j.orders?.order_number||'—')}</td>
              <td>${util().sanitize(j.orders?.customer_name||'—')}</td>
              <td>${util().sanitize(j.orders?.postcode||'—')}</td>
              <td><span class="ims__pill ims__pill--${j.job_type==='delivery'?'info':'neutral'}">${j.job_type==='delivery'?'Delivery':'Collection'}</span></td>
            </tr>`).join('')}
          </tbody>
        </table>` : '<div style="font-size:13px;color:var(--ims-text-sec)">No orders in the Own Truck Queue to add. Use "We deliver" on Unassigned jobs first.</div>'}
      </div>
    `, `
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveManifestEdit('${util().jsArg(manifestId)}')"><i class="ti ti-device-floppy"></i> Save changes</button>
    `, 'xl');
  }

  async function removeManifestStop(itemId, manifestId){
    if(await _manifestDeparted(manifestId)) return;
    const { data: item } = await db().select('manifest_items', {
      select: 'id,logistics_job_id,delivery_status', eq: { id: itemId }, maybeSingle: true, noCompanyFilter: true,
    });
    if(!item){ return; }
    if(item.delivery_status !== 'pending'){
      ui().toast('This stop already has a recorded outcome and cannot be removed', 'error');
      return;
    }
    ui().confirm('Remove this stop from the manifest? The order goes back to the Own Truck Queue and its status reverts to Packed. (To send it another way, use "Back to Unassigned" in the queue.)', async () => {
      const { data: job } = await db().select('logistics_jobs', {
        select: 'order_id,is_return_pickup', eq: { id: item.logistics_job_id }, maybeSingle: true, noCompanyFilter: true,
      });
      await db().remove('manifest_items', itemId);
      await db().update('logistics_jobs', item.logistics_job_id, { manifest_id: null, status: 'scheduled', job_type: 'own_truck' });   // back to the Own Truck Queue
      if(job?.order_id&&job.is_return_pickup){
        await logActivity(job.order_id, '', '', 'return_pickup_unplanned', 'Van pick-up removed from the manifest (back in the queue)');
      } else if(job?.order_id){
        await db().update('orders', job.order_id, { status: 'packed', dispatched_at: null });
        await logActivity(job.order_id, 'dispatched', 'packed', 'removed_from_manifest', 'Removed from manifest via edit');
      }
      ui().toast('Stop removed: the order is back in the Own Truck Queue', 'success');
      openEditManifest(manifestId);
    }, 'Remove stop', true);
  }

  async function saveManifestEdit(manifestId){
    if(await _manifestDeparted(manifestId)) return;
    const g = id => document.getElementById(id)?.value?.trim() || null;
    const driverId = g('ims-em-driver');
    const vehicleId = g('ims-em-vehicle');
    const routeId = g('ims-em-route');
    const dispatchDate = g('ims-em-date');

    const { error } = await db().update('manifests', manifestId, {
      driver_id: driverId, vehicle_id: vehicleId, route_id: routeId,
      dispatched_at: dispatchDate ? new Date(dispatchDate).toISOString() : null,
    });
    if(error){ ui().toast('Failed to save: ' + error.message, 'error'); return; }

    const toAdd = [...document.querySelectorAll('.ims-em-add-check:checked')].map(cb => cb.value);
    if(toAdd.length){
      const { data: existingItems } = await db().select('manifest_items', {
        select: 'load_order', eq: { manifest_id: manifestId }, order: { col: 'load_order', asc: false }, limit: 1, noCompanyFilter: true,
      });
      let nextLoadOrder = (existingItems?.[0]?.load_order || 0) + 1;

      const { data: jobs } = await db().select('logistics_jobs', {
        select: 'id,order_id,is_return_pickup,orders(order_type,customer_id,total_value,order_number,status)',
        in: { id: toAdd }, eq: { status: 'scheduled', job_type: 'own_truck' }, noCompanyFilter: true,   // queue orders only
      });

      for(const job of (jobs || [])){
        await db().insert('manifest_items', {
          manifest_id: manifestId, logistics_job_id: job.id,
          order_id: null, load_order: nextLoadOrder++, delivery_status: 'pending',
        });
        await db().update('logistics_jobs', job.id, { manifest_id: manifestId, status: 'assigned_truck' });
        if(job.is_return_pickup){ if(job.order_id) await logActivity(job.order_id, '', '', 'return_pickup_planned', 'Van pick-up added to a manifest'); continue; }
        if(job.order_id){
          await db().update('orders', job.order_id, { status: 'dispatched', dispatched_at: new Date().toISOString() });
          await logActivity(job.order_id, job.orders?.status || 'packed', 'dispatched', 'dispatched', 'Added to manifest via edit');
        }
        if(job.orders?.order_type === 'wholesale' && job.orders?.customer_id && job.order_id){
          const { data: existingAR } = await db().select('accounts_receivable', {
            eq: { order_id: job.order_id }, maybeSingle: true, noCompanyFilter: true,
          });
          if(!existingAR){
            await db().insert('accounts_receivable', {
              order_id: job.order_id, manifest_id: manifestId, customer_id: job.orders.customer_id,
              amount: job.orders.total_value || 0, paid_amount: 0, status: 'provisional', invoice_ref: job.orders.order_number,
            });
          }
        }
      }
    }

    ui().toast('Manifest updated', 'success');
    ui().closeModal();
    loadManifests();
  }

  async function openManifest(id){
    _openManifestId=id;
    const {data:m}=await db().select('manifests',{
      select:'*, drivers(name,phone), vehicles(registration,make_model), routes(name), manifest_items(id,load_order,delivery_status,delivered_at,recipient_name,failure_reason,pod_photo_path,pod_signature_path,pod_at,pod_removed_at,cod_method,cod_amount,recorded_via,recorded_at,gps_lat,gps_lng,gps_accuracy_m,gps_at,logistics_jobs(order_id,job_type,is_return_pickup,orders(order_number,customer_name,address,postcode,amended_at,order_items(id,product_name,quantity,service_id,service_status))))',
      eq:{id},maybeSingle:true,noCompanyFilter:true
    });
    if(!m)return;
    const items=(m.manifest_items||[]).sort((a,b)=>a.load_order-b.load_order);
    // Who created the manifest and who marked the van as departed.
    const whoIds=[m.created_by,m.departed_by].filter(Boolean);
    const whoMap={};
    if(whoIds.length){
      const {data:us}=await db().select('users',{select:'id,name',in:{id:whoIds},noCompanyFilter:true});
      (us||[]).forEach(u=>{ whoMap[u.id]=u.name; });
    }
    ui().openModal(`Manifest: ${m.manifest_number}`,`
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px">
        <div><span class="ims__label">Driver</span><div>${util().sanitize(m.drivers?.name||'—')}</div></div>
        <div><span class="ims__label">Phone</span><div>${util().sanitize(m.drivers?.phone||'—')}</div></div>
        <div><span class="ims__label">Vehicle</span><div>${util().sanitize(m.vehicles?.registration||'—')} ${m.vehicles?.make_model?'— '+m.vehicles.make_model:''}</div></div>
        <div><span class="ims__label">Route</span><div>${util().sanitize(m.routes?.name||'—')}</div></div>
        <div><span class="ims__label">Dispatched</span><div>${m.dispatched_at?util().formatDateTime(m.dispatched_at):'—'}</div></div>
        <div><span class="ims__label">Status</span><div>${util().pill(m.status)}</div></div>
        <div><span class="ims__label">Created by</span><div>${util().sanitize(whoMap[m.created_by]||'-')}<div style="font-size:11px;color:var(--ims-text-muted)">${m.created_at?util().formatDateTime(m.created_at):''}</div></div></div>
        <div><span class="ims__label">Van departed</span><div>${m.departed_at?`${util().sanitize(whoMap[m.departed_by]||'-')}<div style="font-size:11px;color:var(--ims-text-muted)">${util().formatDateTime(m.departed_at)}</div>`:'Not yet'}</div></div>
      </div>
      <hr class="ims__divider">
      <strong style="font-size:13px">Delivery stops — Loading order (${items.length} stops)</strong>
      <div class="ims__table-wrap" style="margin-top:8px">
        <table class="ims__table">
          <thead><tr><th>Load #</th><th>Order #</th><th>Customer</th><th>Address</th><th>Postcode</th><th>Type</th><th>Status</th><th>Update</th></tr></thead>
          <tbody>${items.map(item=>`<tr>
            <td style="font-weight:700">${item.load_order}</td>
            <td><span class="ims__link" onclick="window.IMS_ORDERS?.openOrder('${util().jsArg(item.logistics_jobs?.order_id||'')}')">${util().sanitize(item.logistics_jobs?.orders?.order_number||'-')}</span>${item.logistics_jobs?.orders?.amended_at?' <span class="ims__pill ims__pill--warning" style="font-size:10px">Amended</span>':''}</td>
            <td>${util().sanitize(item.logistics_jobs?.orders?.customer_name||'—')}</td>
            <td style="font-size:12px">${util().sanitize(item.logistics_jobs?.orders?.address||'—')}</td>
            <td>${util().sanitize(item.logistics_jobs?.orders?.postcode||'—')}</td>
            <td>${item.logistics_jobs?.is_return_pickup?'<span class="ims__pill ims__pill--warning">Return pick-up</span>':`<span class="ims__pill ims__pill--${item.logistics_jobs?.job_type==='delivery'?'info':'neutral'}">${item.logistics_jobs?.job_type||'—'}</span>`}</td>
            <td>${util().pill(item.delivery_status)}</td>
            <td>${['delivered','failed','partial','collected'].includes(item.delivery_status)
              ? `<span style="font-size:11px;color:var(--ims-text-muted)"><i class="ti ti-lock" style="font-size:11px"></i> Recorded</span>`
              : `<select class="ims__select" style="font-size:11px;padding:3px 6px" onchange="window.IMS_LOGISTICS.updateDelivery('${util().jsArg(item.id)}','${util().jsArg(item.logistics_jobs?.order_id||'')}',this.value)">
              <option value="">Update...</option>
              ${item.logistics_jobs?.is_return_pickup
                ? `<option value="collected">Collected</option><option value="failed">Not collected</option>`
                : `<option value="out_for_delivery">Out for delivery</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
              <option value="collected">Collected</option>`}
            </select>`}</td>
          </tr>${(()=>{ const tasks=(item.logistics_jobs?.orders?.order_items||[]).filter(t=>t.service_id); if(!tasks.length) return '';
            return `<tr><td></td><td colspan="7" style="padding-top:0;border-top:none"><div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;font-size:12px"><strong>Tasks:</strong>${tasks.map(t=>`<span style="display:inline-flex;gap:4px;align-items:center;padding:2px 6px;border:1px solid var(--ims-border);border-radius:var(--ims-radius)">${util().sanitize(t.product_name)}${t.quantity>1?` x${t.quantity}`:''}
              ${t.service_status==='done'?'<span class="ims__pill ims__pill--success" style="font-size:10px">Done</span>':t.service_status==='not_done'?'<span class="ims__pill ims__pill--danger" style="font-size:10px">Not done</span>':
              `<button class="ims__btn ims__btn--ghost ims__btn--sm" style="padding:1px 6px" onclick="window.IMS_LOGISTICS.setServiceStatus('${util().jsArg(t.id)}','done','${util().jsArg(item.logistics_jobs?.order_id||'')}','${util().jsArg(t.product_name)}','${util().jsArg(m.id)}')">Done</button><button class="ims__btn ims__btn--ghost ims__btn--sm" style="padding:1px 6px;color:var(--ims-danger)" onclick="window.IMS_LOGISTICS.setServiceStatus('${util().jsArg(t.id)}','not_done','${util().jsArg(item.logistics_jobs?.order_id||'')}','${util().jsArg(t.product_name)}','${util().jsArg(m.id)}')">Not done</button>`}</span>`).join('')}</div></td></tr>`; })()}${_podRow(item)}`).join('')||'<tr><td colspan="8" class="ims__table-empty">No items</td></tr>'}
          </tbody>
        </table>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.IMS_LOGISTICS.printManifest('${util().jsArg(m.id)}')"><i class="ti ti-printer"></i> Print</button>
      <button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal()">Close</button>
    `,'xl');
    _fillPodImages(document.getElementById('ims-modal-body'));
    _fillGpsDistances(document.getElementById('ims-modal-body'));
  }

  // ─── COURIER COLLECTION OF RETURNS ────────────────────────────────
  // A collection shipment for a requested return: courier, date, the
  // courier's references, and the charge (from the courier's rate card
  // where it has one, editable), recorded as a courier expense like a
  // delivery. Statuses: booked = pending, collected = in_transit, back at
  // the warehouse = returned. The order: Return requested -> (collected)
  // Return in transit -> Return received, as for any return. Delivery
  // screens (Tracking, claims, Mark delivered...) never see collections.
  async function _openCollection(orderId){
    const {data}=await _rcDb().from('shipments').select('id,courier_id,tracking_number,courier_order_ref,collection_date,charge_amount,status,couriers(name,cs_email,tracking_url_base,requires_postcode,postcode_param,tracking_form_only)')
      .eq('order_id',orderId).eq('is_return_collection',true).neq('status','returned').order('created_at',{ascending:false}).limit(1).maybeSingle();
    return data||null;
  }
  async function bookCourierCollection(orderId,orderNumber,existingId){
    const c=_rcDb();
    const {data:o}=await c.from('orders').select('id,order_number,status,customer_name,end_customer_name,address,end_address,postcode,end_postcode,phone,end_customer_phone').eq('id',orderId).maybeSingle();
    if(!o){ ui().toast('Order not found','error'); return; }
    let cur=null;
    if(existingId){ ({data:cur}=await c.from('shipments').select('*').eq('id',existingId).maybeSingle()); }
    else {
      if(o.status!=='return_requested'){ ui().toast('A courier collection can only be booked for an order whose return is requested','warning',7000); return; }
      if(await _openCollection(orderId)){ ui().toast(`A courier collection for ${o.order_number} is already booked`,'info',6000); return; }
    }
    const {data:couriers}=await db().select('couriers',{eq:{is_active:true},order:{col:'name'}});
    const tomorrow=new Date(Date.now()+86400000).toISOString().split('T')[0];
    const v=k=>util().sanitize(cur?.[k]??'');
    ui().openModal(`${cur?'Edit':'Book'} courier collection: ${util().sanitize(o.order_number)}`,`
      <div style="font-size:13px;margin-bottom:10px">Collect from <strong>${util().sanitize(o.end_customer_name||o.customer_name||'-')}</strong>, ${util().sanitize(o.end_address||o.address||'')} <strong>${util().sanitize(o.end_postcode||o.postcode||'')}</strong></div>
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Courier</label><select class="ims__select" id="ims-col-courier" ${cur?'disabled':''} onchange="window.IMS_LOGISTICS.collectionPrice('${util().jsArg(orderId)}')"><option value="">Choose courier</option>${(couriers||[]).map(x=>`<option value="${x.id}" ${cur?.courier_id===x.id?'selected':''}>${util().sanitize(x.name)}</option>`).join('')}</select></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Collection date</label><input class="ims__input" type="date" id="ims-col-date" value="${v('collection_date')||tomorrow}"></div>
        <div class="ims__form-group"><label class="ims__label">Courier order no.</label><input class="ims__input" id="ims-col-ref" value="${v('courier_order_ref')}" placeholder="e.g. VDI242546"></div>
        <div class="ims__form-group"><label class="ims__label">Tracking no.</label><input class="ims__input" id="ims-col-track" value="${v('tracking_number')}" placeholder="If the courier gives one"></div>
        ${cur?'':`<div class="ims__form-group"><label class="ims__label">Charge (£, before VAT)</label><input class="ims__input" type="number" step="0.01" min="0" id="ims-col-charge"><span class="ims__input-hint" id="ims-col-charge-hint">Choose the courier to work it out from its rate card</span></div>
        <div class="ims__form-group ims__form-group--full"><label style="display:flex;gap:6px;align-items:center;font-size:13px"><input type="checkbox" id="ims-col-email" checked> Email the courier to request the collection (you can check the email before it goes)</label></div>`}
      </div>`,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveCourierCollection('${util().jsArg(orderId)}','${util().jsArg(existingId||'')}',this)"><i class="ti ti-check"></i> ${cur?'Save changes':'Book collection'}</button>`,'lg');
  }
  // The charge, from the courier's rate card (collections are priced like
  // deliveries); left blank if the courier has no rate card.
  async function collectionPrice(orderId){
    const courierId=document.getElementById('ims-col-courier')?.value, hint=document.getElementById('ims-col-charge-hint'), box=document.getElementById('ims-col-charge');
    if(!courierId||!box) return;
    const {data:o}=await _rcDb().from('orders').select('postcode,end_postcode').eq('id',orderId).maybeSingle();
    const date=document.getElementById('ims-col-date')?.value||undefined;
    let calc=null; try{ calc=await calcExpectedForShipment({courierId,orderId,postcode:o?.end_postcode||o?.postcode||'',date}); }catch(e){ calc=null; }
    // Saved in the same shape as "Assign to courier", so the courier invoice
    // check compares a collection line like any other shipment.
    box.dataset.breakdown=calc&&calc.method==='type_zone'?JSON.stringify({method:'type_zone',zone:calc.zone?.name||null,outward:calc.outward,rate:calc.rateLine,extras:(calc.extras||[]).filter(e=>e.on).map(e=>({name:e.name,qty:e.qty,amount:e.amount})),floor:calc.floor,floor_amount:calc.floorAmt,base:calc.base,fuel_pct:calc.fuelPct,fuel:calc.fuel,total:calc.total,flags:calc.flags,collection:true}):'';
    if(calc&&calc.total!=null&&calc.rateLine){ box.value=Number(calc.total).toFixed(2); if(hint) hint.textContent=`From the rate card: ${calc.rateLine.type}${calc.zone?', '+calc.zone.name:''} (incl. fuel). You can change it.`; }
    else { box.value=''; if(hint) hint.textContent='No rate card price for this courier: enter the charge if you know it.'; }
  }
  async function saveCourierCollection(orderId,existingId,btn){
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const date=g('ims-col-date'); if(!date){ ui().toast('Please choose the collection date','error'); return; }
    const c=_rcDb();
    if(existingId){
      const {error}=await c.from('shipments').update({collection_date:date,dispatched_at:`${date}T12:00:00.000Z`,courier_order_ref:g('ims-col-ref')||null,tracking_number:g('ims-col-track')||null}).eq('id',existingId).eq('is_return_collection',true);
      if(error){ ui().toast('Not saved: '+error.message,'error',8000); return; }
      await logActivity(orderId,'','','return_collection_updated',`Courier collection updated: ${date}`);
      ui().closeModal(); ui().toast('Collection updated','success'); window.IMS_ORDERS?.openOrder?.(orderId); return;
    }
    const courierId=g('ims-col-courier'); if(!courierId){ ui().toast('Please choose the courier','error'); return; }
    const charge=Math.round((parseFloat(g('ims-col-charge'))||0)*100)/100;
    if(btn) btn.disabled=true;
    const {data:o}=await c.from('orders').select('id,order_number,status,customer_name,end_customer_name,address,end_address,postcode,end_postcode,phone,end_customer_phone,order_items(product_name,quantity,service_id)').eq('id',orderId).maybeSingle();
    if(!o||o.status!=='return_requested'){ if(btn) btn.disabled=false; ui().toast('This return is no longer waiting for collection','warning'); return; }
    const {data:cr}=await c.from('couriers').select('name,cs_email').eq('id',courierId).maybeSingle();
    const ref=g('ims-col-ref')||null, track=g('ims-col-track')||null;
    let breakdown=null; try{ const bd=document.getElementById('ims-col-charge')?.dataset.breakdown; breakdown=bd?JSON.parse(bd):null; }catch(e){}
    const {error}=await db().insert('shipments',{order_id:orderId,courier_id:courierId,tracking_number:track,courier_order_ref:ref,ship_mode:'courier',status:'pending',
      is_return_collection:true,collection_date:date,dispatched_at:`${date}T12:00:00.000Z`,charge_amount:charge,expected_charge:charge||null,charge_breakdown:breakdown});
    if(error){ if(btn) btn.disabled=false; ui().toast('Not booked: '+error.message,'error',8000); return; }
    if(charge>0) await db().insert('transactions',{date:new Date().toISOString().split('T')[0],type:'expense',category:'Courier charges',description:`Return collection: ${cr?.name||'courier'} (${o.order_number})`,amount:charge,reference:track||ref||o.order_number});
    await db().update('orders',orderId,{return_method:'courier_collection'});
    await c.from('order_tracking_events').insert({company_id:window.SM.AUTH.company?.id,order_id:orderId,tracking_status:'Return collection booked',notes:`${cr?.name||'Courier'} on ${util().formatDate(date)}`,user_name:window.SM.AUTH.user?.name||'User',user_id:window.SM.AUTH.user?.id||null});
    await logActivity(orderId,'','','return_collection_booked',`Courier collection booked: ${cr?.name||''} on ${date}${charge?` (£${charge.toFixed(2)})`:''}`);
    const wantEmail=document.getElementById('ims-col-email')?.checked;
    ui().closeModal(); ui().toast(`Collection booked with ${cr?.name||'the courier'} for ${util().formatDate(date)}`,'success');
    if(wantEmail) setTimeout(()=>openCollectionEmail(o,cr,date,ref),300);
    else window.IMS_ORDERS?.openOrder?.(orderId);
  }
  // A ready-made email asking the courier to collect (checked before sending).
  function openCollectionEmail(o,cr,date,ref){
    const name=o.end_customer_name||o.customer_name||'', addr=o.end_address||o.address||'', pc=o.end_postcode||o.postcode||'', phone=o.end_customer_phone||o.phone||'';
    const items=(o.order_items||[]).filter(i=>!i.service_id).map(i=>`  ${i.quantity} x ${i.product_name}`).join('\n');
    const company=window.SM.AUTH.company?.name||'';
    const body=`Dear ${cr?.name||'Courier'} Team,\n\nPlease arrange a collection for the return below, to come back to our warehouse.\n\nOrder reference:  ${o.order_number}${ref?`\nYour reference:   ${ref}`:''}\nCollection date:  ${util().formatDate(date)}\n\nCollect from:\n  ${name}\n  ${addr}\n  ${pc}${phone?`\n  Phone: ${phone}`:''}\n\nItems:\n${items||'  (see order)'}\n\nPlease confirm the collection and send us your reference and any tracking number.\n\nKind regards,\n${company}`;
    ui().openModal(`Email ${util().sanitize(cr?.name||'the courier')}: collection ${util().sanitize(o.order_number)}`,`
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Send to</label><input class="ims__input" type="email" id="ims-rn-email" value="${util().sanitize(cr?.cs_email||'')}" placeholder="Courier customer-service email">
          ${cr?.cs_email?'':`<span class="ims__input-hint" style="color:var(--ims-warning)">${util().sanitize(cr?.name||'This courier')} has no customer-services email yet: add it in Logistics &rarr; Couriers, and it will fill in by itself next time.</span>`}</div>
        <div class="ims__form-group"><label class="ims__label">Subject</label><input class="ims__input" id="ims-rn-subject" value="${util().sanitize(`Collection request - ${o.order_number}${ref?' - '+ref:''}`)}"></div>
      </div>
      <div class="ims__form-group"><label class="ims__label">Email (edit as needed before sending)</label><textarea class="ims__textarea" id="ims-rn-body" rows="15" style="font-family:monospace;font-size:12px">${util().sanitize(body)}</textarea></div>`,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal();window.IMS_ORDERS?.openOrder?.('${util().jsArg(o.id)}')">Skip email</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.sendCollectionEmail('${util().jsArg(o.id)}','${util().jsArg(o.order_number)}')"><i class="ti ti-send"></i> Send to courier</button>`,'lg');
  }
  async function sendCollectionEmail(orderId,orderNumber){
    const to=document.getElementById('ims-rn-email')?.value?.trim()||'';
    if(!to){ ui().toast('Please enter the courier email','error'); return; }
    const result=await sendCourierEmail({to,subject:document.getElementById('ims-rn-subject')?.value||'',body:document.getElementById('ims-rn-body')?.value||'',
      cc:window.SM.AUTH.company?.email||'salesmdflondon@gmail.com',orderId,emailType:'courier_collection_request',logNote:`Collection request sent to courier for ${orderNumber}`});
    if(!result.sent) return;
    await _rcDb().from('order_tracking_events').insert({company_id:window.SM.AUTH.company?.id,order_id:orderId,tracking_status:'Collection request sent to courier',notes:`Emailed to ${to}`,user_name:window.SM.AUTH.user?.name||'User',user_id:window.SM.AUTH.user?.id||null});
    ui().closeModal(); ui().toast(`Collection request sent to ${to}`,'success',5000); window.IMS_ORDERS?.openOrder?.(orderId);
  }
  // The courier has collected it: the goods are on their way back.
  async function markCollectionCollected(shipmentId,orderId){
    const c=_rcDb();
    const {error}=await c.from('shipments').update({status:'in_transit'}).eq('id',shipmentId).eq('is_return_collection',true).eq('status','pending');
    if(error){ ui().toast('Not saved: '+error.message,'error'); return; }
    const {data:o}=await c.from('orders').select('status').eq('id',orderId).maybeSingle();
    if(o?.status==='return_requested') await db().update('orders',orderId,{status:'return_in_transit'});
    await c.from('order_tracking_events').insert({company_id:window.SM.AUTH.company?.id,order_id:orderId,tracking_status:'Return collected by courier',notes:null,user_name:window.SM.AUTH.user?.name||'User',user_id:window.SM.AUTH.user?.id||null});
    await logActivity(orderId,o?.status||'return_requested','return_in_transit','return_collected','Collected by the courier: on its way back');
    ui().toast('Marked collected: the return is on its way back','success'); window.IMS_ORDERS?.openOrder?.(orderId);
  }
  // Cancel a booking not yet collected: removed, and its charge reversed.
  async function cancelCourierCollection(shipmentId,orderId){
    ui().confirm('Cancel this courier collection? Its charge is reversed, and the return stays requested.',async()=>{
      const c=_rcDb();
      const {data:sh}=await c.from('shipments').select('id,status,charge_amount,tracking_number,courier_order_ref,is_return_collection,couriers(name),orders(order_number)').eq('id',shipmentId).maybeSingle();
      if(!sh||!sh.is_return_collection||sh.status!=='pending'){ ui().toast('Only a collection not yet collected can be cancelled','warning'); return; }
      const {error}=await c.from('shipments').delete().eq('id',shipmentId);
      if(error){ ui().toast('Not cancelled: '+error.message,'error'); return; }
      if(Number(sh.charge_amount)>0) await db().insert('transactions',{date:new Date().toISOString().split('T')[0],type:'expense',category:'Courier charges',description:`Return collection cancelled: ${sh.couriers?.name||'courier'} (${sh.orders?.order_number||''})`,amount:-Number(sh.charge_amount),reference:sh.tracking_number||sh.courier_order_ref||sh.orders?.order_number||null});
      await logActivity(orderId,'','','return_collection_cancelled','Courier collection cancelled');
      ui().toast('Collection cancelled','success'); window.IMS_ORDERS?.openOrder?.(orderId);
    },'Cancel collection',true);
  }
  // On the order: the return collection, if any, with its actions.
  async function renderReturnCollection(orderId,elId,orderStatus){
    const el=document.getElementById(elId); if(!el) return;
    const sh=await _openCollection(orderId);
    if(!sh){ el.innerHTML=''; return; }
    const st=sh.status==='pending'?'Booked':sh.status==='in_transit'?'Collected: on its way back':sh.status;
    const link=sh.tracking_number&&sh.couriers?util().buildTrackingUrl?.(sh.couriers,sh.tracking_number,''):null;
    el.innerHTML=`<hr class="ims__divider"><strong style="font-size:13px">Return collection</strong>
      <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;font-size:12px;margin-top:8px">
        <span class="ims__pill ims__pill--${sh.status==='pending'?'warning':'info'}">${util().sanitize(st)}</span>
        <span><strong>${util().sanitize(sh.couriers?.name||'Courier')}</strong> &middot; ${util().formatDate(sh.collection_date)}${sh.courier_order_ref?` &middot; ref ${util().sanitize(sh.courier_order_ref)}`:''}${sh.tracking_number?` &middot; ${link?`<a class="ims__link" href="${link}" target="_blank" rel="noopener">${util().sanitize(sh.tracking_number)}</a>`:util().sanitize(sh.tracking_number)}`:''}${Number(sh.charge_amount)>0?` &middot; ${util().formatCurrency(sh.charge_amount)}`:''}</span>
        ${sh.status==='pending'?`<button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.markCollectionCollected('${util().jsArg(sh.id)}','${util().jsArg(orderId)}')"><i class="ti ti-check"></i> Mark collected</button>
        <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_LOGISTICS.bookCourierCollection('${util().jsArg(orderId)}','','${util().jsArg(sh.id)}')"><i class="ti ti-edit"></i> Edit</button>
        <button class="ims__btn ims__btn--ghost ims__btn--sm" style="color:var(--ims-danger)" onclick="window.IMS_LOGISTICS.cancelCourierCollection('${util().jsArg(sh.id)}','${util().jsArg(orderId)}')"><i class="ti ti-x"></i> Cancel</button>`:''}
      </div>`;
  }

  // ─── RETURN PICK-UPS (office) ─────────────────────────────────────
  // A van job that COLLECTS a return. It waits in the Own Truck Queue,
  // goes onto manifests like a delivery, and never changes stock, COD or
  // the order's status until the driver collects it (then "Return in
  // transit"; the warehouse uses "Return received").
  async function arrangeVanPickup(orderId,orderNumber){
    const c=_rcDb();
    const {data:o}=await c.from('orders').select('id,status').eq('id',orderId).maybeSingle();
    if(!o||o.status!=='return_requested'){ ui().toast('A van pick-up can only be arranged for an order whose return is requested','warning',6000); return false; }
    const {data:open}=await c.from('logistics_jobs').select('id,status,manifest_id').eq('order_id',orderId).eq('is_return_pickup',true);
    const live=[];
    for(const j of (open||[])){
      if(j.status==='scheduled'&&!j.manifest_id){ live.push(j); continue; }
      if(j.manifest_id){ const {data:mi}=await c.from('manifest_items').select('delivery_status').eq('logistics_job_id',j.id).maybeSingle(); if(mi&&!['collected','failed','delivered','partial'].includes(mi.delivery_status)) live.push(j); }
    }
    if(live.length){ ui().toast(`A van pick-up for ${orderNumber||'this order'} is already arranged`,'info',6000); return false; }
    const {error}=await db().insert('logistics_jobs',{order_id:orderId,job_type:'own_truck',status:'scheduled',is_return_pickup:true});
    if(error){ ui().toast('Not arranged: '+error.message,'error',8000); return false; }
    await db().update('orders',orderId,{return_method:'own_van_pickup'});
    await logActivity(orderId,'','','return_pickup_arranged','Van pick-up arranged: in the Own Truck Queue');
    ui().toast(`Van pick-up for ${orderNumber||'the order'} added to the Own Truck Queue`,'success');
    return true;
  }
  async function cancelPickup(jobId){
    ui().confirm('Cancel this van pick-up? The return stays requested; you can arrange another way.',async()=>{
      const c=_rcDb();
      const {data:j}=await c.from('logistics_jobs').select('id,order_id,status,manifest_id,is_return_pickup').eq('id',jobId).maybeSingle();
      if(!j||!j.is_return_pickup||j.manifest_id||j.status!=='scheduled'){ ui().toast('Only a pick-up still waiting in the queue can be cancelled','warning'); return; }
      const {error}=await c.from('logistics_jobs').delete().eq('id',jobId);
      if(error){ ui().toast('Not cancelled: '+error.message,'error'); return; }
      if(j.order_id) await logActivity(j.order_id,'','','return_pickup_cancelled','Van pick-up cancelled');
      ui().toast('Van pick-up cancelled','success'); loadOwnTruckQueue();
    },'Cancel pick-up',true);
  }

  // ─── LIVE UPDATES ─────────────────────────────────────────────────
  // While Logistics is open, the database tells this screen when a stop or
  // an order changes (Supabase Realtime, within the same company rules), so
  // an open manifest, Tracking and the manifest list refresh by themselves
  // as drivers record stops. If the live connection drops, the screen
  // checks every 60 seconds until it is back. Several changes close
  // together refresh once, and never while you are typing on the screen.
  let _openManifestId=null;
  const _live={client:null,channel:null,ok:false,poll:null,timer:null};
  function _liveStart(){
    if(_live.channel) return;
    try{
      _live.client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
      _live.channel=_live.client.channel('sm-logistics-live')
        .on('postgres_changes',{event:'*',schema:'public',table:'manifest_items'},()=>_liveChanged())
        .on('postgres_changes',{event:'*',schema:'public',table:'orders'},()=>_liveChanged())
        .subscribe(status=>{
          _live.ok=status==='SUBSCRIBED';
          if(_live.ok) _livePollStop(); else if(['CHANNEL_ERROR','TIMED_OUT','CLOSED'].includes(status)) _livePollStart();
        });
      setTimeout(()=>{ if(!_live.ok) _livePollStart(); },10000);
    }catch(e){ _livePollStart(); }
  }
  function _livePollStart(){ if(!_live.poll) _live.poll=setInterval(()=>_liveChanged(),60000); }
  function _livePollStop(){ if(_live.poll){ clearInterval(_live.poll); _live.poll=null; } }
  function _liveChanged(){ clearTimeout(_live.timer); _live.timer=setTimeout(_liveRefresh,1500); }
  function _manifestOnScreen(){
    const open=document.getElementById('ims-modal-overlay')?.classList.contains('ims__modal-overlay--open');
    return !!(_openManifestId&&open&&/^Manifest: /.test(document.getElementById('ims-modal-title')?.textContent||''));
  }
  function _typingOnScreen(){
    const a=document.activeElement;
    return !!(a&&/^(INPUT|SELECT|TEXTAREA)$/.test(a.tagName)&&a.closest('#ims-modal,#ims-log-content'));
  }
  async function _liveRefresh(){
    if(_typingOnScreen()){ _live.timer=setTimeout(_liveRefresh,5000); return; }
    if(_manifestOnScreen()){ await openManifest(_openManifestId); return; }
    if(document.getElementById('ims-modal-overlay')?.classList.contains('ims__modal-overlay--open')) return;   // another window is open: leave it
    if(window.SM?.UI?.activePanel!=='logistics') return;
    const tab=document.querySelector('#ims-log-tabs .ims__tab--active')?.dataset.tab;
    if(tab==='tracking') await loadTracking();
    else if(tab==='manifests') await loadManifests();
  }

  // ─── GPS: how far the stop was recorded from its delivery postcode ──
  // The postcode's location comes from postcodes.io (free, UK only; only
  // the postcode is sent, never any customer details), looked up in one
  // request and remembered for this session. Over 1 km is flagged: useful
  // for "it never arrived" disputes.
  const _pcCache={};
  function _km(a,b,c,d){ const R=6371,r=x=>x*Math.PI/180; const h=Math.sin(r(c-a)/2)**2+Math.cos(r(a))*Math.cos(r(c))*Math.sin(r(d-b)/2)**2; return 2*R*Math.asin(Math.sqrt(h)); }
  async function _fillGpsDistances(scope){
    const spans=[...((scope||document).querySelectorAll('span[data-gps-check]'))].filter(x=>!x.dataset.done);
    if(!spans.length) return;
    const norm=pc=>String(pc||'').toUpperCase().replace(/\s+/g,'');
    const want=[...new Set(spans.map(x=>norm(x.dataset.postcode)).filter(pc=>pc&&!(pc in _pcCache)))];
    if(want.length){
      try{
        const res=await fetch('https://api.postcodes.io/postcodes',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({postcodes:want.slice(0,100)})});
        const out=await res.json();
        (out?.result||[]).forEach(r=>{ _pcCache[norm(r.query)]=r.result?{lat:r.result.latitude,lng:r.result.longitude}:null; });
      }catch(e){ /* no lookup: the location link still works, just without the distance */ }
    }
    spans.forEach(x=>{
      x.dataset.done='1';
      const pc=_pcCache[norm(x.dataset.postcode)]; if(!pc) return;
      const km=_km(Number(x.dataset.lat),Number(x.dataset.lng),pc.lat,pc.lng), acc=Number(x.dataset.acc)||0;
      const approx=acc>500?' (approximate location)':'';
      x.innerHTML=km>1
        ?`<span style="color:var(--ims-warning);font-weight:600">&#9888; ${km.toFixed(1)} km from the delivery postcode${approx}</span>`
        :`<span style="color:var(--ims-text-muted)">at the delivery postcode (${km<0.1?'under 0.1':km.toFixed(1)} km)${approx}</span>`;
    });
  }

  // ─── PROOF OF DELIVERY (office view) ──────────────────────────────
  // What the driver (or office) recorded at each stop: who received it,
  // when, from where, the failure reason, how COD was paid, and the photo
  // and signature. Images load through private links valid for 10 minutes
  // (the storage is never public) and enlarge on click.
  const COD_LABEL={cash:'cash collected',bank_transfer:'paid by bank transfer',not_collected:'NOT collected'};
  function _podDetails(item,postcode){
    const bits=[];
    if(item.recipient_name) bits.push(`Received by <strong>${util().sanitize(item.recipient_name)}</strong>`);
    if(item.failure_reason) bits.push(`<span style="color:var(--ims-danger)">Failed: ${util().sanitize(item.failure_reason)}</span>`);
    const when=item.pod_at||item.recorded_at||item.delivered_at;
    if(when) bits.push(util().formatDateTime?util().formatDateTime(when):util().formatDate(when));
    if(item.recorded_via) bits.push(item.recorded_via==='driver_app'?'driver app':'office');
    if(item.cod_method) bits.push(`COD ${COD_LABEL[item.cod_method]||item.cod_method}${item.cod_method==='cash'?` ${util().formatCurrency(item.cod_amount||0)}`:''}`);
    if(item.gps_lat!=null&&item.gps_lng!=null){
      const acc=item.gps_accuracy_m!=null?` &plusmn;${item.gps_accuracy_m} m`:'';
      bits.push(`<a class="ims__link" href="https://www.google.com/maps/search/?api=1&query=${Number(item.gps_lat)},${Number(item.gps_lng)}" target="_blank" rel="noopener" title="Where the phone was when the stop was recorded">&#128205; Location${acc}</a>`
        +(postcode?` <span data-gps-check data-lat="${Number(item.gps_lat)}" data-lng="${Number(item.gps_lng)}" data-acc="${Number(item.gps_accuracy_m)||0}" data-postcode="${util().sanitize(postcode)}"></span>`:''));
    }
    if(item.pod_removed_at) bits.push(`<span style="color:var(--ims-text-muted)">Proof removed after ${window.SM.AUTH.company?.pod_retention_months||12} months (retention policy)</span>`);
    const thumbs=[item.pod_photo_path?_podThumb(item.pod_photo_path,'Photo'):'',item.pod_signature_path?_podThumb(item.pod_signature_path,'Signature'):''].join('');
    return {bits,thumbs};
  }
  function _podRow(item){
    if(!['delivered','failed','partial','collected'].includes(item.delivery_status)) return '';
    const {bits,thumbs}=_podDetails(item,item.logistics_jobs?.orders?.postcode||'');
    if(!bits.length&&!thumbs) return '';
    return `<tr><td></td><td colspan="7" style="padding-top:0;border-top:none"><div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;font-size:12px"><strong>Proof:</strong>${thumbs}<span>${bits.join(' &middot; ')}</span></div></td></tr>`;
  }
  function _podThumb(path,label){
    return `<img data-pod-path="${util().sanitize(path)}" alt="${label}" title="${label}: click to enlarge" style="width:56px;height:42px;object-fit:cover;border-radius:6px;border:1px solid var(--ims-border);background:#f3f4f6;cursor:zoom-in" onclick="window.IMS_LOGISTICS.podOpen(this)">`;
  }
  async function _fillPodImages(scope){
    const imgs=[...((scope||document).querySelectorAll('img[data-pod-path]'))].filter(i=>!i.getAttribute('src'));
    if(!imgs.length) return;
    const paths=[...new Set(imgs.map(i=>i.dataset.podPath))];
    const {data,error}=await _rcDb().storage.from('pod').createSignedUrls(paths,600);
    const map={}; (data||[]).forEach(x=>{ if(x&&x.signedUrl&&!x.error) map[x.path]=x.signedUrl; });
    imgs.forEach(i=>{ const u=map[i.dataset.podPath]; if(u) i.setAttribute('src',u); else { i.alt=`${i.alt} (not available${error?': '+error.message:''})`; i.style.cursor='default'; } });
  }
  function podOpen(img){
    const src=img?.getAttribute('src'); if(!src) return;
    document.getElementById('ims-pod-lightbox')?.remove();
    const ov=document.createElement('div'); ov.id='ims-pod-lightbox';
    ov.style.cssText='position:fixed;inset:0;z-index:100000;background:rgba(0,0,0,.82);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:16px;cursor:zoom-out';
    ov.innerHTML=`<img src="${src}" alt="${util().sanitize(img.alt||'')}" style="max-width:96vw;max-height:84vh;object-fit:contain;background:#fff;border-radius:8px">
      <div style="margin-top:10px;display:flex;gap:10px"><a href="${src}" target="_blank" rel="noopener" style="color:#fff;font-size:13px" onclick="event.stopPropagation()">Open in a new tab</a><span style="color:#cbd5e1;font-size:13px">Click anywhere to close</span></div>`;
    ov.addEventListener('click',()=>ov.remove());
    document.body.appendChild(ov);
  }
  // The proof for one order (used by the order view).
  async function renderOrderPod(orderId,elId){
    const el=document.getElementById(elId); if(!el||!orderId) return;
    const c=_rcDb();
    const {data:jobs}=await c.from('logistics_jobs').select('id').eq('order_id',orderId);
    const ids=(jobs||[]).map(j=>j.id); if(!ids.length) return;
    const {data:items}=await c.from('manifest_items').select('id,manifest_id,delivery_status,delivered_at,recipient_name,failure_reason,pod_photo_path,pod_signature_path,pod_at,pod_removed_at,cod_method,cod_amount,recorded_via,recorded_at,gps_lat,gps_lng,gps_accuracy_m,gps_at').in('logistics_job_id',ids);
    const rec=(items||[]).filter(i=>['delivered','failed','partial','collected'].includes(i.delivery_status));
    if(!rec.length) return;
    const {data:mans}=await c.from('manifests').select('id,manifest_number').in('id',[...new Set(rec.map(i=>i.manifest_id))]);
    const {data:ordPc}=await c.from('orders').select('postcode').eq('id',orderId).maybeSingle();
    el.innerHTML=`<hr class="ims__divider"><strong style="font-size:13px">Proof of delivery</strong>
      ${rec.map(i=>{ const {bits,thumbs}=_podDetails(i,ordPc?.postcode||''); const mn=(mans||[]).find(m=>m.id===i.manifest_id)?.manifest_number||'';
        return `<div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;font-size:12px;margin-top:8px">${util().pill(i.delivery_status)}${mn?`<span style="color:var(--ims-text-muted)">${util().sanitize(mn)}</span>`:''}${thumbs}<span>${bits.join(' &middot; ')}</span></div>`; }).join('')}`;
    _fillPodImages(el);
    _fillGpsDistances(el);
  }

  // Stop outcomes and tasks are recorded by the server (driver-api), with
  // exactly the same rules for the office and the driver app: order
  // delivered, stock out, commission, receivable, activity, completion, the
  // customer's email. (This button used to skip the commission and the
  // email, and a failed stop recorded no reason.)
  async function _deliveryApi(action, body){
    try{
      const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
      const {data:got}=await client.auth.getSession();
      const token=got?.session?.access_token;
      if(!token) return {error:'Your session has ended. Please sign in again.'};
      const r=await fetch(`${window.IMS_SUPABASE_URL}/functions/v1/driver-api`,{method:'POST',
        headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`,'apikey':window.IMS_SUPABASE_ANON_KEY},
        body:JSON.stringify({action,...body})});
      const out=await r.json().catch(()=>({}));
      if(!r.ok||out.error) return {error:out.error||(r.status===404?'The delivery service (driver-api) is not set up in Supabase yet.':`Error ${r.status}`)};
      return out;
    }catch(e){ return {error:'StockMaestro could not reach the delivery service. Check the connection and try again.'}; }
  }
  const FAIL_REASONS=['No one home','Access issues','Wrong address','Customer refused delivery','Damaged in transit','Other'];
  async function updateDelivery(itemId,orderId,status){
    if(!status)return;
    if(status==='failed'){
      ui().openModal('Failed delivery',`
        <div class="ims__form-group"><label class="ims__label ims__label--req">Reason</label>
          <select class="ims__select" id="ims-stopfail-reason" onchange="document.getElementById('ims-stopfail-other-wrap').style.display=this.value==='Other'?'':'none'">
            <option value="">Select reason...</option>${FAIL_REASONS.map(r=>`<option value="${r}">${r}</option>`).join('')}
          </select></div>
        <div class="ims__form-group" id="ims-stopfail-other-wrap" style="display:none"><label class="ims__label ims__label--req">Reason</label><input class="ims__input" id="ims-stopfail-other"></div>
        <div class="ims__form-group"><label class="ims__label">Notes</label><input class="ims__input" id="ims-stopfail-notes" placeholder="Optional"></div>
        <div style="font-size:12px;color:var(--ims-text-muted)">The order goes to Failed delivery in Tracking, where you choose to re-attempt or return it.</div>`,
        `<button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
         <button class="ims__btn ims__btn--danger" onclick="window.IMS_LOGISTICS.confirmStopFailed('${util().jsArg(itemId)}',this)"><i class="ti ti-x"></i> Record failed delivery</button>`,'sm');
      return;
    }
    await _recordStop(itemId,status,{});
  }
  async function confirmStopFailed(itemId,btn){
    const sel=document.getElementById('ims-stopfail-reason')?.value||'';
    const other=document.getElementById('ims-stopfail-other')?.value?.trim()||'';
    const reason=sel==='Other'?other:sel;
    if(!reason){ ui().toast('Please give the reason','error'); return; }
    if(btn) btn.disabled=true;
    await _recordStop(itemId,'failed',{reason,notes:document.getElementById('ims-stopfail-notes')?.value?.trim()||null});
  }
  async function _recordStop(itemId,status,extra){
    const r=await _deliveryApi('stop_outcome',{manifest_item_id:itemId,status,...extra});
    if(r.error){ ui().toast(r.error,'error',8000); return; }
    ui().closeModal();
    if(r.already) ui().toast('Stop already recorded: no duplicate actions taken','warning',5000);
    else if(r.already_delivered) ui().toast('Stop marked: order was already delivered, no duplicate actions taken','warning',5000);
    else ui().toast(`Stop marked as ${status.replace(/_/g,' ')}`,'success');
    if((r.stock_problems||[]).length) ui().toast(`Delivered, but stock could not be updated for: ${r.stock_problems.join(', ')}. Please check stock levels.`,'warning',10000);
    if((r.open_tasks||[]).length) ui().toast(`Please confirm the tasks for this stop (done / not done): ${r.open_tasks.join(', ')}`,'warning',10000);
    if(r.manifest_id) await openManifest(r.manifest_id);
  }

  async function deleteManifest(id,manifestNumber){
    if(!window.SM.AUTH.isAdmin()){ui().toast('Admin access required','error');return;}
    // Once the van has departed, its orders are physically on it.
    if(await _manifestDeparted(id)) return;
    ui().confirm(
      `Delete manifest <strong>${util().sanitize(manifestNumber)}</strong>?<br><br>Its orders go back to the Own Truck Queue and revert to Packed. This cannot be undone.`,
      async()=>{
        if(await _manifestDeparted(id)) return;
        const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
        // The orders were marked Dispatched when added: put them back to
        // Packed (they used to stay Dispatched), and their jobs back in the
        // Own Truck Queue (they used to go back to Unassigned).
        const {data:jobs}=await client.from('logistics_jobs').select('id,order_id,is_return_pickup').eq('manifest_id',id);
        for(const j of (jobs||[])){
          if(j.is_return_pickup){ if(j.order_id) await logActivity(j.order_id,'','','return_pickup_unplanned',`Manifest ${manifestNumber} deleted: van pick-up back in the queue`); continue; }
          if(j.order_id){
            await db().update('orders',j.order_id,{status:'packed',dispatched_at:null});
            await logActivity(j.order_id,'dispatched','packed','removed_from_manifest',`Manifest ${manifestNumber} deleted`);
          }
        }
        await client.from('logistics_jobs').update({status:'scheduled',job_type:'own_truck',manifest_id:null}).eq('manifest_id',id);
        await client.from('manifest_items').delete().eq('manifest_id',id);
        await db().remove('manifests',id);
        ui().toast(`Manifest ${manifestNumber} deleted: its orders are back in the Own Truck Queue`,'success');
        loadManifests();
      },
      'Delete manifest', true
    );
  }

  // ─── PRINT MANIFEST ─────────────────────────────────────────────
  async function printManifest(id){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:m}=await db().select('manifests',{
      select:'*, drivers(name,phone), vehicles(registration,make_model), manifest_items(load_order,delivery_status,logistics_jobs(job_type,order_id,is_return_pickup,orders(order_number,customer_name,address,postcode,total_value,payment_status,amended_at,order_items(product_id,product_name,sku,quantity,service_id))))',
      eq:{id},maybeSingle:true,noCompanyFilter:true
    });
    if(!m)return;
    const now=new Date().toLocaleDateString('en-GB');
    const items=(m.manifest_items||[]).sort((a,b)=>a.load_order-b.load_order);
    const deliveryItems=[...items].sort((a,b)=>b.load_order-a.load_order);
    const stopBoxMap={};
    for(const item of items){
      const orderId=item.logistics_jobs?.order_id;
      if(!orderId)continue;
      const stopItems=item.logistics_jobs?.orders?.order_items||[];
      if(stopItems.length)stopBoxMap[orderId]=await util().getOrderBoxSummary(stopItems);
    }
    const allOrderItems=[];
    items.forEach(item=>{(item.logistics_jobs?.orders?.order_items||[]).forEach(oi=>allOrderItems.push(oi));});
    const boxSummary=await util().getOrderBoxSummary(allOrderItems);
    const hasBox=boxSummary.hasData;
    const driverName=m.drivers?.name||'—';
    const dispatchDate=m.dispatched_at?util().formatDate(m.dispatched_at):now;
    const manifestRef=`${driverName} — ${dispatchDate}`;
    const codItems=items.filter(i=>i.logistics_jobs?.orders?.payment_status==='cod');
    const codTotal=codItems.reduce((s,i)=>s+(i.logistics_jobs?.orders?.total_value||0),0);
    const buildHeader=(title)=>window.PRINT.header({
      title,docRef:m.manifest_number||'—',
      rightLabel:'Driver / Vehicle',rightName:driverName,
      rightLines:[`Tel: ${util().sanitize(m.drivers?.phone||'—')}`,`Vehicle: ${util().sanitize(m.vehicles?.registration||'—')} ${util().sanitize(m.vehicles?.make_model||'')}`,``,`<strong>Date:</strong> ${dispatchDate}`,`<strong>Total stops:</strong> ${items.length}`,`<strong>Printed:</strong> ${now}`]
    });
    const loadColWidths=['5%','5%','9%','11%','26%','6%','7%','8%','23%'];
    const loadColumns=[{label:'Load #'},{label:'Stop #'},{label:'Order #'},{label:'SKU'},{label:'Product'},{label:'Qty',align:'r'},{label:'Boxes',align:'r'},{label:'Weight',align:'r'},{label:'Checked ✓',align:'ctr'}];
    const loadTotalsRow=hasBox?['<td colspan="6"><strong>Grand totals</strong></td>',`<td class="r"><strong>${boxSummary.totalBoxes}</strong></td>`,`<td class="r"><strong>${boxSummary.totalWeightKg}kg</strong></td>`,'<td></td>']:['<td colspan="6"><strong>Grand totals</strong></td>','<td></td>','<td></td>','<td></td>'];
    const delivColWidths=['5%','10%','20%','22%','10%','11%','11%','11%'];
    const delivColumns=[{label:'Stop'},{label:'Order #'},{label:'Customer'},{label:'Address'},{label:'Postcode'},{label:'Amount due',align:'r'},{label:'Delivered',align:'ctr'},{label:'Signature',align:'ctr'}];
    const delivRows=deliveryItems.map((item,i)=>{
      const order=item.logistics_jobs?.orders;
      const pickup=!!item.logistics_jobs?.is_return_pickup;
      const isCOD=!pickup&&order?.payment_status==='cod';
      const codBadge=pickup?window.PRINT.badge('COLLECT (return)','warn'):isCOD?window.PRINT.badge('COD','dark'):order?.payment_status==='unpaid'?window.PRINT.badge('ACCOUNT','warn'):window.PRINT.badge('PAID','neutral');
      const amountCell=isCOD?`£${(order?.total_value||0).toFixed(2)}`:`<span style="color:#888">—</span>`;
      return `<tr class="${isCOD?'highlightrow':''}"><td class="ln">${i+1}</td><td>${util().sanitize(order?.order_number||'—')}</td><td>${util().sanitize(order?.customer_name||'—')} ${codBadge}</td><td class="sm">${util().sanitize(order?.address||'—')}${(order?.order_items||[]).some(x=>x.service_id)?`<div><strong>Tasks:</strong> ${(order.order_items||[]).filter(x=>x.service_id).map(x=>util().sanitize(x.product_name)+(x.quantity>1?` x${x.quantity}`:'')).join('; ')}</div>`:''}</td><td class="pc">${util().sanitize(order?.postcode||'—')}</td><td class="r" style="font-weight:${isCOD?'bold':'normal'}">${amountCell}</td><td class="ctr">${window.PRINT.signatureBox()}</td><td class="ctr">${window.PRINT.signatureBox(70)}</td></tr>`;
    });
    const LOAD_ROWS_PER_PAGE=24,DELIV_ROWS_PER_PAGE=22;
    const loadGroups=items.map(item=>{
      const ordId=item.logistics_jobs?.order_id||'';
      const sb=stopBoxMap[ordId];
      const stopNum=items.length-item.load_order+1;
      const orderNum=util().sanitize(item.logistics_jobs?.orders?.order_number||'-')+(item.logistics_jobs?.orders?.amended_at?' (amended)':'');
      const rows=[];
      if(sb&&sb.hasData&&sb.lines&&sb.lines.length){
        sb.lines.forEach((line,li)=>{
          rows.push(`<tr><td class="ln">${li===0?item.load_order:''}</td><td>${li===0?stopNum:''}</td><td>${li===0?orderNum:''}</td><td>${util().sanitize(line.sku||'—')}</td><td>${util().sanitize(line.productName||'—')}</td><td class="r">${line.quantity}</td><td class="r">${line.lineBoxes||'—'}</td><td class="r">${line.lineWeightKg?line.lineWeightKg+'kg':'—'}</td><td class="ctr">${li===0?window.PRINT.signatureBox():''}</td></tr>`);
        });
        rows.push(`<tr class="subtotal"><td colspan="5"><strong>Stop ${stopNum} subtotal</strong></td><td></td><td class="r"><strong>${sb.totalBoxes}</strong></td><td class="r"><strong>${sb.totalWeightKg}kg</strong></td><td></td></tr>`);
        return{rows,rowCount:sb.lines.length+1};
      } else {
        rows.push(`<tr><td class="ln">${item.load_order}</td><td>${stopNum}</td><td>${orderNum}</td><td colspan="5" style="color:#888">No box data recorded</td><td class="ctr">${window.PRINT.signatureBox()}</td></tr>`);
        return{rows,rowCount:1};
      }
    });
    const loadPages=window.PRINT.paginateGrouped(loadGroups,LOAD_ROWS_PER_PAGE);
    const delivPages=window.PRINT.paginate(delivRows,DELIV_ROWS_PER_PAGE);
    const totalPages=loadPages.length+delivPages.length;
    const allPages=[];let pageNum=0;
    loadPages.forEach((pageRows,pi)=>{
      pageNum++;
      const isLast=pi===loadPages.length-1;
      allPages.push(`${buildHeader('Loading Sheet')}<div class="st">Loading sheet — load in this order (load #1 goes deepest into truck) <span class="pg">Page ${pageNum} of ${totalPages}</span></div><hr class="rule">${window.PRINT.table({colWidths:loadColWidths,columns:loadColumns,rows:pageRows,rowsArePrebuilt:true,totalsRow:isLast?loadTotalsRow:null})}<hr class="rule-dbl">${window.PRINT.pageFooter(['StockMaestro © 2026 ABSA Digital',m.manifest_number,manifestRef],pageNum,totalPages)}`);
    });
    delivPages.forEach((pageRows,pi)=>{
      pageNum++;
      const isFirst=pi===0;
      allPages.push(`${buildHeader('Delivery Manifest')}<div class="st">Delivery sheet — stops in delivery order <span class="pg">Page ${pageNum} of ${totalPages}</span></div>${isFirst&&codTotal>0?window.PRINT.summaryBox(`COD collection required: ${codItems.length} stop(s) — total to collect: £${codTotal.toFixed(2)}`):''}<hr class="rule">${window.PRINT.table({colWidths:delivColWidths,columns:delivColumns,rows:pageRows,rowsArePrebuilt:true})}<hr class="rule-dbl">${window.PRINT.pageFooter(['StockMaestro © 2026 ABSA Digital',m.manifest_number,manifestRef],pageNum,totalPages)}`);
    });
    window.PRINT.renderDocument(allPages,`Manifest ${m.manifest_number} — ${manifestRef}`);
  }

  // ─── COURIERS ────────────────────────────────────────────────────
  async function loadCouriers(){
    const content=document.getElementById('ims-log-content');
    if(!content)return;
    const {data:couriers}=await db().select('couriers',{order:{col:'name'}});
    const items=couriers||[];
    content.innerHTML=`
      <div style="display:flex;justify-content:flex-end;margin-bottom:14px">
        <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_LOGISTICS.showCourierForm()"><i class="ti ti-plus"></i> Add courier</button>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:12px">
        ${items.map(c=>`<div class="ims__card">
          <div class="ims__card-header">
            <div class="ims__card-title"><i class="ti ti-truck"></i>${util().sanitize(c.name)}</div>
            <span class="ims__pill ims__pill--${c.is_active?'success':'neutral'}">${c.is_active?'Active':'Inactive'}</span>
          </div>
          <div style="font-size:12px;color:var(--ims-text-sec);margin-bottom:8px">
            ${c.tracking_url_base?`<div><i class="ti ti-link"></i> ${util().sanitize(c.tracking_url_base.slice(0,45))}...</div>`:'<div style="color:var(--ims-warning)">No tracking URL</div>'}
            ${c.requires_postcode?'<div style="color:var(--ims-accent)"><i class="ti ti-map-pin"></i> Requires postcode</div>':''}
            ${c.cs_email?`<div><i class="ti ti-mail"></i> CS: ${util().sanitize(c.cs_email)}</div>`:''}
            ${c.claims_email?`<div><i class="ti ti-file-alert"></i> Claims: ${util().sanitize(c.claims_email)}</div>`:''}
            <div><i class="ti ti-calculator"></i> DIM factor: ${c.dim_factor||5000}</div>
          </div>
          <div class="ims__btn-group">
            <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.showCourierForm('${util().jsArg(c.id)}')"><i class="ti ti-edit"></i> Edit</button>
            <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.manageCourierRates('${util().jsArg(c.id)}','${util().jsArg(c.name)}')"><i class="ti ti-currency-pound"></i> Rates</button>
          </div>
        </div>`).join('')||'<div class="ims__coming-soon"><i class="ti ti-truck"></i><h3>No couriers yet</h3><p>Add courier companies to enable tracking and follow-ups.</p></div>'}
      </div>`;
  }

  async function showCourierForm(existingId=null){
    let c=null;
    if(existingId){const {data}=await db().select('couriers',{eq:{id:existingId},maybeSingle:true,noCompanyFilter:true});c=data;}
    ui().openModal(existingId?`Edit: ${c?.name}`:'Add courier',`
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px">
        <i class="ti ti-info-circle"></i>
        <span>${existingId?'Edit courier details below.':'Step 1 of 2 — Enter courier details. After saving, you will be taken directly to <strong>set up freight rate bands and surcharges</strong>.'}</span>
      </div>
      <div class="ims__form-grid">
        <div class="ims__form-group ims__form-group--full"><label class="ims__label ims__label--req">Courier name</label><input class="ims__input" type="text" id="ims-cur-name" value="${util().sanitize(c?.name||'')}" placeholder="e.g. DHL, Evri, XDP"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Tracking URL base</label><input class="ims__input" type="text" id="ims-cur-url" value="${util().sanitize(c?.tracking_url_base||'')}" placeholder="e.g. https://www.dhl.com/track?id="><span class="ims__input-hint">Enter URL up to where tracking number begins.</span></div>
        <div class="ims__form-group"><label class="ims__label">Requires postcode?</label><select class="ims__select" id="ims-cur-postcode"><option value="false" ${!c?.requires_postcode?'selected':''}>No</option><option value="true" ${c?.requires_postcode?'selected':''}>Yes</option></select></div>
        <div class="ims__form-group"><label class="ims__label">Postcode URL parameter</label><input class="ims__input" type="text" id="ims-cur-pc-param" value="${util().sanitize(c?.postcode_param||'')}" placeholder="e.g. &postcode="></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Tracking page type</label><select class="ims__select" id="ims-cur-formonly"><option value="false" ${!c?.tracking_form_only?'selected':''}>Direct link - tracking number goes in the URL</option><option value="true" ${c?.tracking_form_only?'selected':''}>Form only - staff paste details into the courier's page</option></select><span class="ims__input-hint">Choose "Form only" when the courier's result page uses a generated token that cannot be linked to directly (e.g. V1 Distribution). The tracking button then copies the tracking number and opens their form.</span></div>
        <div class="ims__form-group"><label class="ims__label">DIM factor</label><input class="ims__input" type="number" id="ims-cur-dim" value="${c?.dim_factor||5000}"><span class="ims__input-hint">L×W×H ÷ DIM factor = volumetric weight</span></div>
        <div class="ims__form-group"><label class="ims__label">Customer services email</label><input class="ims__input" type="email" id="ims-cur-cs" value="${util().sanitize(c?.cs_email||'')}"></div>
        <div class="ims__form-group"><label class="ims__label">Claims email</label><input class="ims__input" type="email" id="ims-cur-claims" value="${util().sanitize(c?.claims_email||'')}"></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveCourier('${util().jsArg(existingId||'')}')"><i class="ti ti-check"></i> ${existingId?'Save changes':'Save & set up rates'}</button>
    `,'lg');
  }

  async function saveCourier(existingId){
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const name=g('ims-cur-name');
    if(!name){ui().toast('Courier name is required','error');return;}
    const data={name,tracking_url_base:g('ims-cur-url')||null,requires_postcode:g('ims-cur-postcode')==='true',postcode_param:g('ims-cur-pc-param')||null,tracking_form_only:g('ims-cur-formonly')==='true',dim_factor:parseFloat(g('ims-cur-dim'))||5000,cs_email:g('ims-cur-cs')||null,claims_email:g('ims-cur-claims')||null,auto_track_failures:0};
    let savedId=existingId;
    if(existingId){
      const {error}=await db().update('couriers',existingId,data);
      if(error){ui().toast('Failed: '+error.message,'error');return;}
      ui().closeModal();ui().toast('Courier updated','success');loadCouriers();
    } else {
      const {data:newCourier,error}=await db().insert('couriers',data);
      if(error){ui().toast('Failed: '+error.message,'error');return;}
      savedId=newCourier.id;
      ui().closeModal();
      ui().toast('Courier saved — now set up freight rates','success');
      setTimeout(()=>manageCourierRates(savedId,name),400);
    }
  }

  async function _manageWeightRates(courierId,courierName){
    const {data:bands}=await db().select('courier_rate_bands',{eq:{courier_id:courierId},order:{col:'weight_from'},noCompanyFilter:true});
    const {data:surcharges}=await db().select('courier_surcharges',{eq:{courier_id:courierId},noCompanyFilter:true});
    const items=bands||[];
    const surchargeItems=surcharges||[];
    ui().openModal(`Rates: ${courierName}`,`
      ${window.SM.AUTH.isAdmin()?`<div class="ims__alert ims__alert--info" style="margin-bottom:12px"><i class="ti ti-info-circle"></i><span>This courier is priced by weight. If it prices by product type and zone instead (like V1), <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.rcCreateCard('${util().jsArg(courierId)}','${util().jsArg(courierName)}')">Set up a rate card</button></span></div>`:''}
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
        <strong style="font-size:13px">Weight bands</strong>
        <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.addRateBand('${util().jsArg(courierId)}')"><i class="ti ti-plus"></i> Add band</button>
      </div>
      <div class="ims__table-wrap" style="margin-bottom:16px">
        <table class="ims__table">
          <thead><tr><th>From (kg)</th><th>To (kg)</th><th>Base rate (£)</th><th>Per kg over (£)</th><th></th></tr></thead>
          <tbody id="ims-rate-bands">${items.map(b=>`<tr><td>${b.weight_from}</td><td>${b.weight_to||'∞'}</td><td>£${(b.base_rate||0).toFixed(2)}</td><td>${b.per_kg_over?'£'+b.per_kg_over.toFixed(2):'—'}</td><td><button class="ims__btn ims__btn--danger ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.deleteRateBand('${util().jsArg(b.id)}','${util().jsArg(courierId)}','${util().jsArg(courierName)}')"><i class="ti ti-trash"></i></button></td></tr>`).join('')||'<tr><td colspan="5" class="ims__table-empty">No rate bands configured</td></tr>'}
          </tbody>
        </table>
      </div>
      <strong style="font-size:13px">Surcharges</strong>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:8px">
        ${['fuel','remote_area','residential','saturday','peak_season','oversize','insurance'].map(type=>{
          const s=surchargeItems.find(x=>x.surcharge_type===type);
          const labels={fuel:'Fuel surcharge',remote_area:'Remote area',residential:'Residential',saturday:'Saturday delivery',peak_season:'Peak season',oversize:'Oversize',insurance:'Insurance/cover'};
          return `<div style="display:flex;align-items:center;justify-content:space-between;padding:8px;background:var(--ims-surface2);border-radius:var(--ims-radius);border:1px solid var(--ims-border)"><div><div style="font-size:12px;font-weight:500">${labels[type]}</div><div style="font-size:11px;color:var(--ims-text-muted)">${s?util().formatCurrency(s.value)+' '+(s.value_type==='percent'?'%':'flat'):'Not set'}</div></div><div style="display:flex;gap:4px;align-items:center">${s?`<span class="ims__pill ims__pill--${s.is_active?'success':'neutral'}">${s.is_active?'On':'Off'}</span>`:''}<button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.editSurcharge('${util().jsArg(courierId)}','${util().jsArg(type)}','${util().jsArg(courierName)}')"><i class="ti ti-edit"></i></button></div></div>`;
        }).join('')}
      </div>
    `,`<button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal()">Close</button>`,'lg');
  }

  // ─── SERVICES PRICE LIST ──────────────────────────────────────────
  // Services sold to customers (assembly, old furniture removal...): your
  // customer price, per order or per item, and an optional own-van cost
  // (e.g. a tip fee). A courier's cost for the same service lives in its
  // rate card (Extras), linked to the service. Everyone can view; only an
  // Admin can change.
  async function loadServices(){
    const content=document.getElementById('ims-log-content'); if(!content) return;
    const {data:svcs,error}=await _rcDb().from('services').select('*').eq('company_id',window.SM.AUTH.company?.id).order('sort_order',{ascending:true});
    if(error){ content.innerHTML=`<div class="ims__alert ims__alert--warning"><i class="ti ti-alert-triangle"></i><span>Services could not be loaded (${util().sanitize(error.message)}).</span></div>`; return; }
    const adm=_rcAdmin(), dis=adm?'':'disabled';
    content.innerHTML=`
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;gap:10px;flex-wrap:wrap">
        <div style="font-size:12px;color:var(--ims-text-muted);max-width:640px">Your prices for services sold to customers. Prices follow each order's VAT setting. The courier's cost for the same service is in its rate card (Extras), linked to the service.${adm?'':' <strong>View only: only an Admin can change prices.</strong>'}</div>
        <span id="ims-svc-saved" style="font-size:11px;color:var(--ims-text-muted)"></span>
      </div>
      <div class="ims__table-wrap"><table class="ims__table">
        <thead><tr><th>Service</th><th style="width:130px">Customer price (£)</th><th style="width:140px">Charged per</th><th style="width:150px">Own-van cost (£)</th><th style="width:90px">Active</th></tr></thead>
        <tbody>${(svcs||[]).map(v=>`<tr>
          <td><input class="ims__input ims__input--sm" value="${util().sanitize(v.name)}" ${dis} onchange="window.IMS_LOGISTICS.saveService('${v.id}','name',this.value)"></td>
          <td><input class="ims__input ims__input--sm" type="number" min="0" step="0.01" value="${v.customer_price??0}" ${dis} onchange="window.IMS_LOGISTICS.saveService('${v.id}','customer_price',this.value)"></td>
          <td><select class="ims__select" style="font-size:12px" ${dis} onchange="window.IMS_LOGISTICS.saveService('${v.id}','per',this.value)"><option value="order" ${v.per==='order'?'selected':''}>Per order</option><option value="item" ${v.per==='item'?'selected':''}>Per item</option></select></td>
          <td><input class="ims__input ims__input--sm" type="number" min="0" step="0.01" value="${v.own_van_cost??''}" placeholder="none" ${dis} onchange="window.IMS_LOGISTICS.saveService('${v.id}','own_van_cost',this.value)"></td>
          <td><input type="checkbox" ${v.is_active?'checked':''} ${dis} onchange="window.IMS_LOGISTICS.saveService('${v.id}','is_active',this.checked)"></td>
        </tr>`).join('')||'<tr><td colspan="5" class="ims__table-empty">No services yet</td></tr>'}</tbody>
      </table></div>
      ${adm?`<button class="ims__btn ims__btn--secondary ims__btn--sm" style="margin-top:8px" onclick="window.IMS_LOGISTICS.addService()"><i class="ti ti-plus"></i> Add service</button>`:''}`;
  }
  async function saveService(id, field, value){
    if(!_rcAdmin()) return;
    let v=value;
    if(field==='customer_price') v=_rcNum(value)??0;
    if(field==='own_van_cost') v=_rcNum(value);
    if(field==='name'){ v=String(value||'').trim(); if(!v){ ui().toast('A service needs a name','warning'); return; } }
    const {error}=await _rcDb().from('services').update({[field]:v}).eq('id',id);
    if(error){ ui().toast('Not saved: '+error.message,'error',8000); return; }
    const el=document.getElementById('ims-svc-saved'); if(el) el.textContent='Saved '+new Date().toLocaleTimeString().slice(0,5);
  }
  async function addService(){
    if(!_rcAdmin()) return;
    const db_=_rcDb(), co=window.SM.AUTH.company?.id;
    const {data:ex}=await db_.from('services').select('id').eq('company_id',co);
    const {error}=await db_.from('services').insert({company_id:co,name:'New service',customer_price:0,per:'order',sort_order:(ex||[]).length});
    if(error){ ui().toast('Not saved: '+error.message,'error',8000); return; }
    loadServices();
  }

  // Driver tasks on a manifest stop: each service on the order, confirmed
  // done or not done. Not done flags the order (refund is a separate step,
  // from the order itself; nothing is refunded automatically).
  async function setServiceStatus(itemId, status, orderId, serviceName, manifestId){
    const r=await _deliveryApi('task',{order_item_id:itemId,status});
    if(r.error){ ui().toast(r.error,'error',8000); return; }
    if(status==='not_done') ui().toast(`${serviceName} marked NOT done. If the customer paid for it, refund it from the order.`,'warning',9000);
    else ui().toast(`${serviceName}: done.${r.own_van_cost>0?` Own-van cost ${util().formatCurrency(r.own_van_cost)} recorded.`:''}`,'success',r.own_van_cost>0?7000:4000);
    if(manifestId) openManifest(manifestId);
  }


  // ─── COURIER RATE CARDS ────────────────────────────────────────────
  // For couriers that price by product type and zone (e.g. V1). Set once
  // per courier: zones (with postcodes), product types (with an optional
  // weight limit) and which product is which type. Per dated version
  // ("valid from"): the prices grid, extras (our cost), floor charges, fuel
  // %, VAT and collections settings, so older deliveries are always
  // checked against the rates in force at the time. Couriers without a
  // rate card keep the weight-band screen. Everyone can view; only an
  // Admin can change (the database enforces this too).
  const RC_HIGHLANDS = ['AB31','AB32','AB33','AB34','AB35','AB36','AB37','AB38','AB44','AB45','AB46','AB47','AB48','AB49','AB50','AB51','AB52','AB53','AB54','AB55','AB56','FK17','FK18','FK19','FK20','FK21','G83','IV1','IV2','IV3','IV4','IV5','IV6','IV7','IV8','IV9','IV10','IV11','IV12','IV13','IV14','IV15','IV16','IV17','IV18','IV19','IV20','IV21','IV22','IV23','IV24','IV25','IV26','IV27','IV28','IV30','IV31','IV32','IV36','IV40','IV52','IV53','IV54','IV63','KW1','KW2','KW3','KW4','KW5','KW6','KW7','KW8','KW9','KW10','KW11','KW12','KW13','KW14','PA21','PA22','PA23','PA24','PA25','PA26','PA27','PA28','PA29','PA30','PA31','PA32','PA33','PA34','PA35','PA36','PA37','PA38','PA39','PA40','PH17','PH18','PH19','PH20','PH21','PH22','PH23','PH24','PH25','PH26','PH30','PH31','PH32','PH33','PH34','PH35','PH36','PH37','PH38','PH39','PH40','PH41','PH49','PH50'];
  const RC_NOT_DELIVERED = ['BT','IM','JE','GY','HS','ZE','KW15','KW16','KW17','IV41','IV42','IV43','IV44','IV45','IV46','IV47','IV48','IV49','IV50','IV51','IV55','IV56','PA20','PA41','PA42','PA43','PA44','PA45','PA46','PA47','PA48','PA49','PA60','PA61','PA62','PA63','PA64','PA65','PA66','PA67','PA68','PA69','PA70','PA71','PA72','PA73','PA74','PA75','PA76','PA77','PA78','KA27','KA28','PH42','PH43','PH44','PO30','PO31','PO32','PO33','PO34','PO35','PO36','PO37','PO38','PO39','PO40','PO41','TR21','TR22','TR23','TR24','TR25'];
  let _rc = null;
  const _rcDb = () => window.supabase.createClient(window.IMS_SUPABASE_URL, window.IMS_SUPABASE_ANON_KEY);
  const _rcAdmin = () => window.SM.AUTH.isAdmin();
  const _rcPc = v => String(v || '').toUpperCase().replace(/\s+/g, '');
  const _rcValidPrefix = p => /^[A-Z]{1,2}(\d{1,2}[A-Z]?)?$/.test(p);
  const _rcToday = () => new Date().toISOString().split('T')[0];
  const _rcNum = v => (v === '' || v === null || v === undefined || isNaN(parseFloat(v))) ? null : Math.round(parseFloat(v) * 100) / 100;
  const _rcSortPc = (a, b) => { const m = x => x.match(/^([A-Z]+)(\d*)(.*)$/) || [x, x, '', '']; const A = m(a), B = m(b); return A[1].localeCompare(B[1]) || (parseInt(A[2] || 0) - parseInt(B[2] || 0)) || A[3].localeCompare(B[3]); };
  function _rcSaved(err) {
    if (err) { ui().toast('Not saved: ' + (err.message || err), 'error', 8000); return false; }
    const el = document.getElementById('ims-rc-saved'); if (el) el.textContent = 'Saved ' + new Date().toLocaleTimeString().slice(0, 5);
    return true;
  }

  async function manageCourierRates(courierId, courierName) {
    const { data: cards } = await _rcDb().from('courier_rate_cards').select('id,pricing_method').eq('courier_id', courierId);
    if (!(cards || []).some(c => c.pricing_method === 'type_zone')) return _manageWeightRates(courierId, courierName);
    _rc = { courierId, courierName, companyId: window.SM.AUTH.company?.id, tab: 'prices', cardId: null, prodSearch: '', prodMissing: false };
    await _rcLoad();
    _rcOpen();
  }

  // Switch a weight-priced courier to a rate card: standard zones pre-filled.
  async function rcCreateCard(courierId, courierName) {
    if (!_rcAdmin()) { ui().toast('Only an Admin can change courier rates', 'error'); return; }
    ui().confirm(`Set up a rate card for <strong>${util().sanitize(courierName)}</strong>, priced by product type and zone? Zones start pre-filled (Mainland, Highlands and remote, Not delivered); you then add product types and prices. Its weight bands stay saved.`, async () => {
      const db_ = _rcDb(), co = window.SM.AUTH.company?.id;
      const z = async (name, kind, sort) => (await db_.from('courier_zones').insert({ company_id: co, courier_id: courierId, name, kind, sort_order: sort }).select('id').single()).data?.id;
      const { data: hasZones } = await db_.from('courier_zones').select('id').eq('courier_id', courierId).limit(1);
      if (!(hasZones || []).length) {
        await z('Mainland', 'default', 0);
        const hi = await z('Highlands and remote', 'zone', 1), no = await z('Not delivered', 'not_served', 2);
        const rows = [...RC_HIGHLANDS.map(p => ({ prefix: p, zone_id: hi })), ...RC_NOT_DELIVERED.map(p => ({ prefix: p, zone_id: no }))]
          .map(r => ({ ...r, company_id: co, courier_id: courierId }));
        const { error } = await db_.from('courier_zone_postcodes').insert(rows);
        if (error) { ui().toast('Zones could not be set up: ' + error.message, 'error', 8000); return; }
      }
      const { error } = await db_.from('courier_rate_cards').insert({ company_id: co, courier_id: courierId, pricing_method: 'type_zone', valid_from: _rcToday() });
      if (error) { ui().toast('The rate card could not be created: ' + error.message, 'error', 8000); return; }
      ui().toast(`${courierName}: rate card created`, 'success');
      manageCourierRates(courierId, courierName);
    }, 'Set up rate card', false);
  }

  async function _rcLoad() {
    const db_ = _rcDb(), id = _rc.courierId;
    const [cards, zones, pcs, types] = await Promise.all([
      db_.from('courier_rate_cards').select('*').eq('courier_id', id).eq('pricing_method', 'type_zone').order('valid_from', { ascending: false }),
      db_.from('courier_zones').select('*').eq('courier_id', id).order('sort_order', { ascending: true }),
      db_.from('courier_zone_postcodes').select('id,zone_id,prefix').eq('courier_id', id),
      db_.from('courier_product_types').select('*').eq('courier_id', id).order('sort_order', { ascending: true })
    ]);
    _rc.cards = cards.data || []; _rc.zones = zones.data || []; _rc.pcs = pcs.data || []; _rc.types = types.data || [];
    const inForce = _rc.cards.find(c => String(c.valid_from) <= _rcToday()) || _rc.cards[_rc.cards.length - 1];
    if (!_rc.cardId || !_rc.cards.some(c => c.id === _rc.cardId)) _rc.cardId = inForce?.id || null;
    _rc.inForceId = inForce?.id || null;
    const [rates, extras] = await Promise.all([
      db_.from('courier_card_rates').select('*').eq('card_id', _rc.cardId),
      db_.from('courier_card_extras').select('*').eq('card_id', _rc.cardId).order('sort_order', { ascending: true })
    ]);
    _rc.rates = rates.data || []; _rc.extras = extras.data || [];
    const { data: svcs } = await db_.from('services').select('id,name').eq('company_id', _rc.companyId).order('sort_order', { ascending: true });
    _rc.services = svcs || [];
  }

  function _rcCard() { return _rc.cards.find(c => c.id === _rc.cardId) || {}; }
  function _rcDis() { return _rcAdmin() ? '' : 'disabled'; }

  function _rcOpen() {
    const tabs = [['prices', 'Prices'], ['zones', 'Zones'], ['products', 'Products'], ['extras', 'Extras'], ['settings', 'Settings']];
    ui().openModal(`Rate card: ${_rc.courierName}`, `
      <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;justify-content:space-between;margin-bottom:10px">
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
          <label class="ims__label" style="margin:0">Rates</label>
          <select class="ims__select" style="width:auto" onchange="window.IMS_LOGISTICS.rcSelectCard(this.value)">
            ${_rc.cards.map(c => `<option value="${c.id}" ${c.id === _rc.cardId ? 'selected' : ''}>valid from ${util().formatDate(c.valid_from)}${c.id === _rc.inForceId ? ' (in force)' : String(c.valid_from) > _rcToday() ? ' (future)' : ' (older)'}</option>`).join('')}
          </select>
          ${_rcAdmin() ? `<input class="ims__input" type="date" id="ims-rc-newdate" style="width:auto"><button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.rcNewVersion()" title="Copy these rates into a new version from the chosen date (e.g. when the courier sends a new price list)"><i class="ti ti-copy"></i> New rates from date</button>` : ''}
        </div>
        <span id="ims-rc-saved" style="font-size:11px;color:var(--ims-text-muted)">${_rcAdmin() ? 'Changes save as you go' : 'View only: only an Admin can change rates'}</span>
      </div>
      <div class="ims__tabs" id="ims-rc-tabs" style="margin-bottom:12px">
        ${tabs.map(([k, l]) => `<button class="ims__tab ${k === _rc.tab ? 'ims__tab--active' : ''}" data-tab="${k}" onclick="window.IMS_LOGISTICS.rcTab('${k}')">${l}</button>`).join('')}
      </div>
      <div id="ims-rc-body"></div>
    `, `<button class="ims__btn ims__btn--primary" onclick="window.SM.UI.closeModal()">Close</button>`, 'xl');
    _rcRenderTab();
  }

  function rcTab(tab) {
    _rc.tab = tab;
    document.querySelectorAll('#ims-rc-tabs .ims__tab').forEach(b => b.classList.toggle('ims__tab--active', b.dataset.tab === tab));
    _rcRenderTab();
  }
  async function rcSelectCard(cardId) { _rc.cardId = cardId; await _rcLoad(); _rcOpen(); }

  function _rcRenderTab() {
    const body = document.getElementById('ims-rc-body'); if (!body) return;
    if (_rc.tab === 'prices') body.innerHTML = _rcPricesHtml();
    else if (_rc.tab === 'zones') body.innerHTML = _rcZonesHtml();
    else if (_rc.tab === 'extras') body.innerHTML = _rcExtrasHtml();
    else if (_rc.tab === 'settings') body.innerHTML = _rcSettingsHtml();
    else if (_rc.tab === 'products') { body.innerHTML = '<div class="ims__loading"><i class="ti ti-loader-2 ims__spin"></i></div>'; _rcProducts(); }
  }

  // ── Prices (types x zones) ──
  function _rcPricesHtml() {
    const zones = _rc.zones.filter(z => z.kind !== 'not_served'); const dis = _rcDis(); const card = _rcCard();
    const price = (t, z) => _rc.rates.find(r => r.type_id === t && r.zone_id === z)?.price;
    return `
      <div style="font-size:12px;color:var(--ims-text-muted);margin-bottom:8px">Prices ${card.prices_exclude_vat ? '<strong>exclude VAT</strong>' : 'include VAT'}; fuel ${card.fuel_pct || 0}% is added on top. A blank price means "no price set yet".</div>
      <div class="ims__table-wrap"><table class="ims__table">
        <thead><tr><th>Product type</th><th style="width:90px">Up to kg</th>${zones.map(z => `<th style="width:120px">${util().sanitize(z.name)} (£)</th>`).join('')}<th></th></tr></thead>
        <tbody>${_rc.types.map(t => `<tr>
          <td><input class="ims__input ims__input--sm" value="${util().sanitize(t.name)}" ${dis} onchange="window.IMS_LOGISTICS.rcSaveType('${t.id}','name',this.value)"></td>
          <td><input class="ims__input ims__input--sm" type="number" min="0" step="1" value="${t.max_weight_kg ?? ''}" placeholder="-" ${dis} onchange="window.IMS_LOGISTICS.rcSaveType('${t.id}','max_weight_kg',this.value)"></td>
          ${zones.map(z => `<td><input class="ims__input ims__input--sm" type="number" min="0" step="0.01" value="${price(t.id, z.id) ?? ''}" placeholder="not set" ${dis} onchange="window.IMS_LOGISTICS.rcSavePrice('${t.id}','${z.id}',this.value)"></td>`).join('')}
          <td>${_rcAdmin() ? `<button class="ims__btn ims__btn--ghost ims__btn--sm ims__btn--icon" title="Delete this type" onclick="window.IMS_LOGISTICS.rcDeleteType('${t.id}','${util().jsArg(t.name)}')"><i class="ti ti-trash"></i></button>` : ''}</td>
        </tr>`).join('') || `<tr><td colspan="${zones.length + 3}" class="ims__table-empty">No product types yet</td></tr>`}</tbody>
      </table></div>
      ${_rcAdmin() ? `<button class="ims__btn ims__btn--secondary ims__btn--sm" style="margin-top:8px" onclick="window.IMS_LOGISTICS.rcAddType()"><i class="ti ti-plus"></i> Add product type</button>` : ''}`;
  }
  async function rcSaveType(typeId, field, value) {
    if (!_rcAdmin()) return;
    const v = field === 'max_weight_kg' ? _rcNum(value) : String(value || '').trim();
    if (field === 'name' && !v) { ui().toast('A product type needs a name', 'warning'); return; }
    const { error } = await _rcDb().from('courier_product_types').update({ [field]: v }).eq('id', typeId);
    if (_rcSaved(error)) { const t = _rc.types.find(x => x.id === typeId); if (t) t[field] = v; }
  }
  async function rcSavePrice(typeId, zoneId, value) {
    if (!_rcAdmin()) return;
    const price = _rcNum(value); const db_ = _rcDb();
    const ex = _rc.rates.find(r => r.type_id === typeId && r.zone_id === zoneId);
    const res = ex ? await db_.from('courier_card_rates').update({ price }).eq('id', ex.id).select('*')
                   : await db_.from('courier_card_rates').insert({ company_id: _rc.companyId, card_id: _rc.cardId, type_id: typeId, zone_id: zoneId, price }).select('*');
    if (_rcSaved(res.error)) { if (ex) ex.price = price; else if (res.data?.[0]) _rc.rates.push(res.data[0]); }
  }
  async function rcAddType() {
    if (!_rcAdmin()) return;
    const { data, error } = await _rcDb().from('courier_product_types').insert({ company_id: _rc.companyId, courier_id: _rc.courierId, name: 'New product type', sort_order: _rc.types.length }).select('*');
    if (_rcSaved(error) && data?.[0]) { _rc.types.push(data[0]); _rcRenderTab(); }
  }
  function rcDeleteType(typeId, name) {
    if (!_rcAdmin()) return;
    ui().confirm(`Delete the product type <strong>${util().sanitize(name)}</strong>? Its prices (in every version) and its product links are removed too.`, async () => {
      const { error } = await _rcDb().from('courier_product_types').delete().eq('id', typeId);
      if (_rcSaved(error)) { await _rcLoad(); _rcOpen(); }
    }, 'Delete', true);
  }

  // ── Zones ──
  function _rcZonesHtml() {
    const dis = _rcDis();
    return `
      <div style="font-size:12px;color:var(--ims-text-muted);margin-bottom:10px">A postcode matches its most specific entry: a district (PH19) beats an area (PH). Anything not listed falls in the default zone. Adding a postcode that is in another zone moves it.</div>
      ${_rc.zones.map(z => {
        const list = _rc.pcs.filter(p => p.zone_id === z.id).map(p => p.prefix).sort(_rcSortPc);
        return `<div class="ims__card" style="margin-bottom:10px">
          <div style="display:flex;gap:8px;align-items:center;justify-content:space-between;margin-bottom:6px">
            <div style="display:flex;gap:8px;align-items:center">
              <input class="ims__input ims__input--sm" style="width:220px;font-weight:600" value="${util().sanitize(z.name)}" ${dis} onchange="window.IMS_LOGISTICS.rcRenameZone('${z.id}',this.value)">
              <span class="ims__pill ims__pill--${z.kind === 'not_served' ? 'danger' : z.kind === 'default' ? 'success' : 'info'}" style="font-size:10px">${z.kind === 'default' ? 'Default: everything else' : z.kind === 'not_served' ? 'Not delivered' : list.length + ' postcodes'}</span>
            </div>
            ${_rcAdmin() && z.kind !== 'default' ? `<button class="ims__btn ims__btn--ghost ims__btn--sm ims__btn--icon" title="Delete zone" onclick="window.IMS_LOGISTICS.rcDeleteZone('${z.id}','${util().jsArg(z.name)}')"><i class="ti ti-trash"></i></button>` : ''}
          </div>
          ${z.kind === 'default' ? '<div style="font-size:12px;color:var(--ims-text-muted)">Every postcode not listed in another zone.</div>' : `
          <div style="display:flex;flex-wrap:wrap;gap:4px;max-height:140px;overflow:auto;margin-bottom:6px">
            ${list.map(p => `<span class="ims__pill ims__pill--neutral" style="font-size:11px">${p}${_rcAdmin() ? ` <a href="javascript:void(0)" style="text-decoration:none;color:inherit" title="Remove" onclick="window.IMS_LOGISTICS.rcRemovePostcode('${util().jsArg(p)}')">&times;</a>` : ''}</span>`).join('') || '<span style="font-size:12px;color:var(--ims-text-muted)">No postcodes yet</span>'}
          </div>
          ${_rcAdmin() ? `<div style="display:flex;gap:6px"><input class="ims__input ims__input--sm" id="ims-rc-add-${z.id}" placeholder="Add postcodes, e.g. IV1, PH19, HS" style="max-width:320px"><button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.rcAddPostcodes('${z.id}')">Add</button></div>` : ''}`}
        </div>`;
      }).join('')}
      ${_rcAdmin() ? `<button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.rcAddZone()"><i class="ti ti-plus"></i> Add zone</button>` : ''}`;
  }
  async function rcRenameZone(zoneId, name) {
    if (!_rcAdmin()) return; name = String(name || '').trim(); if (!name) { ui().toast('A zone needs a name', 'warning'); return; }
    const { error } = await _rcDb().from('courier_zones').update({ name }).eq('id', zoneId);
    if (_rcSaved(error)) { const z = _rc.zones.find(x => x.id === zoneId); if (z) z.name = name; }
  }
  async function rcAddPostcodes(zoneId) {
    if (!_rcAdmin()) return;
    const input = document.getElementById('ims-rc-add-' + zoneId);
    const wanted = [...new Set(String(input?.value || '').split(/[\s,;]+/).map(_rcPc).filter(Boolean))];
    if (!wanted.length) return;
    const bad = wanted.filter(p => !_rcValidPrefix(p));
    if (bad.length) { ui().toast(`Not a postcode area or district: ${bad.join(', ')}. Use the first part only, e.g. IV1 or PH19.`, 'error', 8000); return; }
    const db_ = _rcDb(); let moved = 0, added = 0;
    for (const p of wanted) {
      const ex = _rc.pcs.find(x => x.prefix === p);
      if (ex && ex.zone_id === zoneId) continue;
      const res = ex ? await db_.from('courier_zone_postcodes').update({ zone_id: zoneId }).eq('id', ex.id)
                     : await db_.from('courier_zone_postcodes').insert({ company_id: _rc.companyId, courier_id: _rc.courierId, zone_id: zoneId, prefix: p });
      if (!_rcSaved(res.error)) break;
      ex ? moved++ : added++;
    }
    if (moved) ui().toast(`${moved} postcode(s) moved here from another zone`, 'info');
    await _rcLoad(); _rcRenderTab();
  }
  async function rcRemovePostcode(prefix) {
    if (!_rcAdmin()) return;
    const ex = _rc.pcs.find(x => x.prefix === prefix); if (!ex) return;
    const { error } = await _rcDb().from('courier_zone_postcodes').delete().eq('id', ex.id);
    if (_rcSaved(error)) { _rc.pcs = _rc.pcs.filter(x => x.id !== ex.id); _rcRenderTab(); }
  }
  async function rcAddZone() {
    if (!_rcAdmin()) return;
    const { error } = await _rcDb().from('courier_zones').insert({ company_id: _rc.companyId, courier_id: _rc.courierId, name: 'New zone', kind: 'zone', sort_order: _rc.zones.length });
    if (_rcSaved(error)) { await _rcLoad(); _rcRenderTab(); }
  }
  function rcDeleteZone(zoneId, name) {
    if (!_rcAdmin()) return;
    ui().confirm(`Delete the zone <strong>${util().sanitize(name)}</strong>? Its postcodes fall back to the default zone, and its prices (in every version) are removed.`, async () => {
      const { error } = await _rcDb().from('courier_zones').delete().eq('id', zoneId);
      if (_rcSaved(error)) { await _rcLoad(); _rcOpen(); }
    }, 'Delete', true);
  }

  // ── Products: which product is which type ──
  async function _rcProducts() {
    const db_ = _rcDb();
    const [prods, boxes, map] = await Promise.all([
      db_.from('products').select('id,name,sku').eq('company_id', _rc.companyId).order('name', { ascending: true }),
      db_.from('product_boxes').select('product_id,weight_kg'),
      db_.from('courier_product_map').select('id,product_id,type_id').eq('courier_id', _rc.courierId)
    ]);
    const weight = {}; (boxes.data || []).forEach(b => { weight[b.product_id] = (weight[b.product_id] || 0) + (Number(b.weight_kg) || 0); });
    _rc.products = (prods.data || []).map(p => ({ ...p, kg: weight[p.id] ? Math.round(weight[p.id] * 100) / 100 : null }));
    _rc.map = map.data || [];
    _rcProductsRender();
  }
  // The smallest weight band that fits the product's box weight.
  function _rcSuggest(kg) {
    if (!kg) return null;
    return _rc.types.filter(t => t.max_weight_kg != null).sort((a, b) => a.max_weight_kg - b.max_weight_kg).find(t => kg <= t.max_weight_kg) || null;
  }
  function _rcProductsRender() {
    const body = document.getElementById('ims-rc-body'); if (!body || _rc.tab !== 'products') return;
    const typeOf = pid => _rc.map.find(m => m.product_id === pid)?.type_id || '';
    const q = _rc.prodSearch.toLowerCase();
    const list = _rc.products.filter(p => (!q || `${p.name} ${p.sku}`.toLowerCase().includes(q)) && (!_rc.prodMissing || !typeOf(p.id)));
    const shown = list.slice(0, 300);
    const missing = _rc.products.filter(p => !typeOf(p.id)).length;
    const sugg = _rc.products.filter(p => !typeOf(p.id) && _rcSuggest(p.kg)).length;
    const opts = sel => `<option value="">- none -</option>` + _rc.types.map(t => `<option value="${t.id}" ${t.id === sel ? 'selected' : ''}>${util().sanitize(t.name)}</option>`).join('');
    const dis = _rcDis();
    body.innerHTML = `
      <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:8px">
        <input class="ims__input ims__input--sm" style="max-width:220px" placeholder="Search name or SKU" value="${util().sanitize(_rc.prodSearch)}" oninput="window.IMS_LOGISTICS.rcProdFilter(this.value,null)">
        <label style="font-size:12px;display:flex;gap:4px;align-items:center"><input type="checkbox" ${_rc.prodMissing ? 'checked' : ''} onchange="window.IMS_LOGISTICS.rcProdFilter(null,this.checked)"> Only without a type (${missing})</label>
        <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_LOGISTICS.rcExportProducts()"><i class="ti ti-download"></i> Export to Excel</button>
        ${_rcAdmin() ? `<label class="ims__btn ims__btn--ghost ims__btn--sm" style="cursor:pointer"><i class="ti ti-upload"></i> Import<input type="file" accept=".csv" style="display:none" onchange="window.IMS_LOGISTICS.rcImportProducts(this)"></label>` : ''}
      </div>
      ${_rcAdmin() ? `<div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:8px">
        <span style="font-size:12px">Selected:</span><select class="ims__select" id="ims-rc-bulk" style="width:auto;font-size:12px">${opts('')}</select>
        <button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.rcBulkSet()">Set selected</button>
        ${sugg ? `<button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.rcApplySuggestions()" title="Products without a type, using their box weight">Use all weight suggestions (${sugg})</button>` : ''}
      </div>` : ''}
      <div class="ims__table-wrap" style="max-height:55vh;overflow:auto"><table class="ims__table">
        <thead><tr>${_rcAdmin() ? '<th style="width:28px"><input type="checkbox" onchange="document.querySelectorAll(\'.ims-rc-pick\').forEach(c=>c.checked=this.checked)"></th>' : ''}<th>Product</th><th>SKU</th><th>Weight</th><th>${util().sanitize(_rc.courierName)} type</th></tr></thead>
        <tbody>${shown.map(p => { const cur = typeOf(p.id), sg = !cur ? _rcSuggest(p.kg) : null; return `<tr>
          ${_rcAdmin() ? `<td><input type="checkbox" class="ims-rc-pick" value="${p.id}"></td>` : ''}
          <td>${util().sanitize(p.name || '-')}</td><td style="font-size:12px">${util().sanitize(p.sku || '-')}</td>
          <td style="font-size:12px">${p.kg != null ? p.kg + ' kg' : '-'}</td>
          <td><select class="ims__select" style="font-size:12px;width:auto;max-width:260px" ${dis} onchange="window.IMS_LOGISTICS.rcSetType('${p.id}',this.value)">${opts(cur)}</select>
            ${sg && _rcAdmin() ? ` <button class="ims__btn ims__btn--ghost ims__btn--sm" onclick="window.IMS_LOGISTICS.rcSetType('${p.id}','${sg.id}')" title="From its box weight">Suggested: ${util().sanitize(sg.name)}</button>` : ''}</td>
        </tr>`; }).join('') || '<tr><td colspan="5" class="ims__table-empty">No products match</td></tr>'}</tbody>
      </table></div>
      ${list.length > shown.length ? `<div style="font-size:12px;color:var(--ims-text-muted);margin-top:6px">Showing ${shown.length} of ${list.length}: search to narrow down.</div>` : ''}`;
  }
  function rcProdFilter(search, missing) {
    if (search !== null) _rc.prodSearch = search;
    if (missing !== null) _rc.prodMissing = missing;
    _rcProductsRender();
    if (search !== null) { const el = document.querySelector('#ims-rc-body input[placeholder="Search name or SKU"]'); if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); } }
  }
  async function _rcSetMany(pairs) {   // [[productId, typeId or ''], ...]
    const db_ = _rcDb(); let done = 0;
    for (const [pid, tid] of pairs) {
      const ex = _rc.map.find(m => m.product_id === pid);
      let res;
      if (!tid) { if (!ex) continue; res = await db_.from('courier_product_map').delete().eq('id', ex.id); if (!res.error) _rc.map = _rc.map.filter(m => m.id !== ex.id); }
      else if (ex) { if (ex.type_id === tid) continue; res = await db_.from('courier_product_map').update({ type_id: tid }).eq('id', ex.id); if (!res.error) ex.type_id = tid; }
      else { res = await db_.from('courier_product_map').insert({ company_id: _rc.companyId, courier_id: _rc.courierId, product_id: pid, type_id: tid }).select('id,product_id,type_id'); if (!res.error && res.data?.[0]) _rc.map.push(res.data[0]); }
      if (!_rcSaved(res.error)) break;
      done++;
    }
    return done;
  }
  async function rcSetType(productId, typeId) { if (!_rcAdmin()) return; await _rcSetMany([[productId, typeId]]); _rcProductsRender(); }
  async function rcBulkSet() {
    if (!_rcAdmin()) return;
    const tid = document.getElementById('ims-rc-bulk')?.value || '';
    const ids = [...document.querySelectorAll('.ims-rc-pick:checked')].map(c => c.value);
    if (!ids.length) { ui().toast('Tick the products to change first', 'warning'); return; }
    const n = await _rcSetMany(ids.map(id => [id, tid]));
    ui().toast(`${n} product(s) updated`, 'success'); _rcProductsRender();
  }
  async function rcApplySuggestions() {
    if (!_rcAdmin()) return;
    const pairs = _rc.products.filter(p => !_rc.map.some(m => m.product_id === p.id)).map(p => [p.id, _rcSuggest(p.kg)?.id]).filter(x => x[1]);
    const n = await _rcSetMany(pairs);
    ui().toast(`${n} product(s) set from their box weight`, 'success'); _rcProductsRender();
  }
  function rcExportProducts() {
    const typeName = pid => { const m = _rc.map.find(x => x.product_id === pid); return m ? (_rc.types.find(t => t.id === m.type_id)?.name || '') : ''; };
    const rows = _rc.products.map(p => [p.sku || '', p.name || '', p.kg ?? '', typeName(p.id), typeName(p.id) ? '' : (_rcSuggest(p.kg)?.name || '')]);
    util().exportCSV(['SKU', 'Product', 'Weight kg', `${_rc.courierName} type`, 'Suggested type'], rows, `${_rc.courierName.replace(/[^A-Za-z0-9]+/g, '_')}_product_types.csv`);
  }
  async function rcImportProducts(input) {
    if (!_rcAdmin()) return;
    const file = input?.files?.[0]; if (!file) return;
    const text = await file.text(); input.value = '';
    const { headers, rows } = util().parseCSV(text);
    const col = re => headers.findIndex(h => re.test(String(h).trim()));
    const iSku = col(/^sku$/i), iName = col(/^product$/i), iType = col(/type$/i) >= 0 ? headers.findIndex(h => /type$/i.test(h) && !/^suggested/i.test(h)) : -1;
    if (iType < 0 || (iSku < 0 && iName < 0)) { ui().toast('The file needs a "SKU" (or "Product") column and a type column. Use "Export to Excel" to get the right layout.', 'error', 9000); return; }
    const byType = {}; _rc.types.forEach(t => { byType[t.name.trim().toLowerCase()] = t.id; });
    const pairs = [], unknownType = new Set(), unknownProd = [];
    for (const r of rows) {
      const sku = iSku >= 0 ? String(r[iSku] || '').trim() : '', name = iName >= 0 ? String(r[iName] || '').trim() : '';
      const tname = String(r[iType] || '').trim();
      const prod = (sku && _rc.products.find(p => (p.sku || '').trim().toLowerCase() === sku.toLowerCase())) || (!sku && name && _rc.products.find(p => (p.name || '').trim().toLowerCase() === name.toLowerCase()));
      if (!prod) { if (sku || name) unknownProd.push(sku || name); continue; }
      if (!tname) continue;   // left blank: unchanged
      const tid = byType[tname.toLowerCase()];
      if (!tid) { unknownType.add(tname); continue; }
      pairs.push([prod.id, tid]);
    }
    const n = await _rcSetMany(pairs);
    const notes = [];
    if (unknownType.size) notes.push(`types not on this rate card: ${[...unknownType].slice(0, 5).join(', ')}`);
    if (unknownProd.length) notes.push(`${unknownProd.length} row(s) with an unknown SKU/product`);
    ui().toast(`${n} product(s) updated from the file.${notes.length ? ' Skipped: ' + notes.join('; ') + '.' : ''}`, notes.length ? 'warning' : 'success', notes.length ? 12000 : 5000);
    _rcProductsRender();
  }

  // ── Extras (our cost) ──
  function _rcExtrasHtml() {
    const dis = _rcDis();
    return `
      <div style="font-size:12px;color:var(--ims-text-muted);margin-bottom:8px">What the courier charges <strong>us</strong> for extra services (your customer prices come later, in the Services list).</div>
      <div class="ims__table-wrap"><table class="ims__table">
        <thead><tr><th>Extra service</th><th style="width:130px">Our cost (£)</th><th style="width:150px">Charged per</th><th style="width:220px">This is our service</th><th></th></tr></thead>
        <tbody>${_rc.extras.map(e => `<tr>
          <td><input class="ims__input ims__input--sm" value="${util().sanitize(e.name)}" ${dis} onchange="window.IMS_LOGISTICS.rcSaveExtra('${e.id}','name',this.value)"></td>
          <td><input class="ims__input ims__input--sm" type="number" min="0" step="0.01" value="${e.price ?? ''}" ${dis} onchange="window.IMS_LOGISTICS.rcSaveExtra('${e.id}','price',this.value)"></td>
          <td><select class="ims__select" style="font-size:12px" ${dis} onchange="window.IMS_LOGISTICS.rcSaveExtra('${e.id}','per',this.value)"><option value="order" ${e.per === 'order' ? 'selected' : ''}>Per order</option><option value="item" ${e.per === 'item' ? 'selected' : ''}>Per item</option></select></td>
          <td><select class="ims__select" style="font-size:12px" ${dis} onchange="window.IMS_LOGISTICS.rcSaveExtra('${e.id}','service_id',this.value)"><option value="">- not linked -</option>${(_rc.services || []).map(v => `<option value="${v.id}" ${v.id === e.service_id ? 'selected' : ''}>${util().sanitize(v.name)}</option>`).join('')}</select></td>
          <td>${_rcAdmin() ? `<button class="ims__btn ims__btn--ghost ims__btn--sm ims__btn--icon" title="Delete" onclick="window.IMS_LOGISTICS.rcDeleteExtra('${e.id}','${util().jsArg(e.name)}')"><i class="ti ti-trash"></i></button>` : ''}</td>
        </tr>`).join('') || '<tr><td colspan="5" class="ims__table-empty">No extras yet</td></tr>'}</tbody>
      </table></div>
      ${_rcAdmin() ? `<button class="ims__btn ims__btn--secondary ims__btn--sm" style="margin-top:8px" onclick="window.IMS_LOGISTICS.rcAddExtra()"><i class="ti ti-plus"></i> Add extra</button>` : ''}`;
  }
  async function rcSaveExtra(id, field, value) {
    if (!_rcAdmin()) return;
    const v = field === 'price' ? (_rcNum(value) ?? 0) : field === 'service_id' ? (value || null) : String(value || '').trim();
    if (field === 'name' && !v) { ui().toast('An extra needs a name', 'warning'); return; }
    const { error } = await _rcDb().from('courier_card_extras').update({ [field]: v }).eq('id', id);
    if (_rcSaved(error)) { const e = _rc.extras.find(x => x.id === id); if (e) e[field] = v; }
  }
  async function rcAddExtra() {
    if (!_rcAdmin()) return;
    const { data, error } = await _rcDb().from('courier_card_extras').insert({ company_id: _rc.companyId, card_id: _rc.cardId, name: 'New extra', price: 0, per: 'order', sort_order: _rc.extras.length }).select('*');
    if (_rcSaved(error) && data?.[0]) { _rc.extras.push(data[0]); _rcRenderTab(); }
  }
  function rcDeleteExtra(id, name) {
    if (!_rcAdmin()) return;
    ui().confirm(`Delete the extra <strong>${util().sanitize(name)}</strong> from this version of the rates?`, async () => {
      const { error } = await _rcDb().from('courier_card_extras').delete().eq('id', id);
      if (_rcSaved(error)) { _rc.extras = _rc.extras.filter(x => x.id !== id); _rcRenderTab(); }
    }, 'Delete', true);
  }

  // ── Settings ──
  function _rcSettingsHtml() {
    const c = _rcCard(), dis = _rcDis();
    const f = (field, label, input) => `<div class="ims__form-group"><label class="ims__label">${label}</label>${input}</div>`;
    const ch = field => `onchange="window.IMS_LOGISTICS.rcSaveCard('${field}',this.type==='checkbox'?this.checked:this.value)"`;
    return `
      <div class="ims__form-grid">
        ${f('valid_from', 'Valid from', `<input class="ims__input" type="date" value="${c.valid_from || ''}" ${dis} ${ch('valid_from')}>`)}
        ${f('fuel_pct', 'Fuel surcharge (%)', `<input class="ims__input" type="number" min="0" step="0.01" value="${c.fuel_pct ?? 0}" ${dis} ${ch('fuel_pct')}>`)}
        ${f('floor_first', 'Room of choice: up to the first floor (£)', `<input class="ims__input" type="number" min="0" step="0.01" value="${c.floor_first ?? 0}" ${dis} ${ch('floor_first')}>`)}
        ${f('floor_extra', 'Each floor above that (£)', `<input class="ims__input" type="number" min="0" step="0.01" value="${c.floor_extra ?? 0}" ${dis} ${ch('floor_extra')}>`)}
        <div class="ims__form-group"><label style="display:flex;gap:6px;align-items:center;font-size:13px"><input type="checkbox" ${c.prices_exclude_vat ? 'checked' : ''} ${dis} ${ch('prices_exclude_vat')}> Prices exclude VAT</label></div>
        <div class="ims__form-group"><label style="display:flex;gap:6px;align-items:center;font-size:13px"><input type="checkbox" ${c.collections_same ? 'checked' : ''} ${dis} ${ch('collections_same')}> Collections cost the same as deliveries</label></div>
        ${f('notes', 'Notes', `<textarea class="ims__textarea" rows="2" ${dis} ${ch('notes')}>${util().sanitize(c.notes || '')}</textarea>`)}
      </div>
      ${_rcAdmin() && _rc.cards.length > 1 ? `<button class="ims__btn ims__btn--ghost ims__btn--sm" style="color:var(--ims-danger)" onclick="window.IMS_LOGISTICS.rcDeleteVersion()"><i class="ti ti-trash"></i> Delete this version</button>` : ''}`;
  }
  async function rcSaveCard(field, value) {
    if (!_rcAdmin()) return;
    let v = value;
    if (['fuel_pct', 'floor_first', 'floor_extra'].includes(field)) v = _rcNum(value) ?? 0;
    if (field === 'valid_from') {
      if (!v) { ui().toast('Please choose a date', 'warning'); return; }
      if (_rc.cards.some(c => c.id !== _rc.cardId && String(c.valid_from) === v)) { ui().toast('Another version already starts on that date', 'error'); _rcRenderTab(); return; }
    }
    const { error } = await _rcDb().from('courier_rate_cards').update({ [field]: v }).eq('id', _rc.cardId);
    if (_rcSaved(error)) { const c = _rcCard(); c[field] = v; if (field === 'valid_from') { await _rcLoad(); _rcOpen(); } }
  }
  async function rcNewVersion() {
    if (!_rcAdmin()) return;
    const date = document.getElementById('ims-rc-newdate')?.value;
    if (!date) { ui().toast('Choose the date the new rates start', 'warning'); return; }
    if (_rc.cards.some(c => String(c.valid_from) === date)) { ui().toast('A version already starts on that date', 'error'); return; }
    const db_ = _rcDb(), src = _rcCard();
    const { id, created_at, valid_from, ...fields } = src;
    const { data: nc, error } = await db_.from('courier_rate_cards').insert({ ...fields, valid_from: date }).select('*');
    if (!_rcSaved(error) || !nc?.[0]) return;
    const newId = nc[0].id;
    const rates = _rc.rates.map(r => ({ company_id: r.company_id, card_id: newId, type_id: r.type_id, zone_id: r.zone_id, price: r.price }));
    const extras = _rc.extras.map(e => ({ company_id: e.company_id, card_id: newId, name: e.name, price: e.price, per: e.per, sort_order: e.sort_order, service_id: e.service_id || null }));
    if (rates.length) { const r = await db_.from('courier_card_rates').insert(rates); if (!_rcSaved(r.error)) return; }
    if (extras.length) { const r = await db_.from('courier_card_extras').insert(extras); if (!_rcSaved(r.error)) return; }
    ui().toast(`New rates from ${util().formatDate(date)}: copied from the current version. Change what is different.`, 'success', 7000);
    _rc.cardId = newId; await _rcLoad(); _rcOpen();
  }
  function rcDeleteVersion() {
    if (!_rcAdmin() || _rc.cards.length < 2) return;
    const c = _rcCard();
    ui().confirm(`Delete the rates valid from <strong>${util().formatDate(c.valid_from)}</strong>, with its prices and extras? Deliveries in that period will be checked against the previous version.`, async () => {
      const { error } = await _rcDb().from('courier_rate_cards').delete().eq('id', _rc.cardId);
      if (_rcSaved(error)) { _rc.cardId = null; await _rcLoad(); _rcOpen(); }
    }, 'Delete', true);
  }

  async function addRateBand(courierId){
    const from=prompt('Weight from (kg):','0');
    const to=prompt('Weight to (kg, leave blank for ∞):','');
    const rate=prompt('Base rate (£):','0');
    const perKg=prompt('Per kg over band (£, optional):','');
    if(from===null||rate===null)return;
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    await client.from('courier_rate_bands').insert({courier_id:courierId,weight_from:parseFloat(from)||0,weight_to:to?parseFloat(to):null,base_rate:parseFloat(rate)||0,per_kg_over:perKg?parseFloat(perKg):0,sort_order:0});
    ui().toast('Rate band added','success');
    const {data:c}=await db().select('couriers',{eq:{id:courierId},maybeSingle:true,noCompanyFilter:true});
    if(c) manageCourierRates(courierId,c.name);
  }

  async function deleteRateBand(bandId,courierId,courierName){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    await client.from('courier_rate_bands').delete().eq('id',bandId);
    ui().toast('Rate band deleted','success');
    manageCourierRates(courierId,courierName);
  }

  async function editSurcharge(courierId,type,courierName){
    const value=prompt(`${type} — value (e.g. 8.5 for 8.5%):`, '');
    const valueType=prompt('Type: percent or flat?','flat');
    if(value===null)return;
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:existing}=await client.from('courier_surcharges').select('id').eq('courier_id',courierId).eq('surcharge_type',type).maybeSingle();
    if(existing){
      await client.from('courier_surcharges').update({value:parseFloat(value)||0,value_type:valueType||'flat',is_active:true}).eq('id',existing.id);
    } else {
      await client.from('courier_surcharges').insert({courier_id:courierId,surcharge_type:type,value:parseFloat(value)||0,value_type:valueType||'flat',is_active:true});
    }
    ui().toast('Surcharge updated','success');
    manageCourierRates(courierId,courierName);
  }

  // ─── ROUTES ─────────────────────────────────────────────────────
  async function loadRoutes(){
    const content=document.getElementById('ims-log-content');
    if(!content)return;
    const {data:routes}=await db().select('routes',{order:{col:'name'}});
    const items=routes||[];
    content.innerHTML=`
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px;gap:12px">
        <div class="ims__alert ims__alert--info" style="margin:0;flex:1"><i class="ti ti-info-circle"></i><span><strong>Routes are named templates</strong> — e.g. "North London Run". Driver and truck are assigned when you create a manifest from <strong>Unassigned jobs → Plan route</strong>.</span></div>
        <button class="ims__btn ims__btn--primary ims__btn--sm" onclick="window.IMS_LOGISTICS.showRouteForm()"><i class="ti ti-plus"></i> Add route</button>
      </div>
      <div class="ims__card"><div class="ims__table-wrap"><table class="ims__table">
        <thead><tr><th>Route name</th><th>Zone</th><th>Stops</th><th>Status</th><th></th></tr></thead>
        <tbody>${items.map(r=>`<tr><td><strong>${util().sanitize(r.name)}</strong></td><td>${util().sanitize(r.zone||'—')}</td><td>${Array.isArray(r.stops)?r.stops.length:0} stops</td><td><span class="ims__pill ims__pill--${r.is_active?'success':'neutral'}">${r.is_active?'Active':'Inactive'}</span></td><td><button class="ims__btn ims__btn--secondary ims__btn--sm ims__btn--icon" onclick="window.IMS_LOGISTICS.showRouteForm('${util().jsArg(r.id)}')"><i class="ti ti-edit"></i></button></td></tr>`).join('')||'<tr><td colspan="5" class="ims__table-empty">No routes configured</td></tr>'}
        </tbody>
      </table></div></div>`;
  }

  async function showRouteForm(existingId=null){
    let r=null;
    if(existingId){const {data}=await db().select('routes',{eq:{id:existingId},maybeSingle:true,noCompanyFilter:true});r=data;}
    ui().openModal(existingId?`Edit: ${r?.name}`:'Add route',`
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Route name</label><input class="ims__input" type="text" id="ims-rt-name" value="${util().sanitize(r?.name||'')}"></div>
        <div class="ims__form-group"><label class="ims__label">Zone / Hub</label><input class="ims__input" type="text" id="ims-rt-zone" value="${util().sanitize(r?.zone||'')}" placeholder="e.g. North London"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Stops (one per line)</label><textarea class="ims__textarea" id="ims-rt-stops" rows="4" placeholder="Enter delivery zones or stops">${Array.isArray(r?.stops)?r.stops.join('\n'):''}</textarea></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveRoute('${util().jsArg(existingId||'')}')"><i class="ti ti-check"></i> Save route</button>
    `,'sm');
  }

  async function saveRoute(existingId){
    const name=document.getElementById('ims-rt-name')?.value?.trim();
    const zone=document.getElementById('ims-rt-zone')?.value?.trim();
    const stopsRaw=document.getElementById('ims-rt-stops')?.value||'';
    const stops=stopsRaw.split('\n').map(s=>s.trim()).filter(Boolean);
    if(!name){ui().toast('Route name is required','error');return;}
    const data={name,zone:zone||null,stops};
    const {error}=existingId?await db().update('routes',existingId,data):await db().insert('routes',data);
    if(error){ui().toast('Failed: '+error.message,'error');return;}
    ui().closeModal();ui().toast(`Route ${existingId?'updated':'added'}`,'success');loadRoutes();
  }

  // ─── FLEET ──────────────────────────────────────────────────────
  async function loadFleet(){
    const content=document.getElementById('ims-log-content');
    if(!content)return;
    const [{data:vehicles},{data:drivers}]=await Promise.all([
      db().select('vehicles',{order:{col:'registration'}}),
      db().select('drivers',{order:{col:'name'}})
    ]);
    content.innerHTML=`
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
        <div class="ims__card">
          <div class="ims__card-header"><div class="ims__card-title"><i class="ti ti-car"></i>Vehicles</div><button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.showVehicleForm()"><i class="ti ti-plus"></i> Add</button></div>
          <table class="ims__table"><thead><tr><th>Reg</th><th>Type</th><th>Capacity</th><th>MOT due</th><th></th></tr></thead>
          <tbody>${[...(vehicles||[])].sort((a,b)=>(b.is_active!==false)-(a.is_active!==false)).map(v=>{ const off=v.is_active===false; return `<tr style="${off?'opacity:.55':''}"><td><strong>${util().sanitize(v.registration)}</strong>${off?' <span class="ims__pill ims__pill--neutral" style="font-size:10px">Inactive</span>':''}</td><td>${util().sanitize(v.make_model||v.vehicle_type||'-')}</td><td>${v.capacity_units?v.capacity_units+' units':'-'}</td><td style="color:${v.mot_due&&new Date(v.mot_due)<new Date()?'var(--ims-danger)':''}">${v.mot_due?util().formatDate(v.mot_due):'-'}</td>
            <td style="white-space:nowrap"><div class="ims__btn-group">${_fleetButtons('vehicle',v.id,v.registration,off)}</div></td></tr>`; }).join('')||'<tr><td colspan="5" class="ims__table-empty">No vehicles</td></tr>'}
          </tbody></table>
        </div>
        <div class="ims__card">
          <div class="ims__card-header"><div class="ims__card-title"><i class="ti ti-id-badge"></i>Drivers</div><button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.showDriverForm()"><i class="ti ti-plus"></i> Add</button></div>
          <table class="ims__table"><thead><tr><th>Name</th><th>Phone</th><th>Licence expiry</th><th>COD balance</th><th></th></tr></thead>
          <tbody>${[...(drivers||[])].sort((a,b)=>(b.is_active!==false)-(a.is_active!==false)).map(d=>`<tr style="${d.is_active===false?'opacity:.55':''}"><td><strong>${util().sanitize(d.name)}</strong>${d.is_active===false?' <span class="ims__pill ims__pill--neutral" style="font-size:10px">Inactive</span>':''}</td><td>${util().sanitize(d.phone||'—')}</td><td style="color:${d.licence_expiry&&new Date(d.licence_expiry)<new Date()?'var(--ims-danger)':''}">${d.licence_expiry?util().formatDate(d.licence_expiry):'—'}</td><td><span id="ims-cod-bal-${d.id}" style="font-weight:600;color:var(--ims-text-muted)">Loading...</span></td><td><div class="ims__btn-group"><button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.openCODAccount('${util().jsArg(d.id)}','${util().jsArg(d.name)}')"><i class="ti ti-wallet"></i> COD account</button>${_fleetButtons('driver',d.id,d.name,d.is_active===false)}</div></td></tr>`).join('')||'<tr><td colspan="5" class="ims__table-empty">No drivers</td></tr>'}
          </tbody></table>
        </div>
      </div>`;
    loadAllCODBalances(drivers||[]);
  }

  async function loadAllCODBalances(drivers){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    for(const d of drivers){
      const {data:acc}=await client.from('driver_cod_accounts').select('balance').eq('driver_id',d.id).maybeSingle();
      const el=document.getElementById(`ims-cod-bal-${d.id}`);
      if(el){
        const bal=acc?.balance||0;
        el.textContent=bal===0?'£0.00 — clear':util().formatCurrency(Math.abs(bal));
        el.style.color=bal>0?'var(--ims-danger)':bal<0?'var(--ims-success)':'var(--ims-text-muted)';
        el.title=bal>0?'Driver owes company':bal<0?'Company owes driver':'Account clear';
      }
    }
  }

  async function openCODAccount(driverId,driverName){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    let {data:acc}=await client.from('driver_cod_accounts').select('*').eq('driver_id',driverId).maybeSingle();
    if(!acc){
      const {data:newAcc}=await client.from('driver_cod_accounts').insert({company_id:window.SM.AUTH.company?.id,driver_id:driverId,balance:0}).select().maybeSingle();
      acc=newAcc;
    }
    const {data:txns}=await client.from('cod_transactions').select('*,orders(order_number,customer_name,total_value)').eq('driver_id',driverId).order('created_at',{ascending:false}).limit(50);
    const balance=acc?.balance||0;
    const cu=await _codCashUp(driverId);
    const txnRows=(txns||[]).map(t=>`<tr><td style="font-size:11px">${util().formatDate(t.created_at)}</td><td>${util().sanitize(t.orders?.order_number||'—')}</td><td>${util().sanitize(t.orders?.customer_name||'—')}</td><td><span class="ims__pill ims__pill--${t.type==='debit'?'warning':t.type==='credit'?'success':'neutral'}">${t.type}</span></td><td style="text-align:right;font-weight:600;color:${t.type==='debit'?'var(--ims-danger)':'var(--ims-success)'}">${t.type==='debit'?'+':'-'}${util().formatCurrency(Math.abs(t.amount))}</td><td style="font-size:11px">${util().sanitize(t.notes||'—')}</td></tr>`).join('');
    ui().openModal(`COD Account: ${driverName}`,`
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:16px">
        <div class="ims__stat" style="padding:12px"><div class="ims__stat-label">Current balance</div><div class="ims__stat-value ims__stat-value--${balance>0?'danger':balance<0?'success':''}" style="font-size:22px">${balance===0?'£0.00 — Clear':util().formatCurrency(Math.abs(balance))}</div><div style="font-size:11px;color:var(--ims-text-muted);margin-top:4px">${balance>0?'Driver owes this amount to company':balance<0?'Company owes driver':'Account is clear'}</div></div>
        <div class="ims__stat" style="padding:12px"><div class="ims__stat-label">Total debits (COD taken)</div><div class="ims__stat-value" style="font-size:18px">${util().formatCurrency((txns||[]).filter(t=>t.type==='debit').reduce((s,t)=>s+(t.amount||0),0))}</div></div>
        <div class="ims__stat" style="padding:12px"><div class="ims__stat-label">Total credits (paid in)</div><div class="ims__stat-value ims__stat-value--success" style="font-size:18px">${util().formatCurrency((txns||[]).filter(t=>t.type==='credit').reduce((s,t)=>s+(t.amount||0),0))}</div></div>
      </div>
      ${balance>0?`<div class="ims__alert ims__alert--warning" style="margin-bottom:14px"><i class="ti ti-alert-triangle"></i><span><strong>${driverName}</strong> owes <strong>${util().formatCurrency(balance)}</strong> to the company.</span></div>`:''}
      ${_codCashUpHtml(cu,driverId,driverName)}
      <strong style="font-size:13px">Transaction history</strong>
      <div class="ims__table-wrap" style="max-height:320px;overflow-y:auto;margin-top:8px">
        <table class="ims__table"><thead><tr><th>Date</th><th>Order #</th><th>Customer</th><th>Type</th><th style="text-align:right">Amount</th><th>Notes</th></tr></thead>
        <tbody>${txnRows||'<tr><td colspan="6" class="ims__table-empty">No transactions yet</td></tr>'}</tbody>
        </table>
      </div>
    `,`
      <div class="ims__btn-group">
        <button class="ims__btn ims__btn--success" onclick="window.IMS_LOGISTICS.recordCODPayment('${util().jsArg(driverId)}','${util().jsArg(driverName)}',${cu.cashTotal>0?cu.cashTotal:0})"><i class="ti ti-cash"></i> Record payment</button>
        <button class="ims__btn ims__btn--secondary" onclick="window.IMS_LOGISTICS.reconcileCODAccount('${util().jsArg(driverId)}','${util().jsArg(driverName)}')"><i class="ti ti-file-check"></i> Reconcile</button>
        <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Close</button>
      </div>
    `,'xl');
  }

  // ─── COD CASH-UP ──────────────────────────────────────────────────
  // Per driver, from his COD charges less reversals, order by order:
  //   cash to hand in  = delivered orders whose COD is still outstanding
  //   still on the van = not yet delivered
  //   bank transfers to confirm (the driver recorded "paid by bank transfer")
  //   COD not collected (to chase)
  async function _codCashUp(driverId){
    const c=_rcDb();
    const {data:rows}=await c.from('cod_transactions').select('id,type,amount,order_id,notes,confirmed_at,created_at,orders(order_number,customer_name,status,payment_status)').eq('driver_id',driverId).not('order_id','is',null);
    const per={};
    (rows||[]).forEach(t=>{ const p=per[t.order_id]=per[t.order_id]||{order_id:t.order_id,o:t.orders||{},net:0,first:t.created_at};
      if(t.type==='debit') p.net+=Number(t.amount)||0; else if(t.type==='reversal') p.net-=Number(t.amount)||0;
      if(t.created_at<p.first) p.first=t.created_at; });
    const list=Object.values(per).filter(p=>p.net>0.005&&p.o.payment_status!=='paid');
    const cash=list.filter(p=>p.o.status==='delivered'), van=list.filter(p=>p.o.status!=='delivered');
    const bank=(rows||[]).filter(t=>t.type==='reversal'&&/^Paid by bank transfer/.test(t.notes||'')&&!t.confirmed_at);
    const notColl=(rows||[]).filter(t=>t.type==='reversal'&&/^COD not collected/.test(t.notes||'')&&t.orders?.payment_status!=='paid');
    const sum=a=>Math.round(a.reduce((s,p)=>s+p.net,0)*100)/100;
    return {cash,van,bank,notColl,cashTotal:sum(cash),vanTotal:sum(van)};
  }
  function _codCashUpHtml(cu,driverId,driverName){
    const fm=n=>util().formatCurrency(n);
    const tbl=(head,rows)=>`<div class="ims__table-wrap" style="margin:6px 0 14px"><table class="ims__table"><thead><tr>${head.map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table></div>`;
    return `
      <strong style="font-size:13px">Cash to hand in: ${fm(cu.cashTotal)}</strong> <span style="font-size:12px;color:var(--ims-text-muted)">(delivered COD orders not yet paid in)</span>
      ${cu.cash.length?tbl(['Order #','Customer','Amount'],cu.cash.map(p=>`<tr><td>${util().sanitize(p.o.order_number||'-')}</td><td>${util().sanitize(p.o.customer_name||'-')}</td><td>${fm(p.net)}</td></tr>`).join('')):'<div style="font-size:12px;color:var(--ims-text-muted);margin:4px 0 14px">Nothing to hand in.</div>'}
      ${cu.van.length?`<strong style="font-size:13px">Still on the van: ${fm(cu.vanTotal)}</strong> <span style="font-size:12px;color:var(--ims-text-muted)">(COD orders not delivered yet)</span>
      ${tbl(['Order #','Customer','Amount'],cu.van.map(p=>`<tr><td>${util().sanitize(p.o.order_number||'-')}</td><td>${util().sanitize(p.o.customer_name||'-')}</td><td>${fm(p.net)}</td></tr>`).join(''))}`:''}
      ${cu.bank.length?`<strong style="font-size:13px">Bank transfers to confirm (${cu.bank.length})</strong> <span style="font-size:12px;color:var(--ims-text-muted)">(the customer told the driver they paid by bank transfer)</span>
      ${tbl(['Order #','Customer','Amount',''],cu.bank.map(t=>`<tr><td>${util().sanitize(t.orders?.order_number||'-')}</td><td>${util().sanitize(t.orders?.customer_name||'-')}</td><td>${fm(t.amount)}</td>
        <td><button class="ims__btn ims__btn--secondary ims__btn--sm" onclick="window.IMS_LOGISTICS.confirmBankTransfer('${util().jsArg(t.id)}','${util().jsArg(driverId)}','${util().jsArg(driverName)}')"><i class="ti ti-check"></i> Confirm received</button></td></tr>`).join(''))}`:''}
      ${cu.notColl.length?`<strong style="font-size:13px;color:var(--ims-danger)">COD not collected (${cu.notColl.length})</strong> <span style="font-size:12px;color:var(--ims-text-muted)">(delivered, but the customer did not pay: to chase)</span>
      ${tbl(['Order #','Customer','Amount'],cu.notColl.map(t=>`<tr><td>${util().sanitize(t.orders?.order_number||'-')}</td><td>${util().sanitize(t.orders?.customer_name||'-')}</td><td>${fm(t.amount)}</td></tr>`).join(''))}`:''}`;
  }
  // The money arrived in the bank: the order is paid, recorded as COD
  // income, and the item leaves the "to confirm" list.
  async function confirmBankTransfer(txId,driverId,driverName){
    const c=_rcDb();
    const {data:t}=await c.from('cod_transactions').select('id,order_id,amount,confirmed_at,orders(order_number,payment_status)').eq('id',txId).maybeSingle();
    if(!t||t.confirmed_at){ ui().toast('Already confirmed','info'); return openCODAccount(driverId,driverName); }
    const {error}=await c.from('cod_transactions').update({confirmed_at:new Date().toISOString(),confirmed_by:window.SM.AUTH.user?.id||null}).eq('id',txId).is('confirmed_at',null);
    if(error){ ui().toast('Not saved: '+error.message,'error',8000); return; }
    if(t.order_id){
      await c.from('orders').update({payment_status:'paid'}).eq('id',t.order_id);
      await c.from('accounts_receivable').delete().eq('order_id',t.order_id);
      await db().insert('transactions',{date:new Date().toISOString().split('T')[0],type:'income',category:'COD collection',description:`COD paid by bank transfer: ${t.orders?.order_number||''}`,amount:Number(t.amount)||0,reference:t.orders?.order_number||null});
      await logActivity(t.order_id,t.orders?.payment_status||'cod','paid','cod_bank_transfer_confirmed',`Bank transfer of ${util().formatCurrency(t.amount)} received`);
    }
    ui().toast(`Bank transfer for ${t.orders?.order_number||'the order'} confirmed: order paid`,'success');
    openCODAccount(driverId,driverName);
  }

  async function recordCODPayment(driverId,driverName,currentBalance){
    ui().openModal(`Record payment: ${driverName}`,`
      <div class="ims__alert ims__alert--info" style="margin-bottom:14px"><i class="ti ti-info-circle"></i><span>Record cash handed over by the driver. It is applied to his delivered COD orders that are still outstanding, oldest first.</span></div>
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Amount received (£)</label><input class="ims__input" type="number" step="0.01" min="0" id="ims-cod-amount" value="${currentBalance>0?currentBalance.toFixed(2):''}"><span class="ims__input-hint">Current balance owed: ${util().formatCurrency(currentBalance)}</span></div>
        <div class="ims__form-group"><label class="ims__label ims__label--req">Date received</label><input class="ims__input" type="date" id="ims-cod-date" value="${new Date().toISOString().split('T')[0]}"></div>
        <div class="ims__form-group"><label class="ims__label">Received by</label><input class="ims__input" type="text" id="ims-cod-received-by" value="${window.SM.AUTH.user?.name||''}" placeholder="Supervisor name"></div>
        <div class="ims__form-group"><label class="ims__label">Notes</label><input class="ims__input" type="text" id="ims-cod-notes" placeholder="e.g. End of route payment"></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--success" onclick="window.IMS_LOGISTICS.confirmCODPayment('${util().jsArg(driverId)}','${util().jsArg(driverName)}')"><i class="ti ti-check"></i> Confirm payment received</button>
    `,'sm');
  }

  async function confirmCODPayment(driverId,driverName){
    const amount=parseFloat(document.getElementById('ims-cod-amount')?.value)||0;
    const notes=document.getElementById('ims-cod-notes')?.value?.trim()||'';
    const receivedBy=document.getElementById('ims-cod-received-by')?.value?.trim()||'';
    if(!amount||amount<=0){ui().toast('Please enter a valid amount','error');return;}
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    await client.from('cod_transactions').insert({company_id:window.SM.AUTH.company?.id,driver_id:driverId,order_id:null,type:'credit',amount,notes:`Payment received${receivedBy?' by '+receivedBy:''}.${notes?' '+notes:''}`,created_by:window.SM.AUTH.user?.id});
    const {data:acc}=await client.from('driver_cod_accounts').select('balance').eq('driver_id',driverId).maybeSingle();
    const newBalance=Math.max(0,(acc?.balance||0)-amount);
    await client.from('driver_cod_accounts').update({balance:newBalance}).eq('driver_id',driverId);
    await db().insert('transactions',{date:new Date().toISOString().split('T')[0],type:'income',category:'COD collection',description:`COD payment received from driver ${driverName}`,amount,reference:`COD-${driverName}`});

    // Close out the specific orders this payment covers — oldest unsettled
    // COD debit first — so their payment_status actually flips to 'paid'
    // instead of staying 'cod' forever even after the cash has been banked.
    // Any of those that also carry a wholesale accounts_receivable get that
    // cleared too, so Accounts doesn't keep chasing an order already paid
    // for in cash at the door.
    // Only orders actually DELIVERED whose COD is still outstanding (charges
    // less reversals): a failed delivery, a bank transfer or COD not
    // collected reverses the charge, and such orders must never be marked
    // paid by cash handed in.
    const {data:debits}=await client.from('cod_transactions')
      .select('order_id,amount,orders(payment_status,status)')
      .eq('driver_id',driverId).eq('type','debit').not('order_id','is',null)
      .order('created_at',{ascending:true});
    const {data:revs}=await client.from('cod_transactions').select('order_id,amount').eq('driver_id',driverId).eq('type','reversal').not('order_id','is',null);
    const reversed={}; (revs||[]).forEach(r=>{ reversed[r.order_id]=(reversed[r.order_id]||0)+(Number(r.amount)||0); });
    let remaining=amount;
    for(const d of (debits||[])){
      if(remaining<=0)break;
      if(!d.order_id||d.orders?.payment_status==='paid')continue;
      if(d.orders?.status!=='delivered')continue;                                  // cash not collected yet
      if((Number(d.amount)||0)-(reversed[d.order_id]||0)<=0.005)continue;          // reversed: nothing outstanding
      await client.from('orders').update({payment_status:'paid'}).eq('id',d.order_id);
      await client.from('accounts_receivable').delete().eq('order_id',d.order_id);
      remaining-=(d.amount||0);
    }

    ui().closeModal();
    ui().toast(`£${amount.toFixed(2)} recorded — driver account updated`,'success',4000);
    loadFleet();
  }

  async function reconcileCODAccount(driverId,driverName){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const {data:acc}=await client.from('driver_cod_accounts').select('balance').eq('driver_id',driverId).maybeSingle();
    const balance=acc?.balance||0;
    if(balance===0){ui().toast('Account is already clear — nothing to reconcile','info');return;}
    ui().confirm(`Mark ${driverName}'s COD account as reconciled?<br><br>Current balance: <strong>${util().formatCurrency(balance)}</strong><br><br>This will zero the balance, record a reconciliation entry, and record the shortfall as a write-off expense in Accounts → Transactions.`,async()=>{
      await client.from('cod_transactions').insert({company_id:window.SM.AUTH.company?.id,driver_id:driverId,type:'reversal',amount:balance,notes:'Manual reconciliation — balance zeroed',created_by:window.SM.AUTH.user?.id});
      await client.from('driver_cod_accounts').update({balance:0,last_reconciled_at:new Date().toISOString()}).eq('driver_id',driverId);
      // The zeroed balance is money the company is writing off, not money
      // that appeared from nowhere — record it so it doesn't just vanish
      // from the books with no trace, the same gap a rejected courier
      // claim used to have before that was fixed.
      await db().insert('transactions',{date:new Date().toISOString().split('T')[0],type:'expense',category:'Write-off — COD reconciliation',description:`COD balance written off — ${driverName}`,amount:balance,reference:`COD-${driverName}`});
      ui().toast('Account reconciled — balance zeroed and write-off recorded','success');
      loadFleet();
    },'Reconcile',false);
  }

  async function showVehicleForm(id=null){
    const v=id?((await db().select('vehicles',{eq:{id},maybeSingle:true})).data||{}):{};
    const val=k=>util().sanitize(v[k]??'');
    ui().openModal(id?`Edit vehicle: ${util().sanitize(v.registration||'')}`:'Add vehicle',`
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Registration</label><input class="ims__input" type="text" id="ims-veh-reg" placeholder="e.g. AB21 XYZ" value="${val('registration')}"></div>
        <div class="ims__form-group"><label class="ims__label">Make &amp; model</label><input class="ims__input" type="text" id="ims-veh-model" placeholder="e.g. Ford Transit" value="${val('make_model')}"></div>
        <div class="ims__form-group"><label class="ims__label">Vehicle type</label><select class="ims__select" id="ims-veh-type"><option value="van">Van</option><option value="truck">Truck</option><option value="car">Car</option></select></div>
        <div class="ims__form-group"><label class="ims__label">Capacity (units)</label><input class="ims__input" type="number" id="ims-veh-cap" value="${val('capacity_units')}"></div>
        <div class="ims__form-group"><label class="ims__label">MOT due</label><input class="ims__input" type="date" id="ims-veh-mot" value="${val('mot_due')}"></div>
        <div class="ims__form-group"><label class="ims__label">Service due</label><input class="ims__input" type="date" id="ims-veh-service" value="${val('service_due')}"></div>
        <div class="ims__form-group ims__form-group--full"><label class="ims__label">Notes</label><textarea class="ims__textarea" id="ims-veh-notes" rows="2">${val('notes')}</textarea></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveVehicle('${util().jsArg(id||'')}')"><i class="ti ti-check"></i> Save</button>
    `,'sm');
    if(v.vehicle_type){ const t=document.getElementById('ims-veh-type'); if(t) t.value=v.vehicle_type; }
  }

  async function saveVehicle(existingId=''){
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const reg=g('ims-veh-reg');
    if(!reg){ui().toast('Registration is required','error');return;}
    const row={registration:reg,make_model:g('ims-veh-model')||null,vehicle_type:g('ims-veh-type')||'van',capacity_units:parseInt(g('ims-veh-cap'))||null,mot_due:g('ims-veh-mot')||null,service_due:g('ims-veh-service')||null,notes:g('ims-veh-notes')||null};
    if(existingId){
      const {error}=await db().update('vehicles',existingId,row);
      if(error){ui().toast('Failed: '+error.message,'error');return;}
      ui().closeModal();ui().toast('Vehicle updated','success');loadFleet();return;
    }
    const {error}=await db().insert('vehicles',{registration:reg,make_model:g('ims-veh-model')||null,vehicle_type:g('ims-veh-type')||'van',capacity_units:parseInt(g('ims-veh-cap'))||null,mot_due:g('ims-veh-mot')||null,service_due:g('ims-veh-service')||null,notes:g('ims-veh-notes')||null});
    if(error){ui().toast('Failed: '+error.message,'error');return;}
    ui().closeModal();ui().toast('Vehicle added','success');loadFleet();
  }

  async function showDriverForm(id=null){
    const d=id?((await db().select('drivers',{eq:{id},maybeSingle:true})).data||{}):{};
    const val=k=>util().sanitize(d[k]??'');
    ui().openModal(id?`Edit driver: ${util().sanitize(d.name||'')}`:'Add driver',`
      <div class="ims__form-grid">
        <div class="ims__form-group"><label class="ims__label ims__label--req">Driver name</label><input class="ims__input" type="text" id="ims-drv-name" value="${val('name')}"></div>
        <div class="ims__form-group"><label class="ims__label">Phone</label><input class="ims__input" type="tel" id="ims-drv-phone" value="${val('phone')}"></div>
        <div class="ims__form-group"><label class="ims__label">Email</label><input class="ims__input" type="email" id="ims-drv-email" value="${val('email')}"></div>
        <div class="ims__form-group"><label class="ims__label">Licence number</label><input class="ims__input" type="text" id="ims-drv-licence" value="${val('licence_number')}"></div>
        <div class="ims__form-group"><label class="ims__label">Licence expiry</label><input class="ims__input" type="date" id="ims-drv-expiry" value="${val('licence_expiry')}"></div>
      </div>
    `,`
      <button class="ims__btn ims__btn--ghost" onclick="window.SM.UI.closeModal()">Cancel</button>
      <button class="ims__btn ims__btn--primary" onclick="window.IMS_LOGISTICS.saveDriver('${util().jsArg(id||'')}')"><i class="ti ti-check"></i> Save</button>
    `,'sm');
  }

  async function saveDriver(existingId=''){
    const g=id=>document.getElementById(id)?.value?.trim()||'';
    const name=g('ims-drv-name');
    if(!name){ui().toast('Driver name is required','error');return;}
    if(existingId){
      const {error}=await db().update('drivers',existingId,{name,phone:g('ims-drv-phone')||null,email:g('ims-drv-email')||null,licence_number:g('ims-drv-licence')||null,licence_expiry:g('ims-drv-expiry')||null});
      if(error){ui().toast('Failed: '+error.message,'error');return;}
      ui().closeModal();ui().toast('Driver updated','success');loadFleet();return;
    }
    const {error}=await db().insert('drivers',{name,phone:g('ims-drv-phone')||null,email:g('ims-drv-email')||null,licence_number:g('ims-drv-licence')||null,licence_expiry:g('ims-drv-expiry')||null});
    if(error){ui().toast('Failed: '+error.message,'error');return;}
    ui().closeModal();ui().toast('Driver added','success');loadFleet();
  }

  // ─── FLEET: edit, delete, deactivate ──────────────────────────────
  // Edit: anyone with Logistics access. Delete and deactivate: Admins only
  // (the database enforces this too). A driver or vehicle with history
  // (manifests, COD, a driver login) cannot be deleted; it can be
  // deactivated instead: hidden from new manifests and the driver-login
  // list, history kept, and a linked driver login can no longer use the
  // driver app.
  function _fleetButtons(kind,id,label,off){
    const edit=`<button class="ims__btn ims__btn--ghost ims__btn--sm ims__btn--icon" title="Edit" onclick="window.IMS_LOGISTICS.${kind==='driver'?'showDriverForm':'showVehicleForm'}('${util().jsArg(id)}')"><i class="ti ti-edit"></i></button>`;
    if(!window.SM.AUTH.isAdmin()) return edit;
    const toggle=off
      ? `<button class="ims__btn ims__btn--ghost ims__btn--sm" title="Reactivate" onclick="window.IMS_LOGISTICS.setFleetActive('${kind}','${util().jsArg(id)}','${util().jsArg(label)}',true)">Reactivate</button>`
      : `<button class="ims__btn ims__btn--ghost ims__btn--sm ims__btn--icon" title="Deactivate" onclick="window.IMS_LOGISTICS.setFleetActive('${kind}','${util().jsArg(id)}','${util().jsArg(label)}',false)"><i class="ti ti-player-pause"></i></button>`;
    const del=`<button class="ims__btn ims__btn--ghost ims__btn--sm ims__btn--icon" title="Delete" style="color:var(--ims-danger)" onclick="window.IMS_LOGISTICS.deleteFleet('${kind}','${util().jsArg(id)}','${util().jsArg(label)}')"><i class="ti ti-trash"></i></button>`;
    return edit+toggle+del;
  }
  async function _fleetUsage(kind,id){
    const c=_rcDb(), used=[];
    const count=async(q)=>{ const {count:n}=await q; return n||0; };
    if(kind==='driver'){
      const m=await count(c.from('manifests').select('id',{count:'exact',head:true}).eq('driver_id',id)); if(m) used.push(`${m} manifest${m===1?'':'s'}`);
      const t=await count(c.from('cod_transactions').select('id',{count:'exact',head:true}).eq('driver_id',id)); if(t) used.push(`${t} COD transaction${t===1?'':'s'}`);
      const {data:acc}=await c.from('driver_cod_accounts').select('id,balance').eq('driver_id',id).maybeSingle();
      if(acc&&Number(acc.balance)) used.push(`a COD balance of ${util().formatCurrency(Math.abs(Number(acc.balance)))}`);
      const {data:logins}=await c.from('users').select('name').eq('driver_id',id).eq('is_active',true);   // active logins only
      if((logins||[]).length) used.push(`the driver login${logins.length===1?'':'s'} ${logins.map(u=>u.name).join(', ')}`);
      return {used,emptyAccount:acc&&!Number(acc.balance)&&!t?acc.id:null};
    }
    const m=await count(c.from('manifests').select('id',{count:'exact',head:true}).eq('vehicle_id',id)); if(m) used.push(`${m} manifest${m===1?'':'s'}`);
    return {used,emptyAccount:null};
  }
  async function deleteFleet(kind,id,label){
    if(!window.SM.AUTH.isAdmin()){ ui().toast('Only an Admin can delete a '+kind,'error'); return; }
    const {used,emptyAccount}=await _fleetUsage(kind,id);
    const what=kind==='driver'?'driver':'vehicle';
    if(used.length){
      ui().confirm(`<strong>${util().sanitize(label)}</strong> cannot be deleted: this ${what} has ${util().sanitize(used.join(', '))}. Deleting would break that history.<br><br>Deactivate instead? It will be hidden from new manifests${kind==='driver'?' and the driver-login list, and a linked driver login can no longer use the driver app':''}. All history is kept, and you can reactivate it at any time.`,
        ()=>setFleetActive(kind,id,label,false,true),'Deactivate instead',false);
      return;
    }
    ui().confirm(`Delete the ${what} <strong>${util().sanitize(label)}</strong>? This cannot be undone.`,async()=>{
      if(emptyAccount) await _rcDb().from('driver_cod_accounts').delete().eq('id',emptyAccount);   // an empty COD account, never used
      const {error}=await db().remove(kind==='driver'?'drivers':'vehicles',id);
      if(error){ ui().toast('Not deleted: '+error.message,'error',8000); return; }
      ui().toast(`${kind==='driver'?'Driver':'Vehicle'} deleted`,'success'); loadFleet();
    },'Delete',true);
  }
  async function setFleetActive(kind,id,label,active,confirmed=false){
    if(!window.SM.AUTH.isAdmin()){ ui().toast('Only an Admin can do this','error'); return; }
    const go=async()=>{
      const {error}=await db().update(kind==='driver'?'drivers':'vehicles',id,{is_active:active});
      if(error){ ui().toast('Not saved: '+error.message,'error',8000); return; }
      ui().toast(`${util().sanitize(label)} ${active?'reactivated':'deactivated'}`,'success'); loadFleet();
    };
    if(active||confirmed) return go();
    ui().confirm(`Deactivate <strong>${util().sanitize(label)}</strong>? It will be hidden from new manifests${kind==='driver'?' and the driver-login list, and a linked driver login can no longer use the driver app':''}. All history is kept.`,go,'Deactivate',false);
  }

  // ─── PICKING LIST ────────────────────────────────────────────────
  async function printPicklist(){
    const checked=[...document.querySelectorAll('.ims-job-check:checked')];
    if(!checked.length){ui().toast('Select at least one order to print picklist','warning');return;}
    const orderIds=checked.map(cb=>cb.dataset.orderId).filter(Boolean);
    if(!orderIds.length){ui().toast('No valid orders selected','warning');return;}
    printPicklistForOrders(orderIds);
  }

  async function printPicklistForOrders(orderIds){
    const client=window.supabase.createClient(window.IMS_SUPABASE_URL,window.IMS_SUPABASE_ANON_KEY);
    const now=new Date().toLocaleDateString('en-GB');
    const {data:orders}=await client.from('orders').select('id,order_number,customer_name,postcode,address').in('id',orderIds);
    const {data:items}=await client.from('order_items').select('order_id,product_id,product_name,sku,quantity,products(name,sku,bin_location,warehouse_location)').in('order_id',orderIds);
    const productIds=[...new Set((items||[]).map(i=>i.product_id).filter(Boolean))];
    const locationMap={};
    if(productIds.length){
      const {data:stock}=await client.from('stock_levels').select('product_id,bin_location,warehouses(name)').in('product_id',productIds);
      (stock||[]).forEach(s=>{locationMap[s.product_id]=s;});
    }
    const allItems=(items||[]).map(i=>({product_id:i.product_id,quantity:i.quantity,product_name:i.product_name,sku:i.sku}));
    const pickBoxSummary=await util().getOrderBoxSummary(allItems);
    const skuMap={};
    (items||[]).forEach(item=>{
      const key=item.sku||item.product_name;
      if(!skuMap[key]){
        const boxLine=pickBoxSummary.lines.find(l=>l.productId===item.product_id);
        skuMap[key]={sku:item.sku||'—',name:item.product_name||'—',bin:locationMap[item.product_id]?.bin_location||'—',warehouse:locationMap[item.product_id]?.warehouses?.name||'—',totalQty:0,totalBoxes:0,totalWeightKg:0,orders:[],boxLine};
      }
      skuMap[key].totalQty+=item.quantity||0;
      const orderRef=(orders||[]).find(o=>o.id===item.order_id);
      skuMap[key].orders.push({orderNumber:orderRef?.order_number||'—',customer:orderRef?.customer_name||'—',qty:item.quantity||0});
    });
    Object.values(skuMap).forEach(row=>{if(row.boxLine&&row.boxLine.lineBoxes){row.totalBoxes=row.boxLine.lineBoxes;row.totalWeightKg=row.boxLine.lineWeightKg;}});
    const sorted=Object.values(skuMap).sort((a,b)=>(a.bin||'zzz').localeCompare(b.bin||'zzz'));
    const hasBoxData=pickBoxSummary.hasData;
    const totalBoxesAll=sorted.reduce((s,p)=>s+(p.totalBoxes||0),0);
    const totalWeightAll=sorted.reduce((s,p)=>s+(p.totalWeightKg||0),0);
    const totalUnitsAll=sorted.reduce((s,p)=>s+p.totalQty,0);
    const pickColWidths=hasBoxData?['10%','16%','26%','10%','8%','8%','12%','10%']:['10%','16%','30%','12%','22%','10%'];
    const pickColumns=hasBoxData?[{label:'Bin'},{label:'SKU'},{label:'Product'},{label:'Units',align:'r'},{label:'Boxes',align:'r'},{label:'Weight',align:'r'},{label:'Warehouse'},{label:'Checked ✓',align:'ctr'}]:[{label:'Bin'},{label:'SKU'},{label:'Product'},{label:'Units',align:'r'},{label:'Warehouse'},{label:'Checked ✓',align:'ctr'}];
    const pickRows=sorted.map(p=>{const base=[util().sanitize(p.bin),util().sanitize(p.sku),util().sanitize(p.name),String(p.totalQty)];const boxCols=hasBoxData?[p.totalBoxes||'—',p.totalWeightKg?p.totalWeightKg+'kg':'—']:[];return[...base,...boxCols,util().sanitize(p.warehouse),window.PRINT.signatureBox()];});
    const pickTotalsRow=hasBoxData?['<td colspan="3"><strong>Total</strong></td>',`<td class="r"><strong>${totalUnitsAll} units</strong></td>`,`<td class="r"><strong>${totalBoxesAll} boxes</strong></td>`,`<td class="r"><strong>${totalWeightAll.toFixed(1)}kg</strong></td>`,'<td colspan="2"></td>']:['<td colspan="3"><strong>Total</strong></td>',`<td class="r"><strong>${totalUnitsAll} units</strong></td>`,'<td colspan="2"></td>'];
    const pickTableHtml=window.PRINT.table({colWidths:pickColWidths,columns:pickColumns,rows:pickRows,totalsRow:pickTotalsRow});
    const orderRows=(orders||[]).map(o=>[util().sanitize(o.order_number),util().sanitize(o.customer_name||'—'),util().sanitize(o.postcode||'—'),util().sanitize(o.address||'—')]);
    const orderTableHtml=window.PRINT.table({colWidths:['15%','30%','15%','40%'],columns:[{label:'Order #'},{label:'Customer'},{label:'Postcode'},{label:'Address'}],rows:orderRows});
    const headerHtml=window.PRINT.header({title:'Pick List',docRef:`${orderIds.length} order(s) — Printed: ${now}`,rightLabel:'Picker sign-off',rightLines:[`Date: ${now}`,'Time started: ____________','Time completed: _________']});
    const summaryHtml=hasBoxData?window.PRINT.summaryBox(`Total boxes to pick: ${pickBoxSummary.totalBoxes} &nbsp;|&nbsp; Total weight: ${pickBoxSummary.totalWeightKg} kg`):'';
    const page=`${headerHtml}${summaryHtml}<div class="st">Consolidated pick list — sorted by bin location</div><hr class="rule">${pickTableHtml}<hr class="rule-dbl"><div class="st" style="margin-top:12px">Order reference — what goes where</div><hr class="rule">${orderTableHtml}<hr class="rule-dbl">${window.PRINT.pageFooter(['StockMaestro © 2026 ABSA Digital','Pick List'],1,1)}`;
    window.PRINT.renderDocument([page],`Pick List — ${orderIds.length} orders`);
  }

  // ─── EVENTS ─────────────────────────────────────────────────────

  // ─── COURIER FOLLOW-UP ──────────────────────────────────────────
  window.addEventListener('ims:panel',e=>{
    if(e.detail.panel==='logistics'){initTabs();loadTracking();_liveStart();}
  });
  window.addEventListener('ims:ready',()=>{
    document.getElementById('ims-courier-setup-btn')?.addEventListener('click',()=>{
      window.SM.UI.showPanel('logistics');
      setTimeout(()=>{
        const logTabs=document.getElementById('ims-log-tabs');
        logTabs?.querySelectorAll('.ims__tab').forEach(t=>t.classList.remove('ims__tab--active'));
        logTabs?.querySelector('[data-tab="couriers"]')?.classList.add('ims__tab--active');
        loadCouriers();
      },100);
    });
    document.getElementById('ims-manifest-btn')?.addEventListener('click',planRoute);
  });

  window.IMS_LOGISTICS={ bookCourierCollection, collectionPrice, saveCourierCollection, sendCollectionEmail, markCollectionCollected, cancelCourierCollection, renderReturnCollection, arrangeVanPickup, cancelPickup, _liveRefresh, podOpen, renderOrderPod, confirmBankTransfer, confirmStopFailed, deleteFleet, setFleetActive,
    markVanDeparted, backToUnassigned, saveService, addService, setServiceStatus,
    refreshCourierCharge, toggleChargeExtra, chargeEdited, moveChargePostcode, calcExpectedForShipment,
    rcCreateCard, rcTab, rcSelectCard, rcSaveType, rcSavePrice, rcAddType, rcDeleteType, rcRenameZone, rcAddPostcodes,
    rcRemovePostcode, rcAddZone, rcDeleteZone, rcProdFilter, rcSetType, rcBulkSet, rcApplySuggestions, rcExportProducts,
    rcImportProducts, rcSaveExtra, rcAddExtra, rcDeleteExtra, rcSaveCard, rcNewVersion, rcDeleteVersion,
    load:loadTracking, loadTab,
    loadTracking, buildTrackingTable, buildFilterBar, filterTracking, toggleSwatchFilter,
    refreshTabBadges,
    openTrackingUpdate, saveTrackingEvent, openTrackingHistory,
    openEditTracking, saveTrackingNumber,
    openCourierFollowUp, saveFollowUp,
    markAsLost, confirmMarkAsLost,
    openRaiseClaim, saveClaim, syncClaimAmount, syncClaimType,
    openClaimFollowUp, saveClaimFollowUp,
    loadLostOrders, addLostOrderNote, saveLostOrderNote,
    openCourierClaimRef, saveCourierClaimRef,
    openSettleClaim, confirmSettleClaim,
    openRejectClaim, confirmRejectClaim,
    openParcelFound, confirmParcelFound, restockFoundParcel, smartRestock,
    viewClosedClaims, openCloseNoClaim, confirmCloseNoClaim, writeOffOrderStock,
    confirmDelivered, confirmDeliveredSave, openFailedDelivery, confirmFailed,
    requestReAttempt, confirmReAttempt,
    requestReturn, confirmReturn, openReturnNotify, sendReturnNotify,
    markReturnReceived, confirmReturnReceived,
    openActivityLog, logActivity, deductStockOnDelivery,
    loadUnassigned, loadShipments, filterShipments, loadManifests, loadRoutes, loadFleet, loadCouriers,
    toggleAll, assignJobCourier, confirmCourierAssignment, assignOwnTruck, confirmOwnTruck,
    reassignToOwnTruck, confirmReassignToOwnTruck,
    loadOwnTruckQueue, planRouteFromQueue,
    markSelfCollection, markCollected, showSelfCollectionModal, confirmMarkCollected, cancelSelfCollection,
    bulkAssignCourier, confirmBulkAssign, planRoute, createManifest, openManifest, updateDelivery,
    openEditManifest, removeManifestStop, saveManifestEdit,
    printManifest, deleteManifest,
    showCourierForm, saveCourier, manageCourierRates, addRateBand, deleteRateBand, editSurcharge,
    showRouteForm, saveRoute, showVehicleForm, saveVehicle, showDriverForm, saveDriver,
    loadAllCODBalances, openCODAccount, recordCODPayment, confirmCODPayment, reconcileCODAccount,
    printPicklist, printPicklistForOrders, cancelUnassignedOrder,
    _failChoice: null
  };
})();